import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { formatMoney } from '../utils/formatMoney';
import { useTranslation } from 'react-i18next';
import { useToast } from './shared/Toast';
import { useAuth } from '../hooks/useAuth';
import { usePermissions } from '../hooks/usePermissions';
import { useProductIngredients } from '../hooks/useProductIngredients';
import { productsAPI } from '../services/api';
import { API_BASE_URL } from '../components/config/api';
import Navigation from './Navigation';
import TouchKeyboard from './ui/TouchKeyboard';
import CountryCodeInput, { countryLabel } from './ui/CountryCodeInput';
import { 
  Phone, User, MapPin, ShoppingBag, Plus, Edit, Search,
  Home, Briefcase, Package, DollarSign, TrendingUp, Award,
  ShoppingCart, X, Star, RefreshCw, Navigation2, Clipboard,
  Banknote, CreditCard, Truck, Minus, Receipt, FileText, Building2, Loader2,
  ChevronRight, Settings, Settings2, Check, Tag, MessageSquare, AlertTriangle, Ellipsis, Undo2
} from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import type { Product, ProductSize, Category, BasketItem, PosOptionGroup, SelectedOptionEntry } from '../types/order';
import OpenPriceModal from './shared/OpenPriceModal';
import WeightModal from './shared/WeightModal';
import ProductOptionsModal from './shared/ProductOptionsModal';
import ProductCatalog from './shared/ProductCatalog';
import { productDisplayPrice, lineUnitPrice, sizeChannelPrice, defaultSize, isFeeProduct } from '../utils/pricing';
import { computeAutoFeeBasket, autoFeeNoteUnit, manualFeeLine, autoManagedFeeLineId, addOrMergeManualFee, isOwnAutoFeeLine } from '../utils/autoFee';
import { isWeightedOpenPrice, weightedOpenPriceLine, weightUnitOf } from '../utils/weightedOpenPrice';
import { editedLineBasePrice, lineOwnAmount, EditedLineOwn } from '../utils/basketLineEdit';
import { withSellableSizes } from '../utils/sellableSizes';
import { useDailyDishesToday } from '../hooks/useDailyDishesToday';
import { applyDailyDishes } from '../utils/dailyDishes';
import { extraSimpleIngredients, removableSimpleIngredients, simpleIngredientNames } from '../utils/simpleIngredients';
import { afterCashierEdit, buildReorderLines, followRuleAfterCashierChange } from '../utils/reorderLine';
import { deliverySubtotal } from '../utils/deliverySubtotal';
import { resolveBarcodeLine, findBarcodeMergeLine, BarcodeLookupData } from '../utils/barcodeLine';
import { addOrMergeBasketLine } from '../utils/basketMerge';
import { changeLineQuantityOrRemove, removeBasketLine, restoreRemovedLines } from '../utils/basketOps';
import { buildDeliveryItemNotes } from '../utils/orderHelpers';
import ProductSizeModal from './shared/ProductSizeModal';
import BasketItemRow, { BASKET_V2_COLUMNS } from './shared/BasketItemRow';
import { useTheme } from '../contexts/ThemeContext';
import { catalogLookOf, useCatalogPrefsV2 } from '../utils/catalogPrefsV2';
import { formatEuro } from '../utils/formatEuro';
import { swallowGhostClick } from '../utils/ghostClick';
import NumpadModal from './shared/NumpadModal';
import ProductCustomizeModal, { ProductCustomizeResult } from './shared/ProductCustomizeModal';
import PromotionBanner, { EligiblePromotion } from './shared/PromotionBanner';
import UpsellPrompt from './shared/UpsellPrompt';
import { useUpsellSuggestions } from '../hooks/useUpsellSuggestions';
import { pickUpsell, UpsellPick } from '../utils/upsell';
import { loadGoogleMaps, getStoreCenter } from '../utils/googleMaps';
import { DeliveryMethod, deliveryMethodOptions, impliedDeliveryMethod, preferredAddress } from '../utils/deliveryRepeat';
import { DELIVERY_INTENT_EVENT, DeliveryCallIntent, takePendingDeliveryIntent } from '../utils/deliveryCallIntent';
import OrderDriver from './delivery/OrderDriver';

type DeliveryPaymentMethod = 'cash' | 'card' | 'iris';

interface Customer {
  id: number;
  phone: string;
  phone_secondary?: string;
  name: string;
  email?: string;
  customer_comments?: string;
  hidden_comments?: string;
  total_orders: number;
  total_spent: number;
  average_order_value: number;
  is_vip: boolean;
  loyalty_points: number;
  addresses: Address[];
  recent_orders: RecentOrder[];
  address_count?: number;
  order_count?: number;
  // Company/Invoice details
  company_name?: string;
  company_vat?: string;
  company_tax_office?: string;
  company_commercial_title?: string;
  company_address?: string;
  company_city?: string;
  company_postal_code?: string;
  company_country_code?: string;
  company_branch_code?: number;
}

interface Address {
  id: number;
  label?: string;
  address_type: string;
  street_address: string;
  area?: string;
  city?: string;
  postal_code?: string;
  floor?: string;
  doorbell?: string;
  delivery_notes?: string;
  is_default: boolean;
  times_used: number;
  last_used_at?: string | null;
  latitude?: number;
  longitude?: number;
  formatted_address?: string;
}

interface RecentOrder {
  id: number;
  short_code: string;
  order_date: string;
  total_amount: number;
  items_count: number;
  status: string;
  street_address?: string;
  area?: string;
  order_type?: string;
  delivery_address_id?: number | null;
}

interface DeliveryWithMapProps {
  onPageChange?: (page: string) => void;
}

const DeliveryWithMap: React.FC<DeliveryWithMapProps> = ({ onPageChange }) => {
  const { store, employee } = useAuth();
  const { showToast } = useToast();
  const { t } = useTranslation(['delivery', 'common', 'products']);
  // Ετικέτες οθόνης μόνο: η τιμή που αποθηκεύεται (address_type / status) μένει ως έχει.
  const addressTypeLabel = (type?: string) => {
    switch ((type || '').toLowerCase()) {
      case 'home': return t('delivery:addressTypeHome');
      case 'work': return t('delivery:addressTypeWork');
      case 'office': return t('delivery:addressTypeOffice');
      case 'parents': return t('delivery:addressTypeParents');
      default: return type;
    }
  };
  const orderStatusLabel = (status?: string) =>
    status ? t(`orders:status.${status}`, { defaultValue: status }) : status;
  // «Νέα εμφάνιση» (ανά συσκευή — ThemeContext): ίδια με το Take Away.
  const { look } = useTheme();
  const v2 = look === 'v2';
  // Διάταξη καταλόγου της συσκευής («μία οθόνη» / «σε βήματα») — ίδια ρύθμιση με το Take Away.
  const catalogPrefs = useCatalogPrefsV2();
  const [showBasketMenu, setShowBasketMenu] = useState(false);
  const DEFAULT_CENTER = getStoreCenter(store);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [newAddressData, setNewAddressData] = useState({
    street_address: '',
    area: '',
    city: '',
    floor: '',
    doorbell: '',
    postal_code: '',
    delivery_notes: '',
    latitude: null as number | null,
    longitude: null as number | null,
    formatted_address: '',
    address_type: 'home',
    is_default: false
  });
  const [orderMode, setOrderMode] = useState(false);
  
  // Edit address state
  const [showEditAddressForm, setShowEditAddressForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState<Address | null>(null);
  const [editAddressData, setEditAddressData] = useState({
    street_address: '',
    area: '',
    city: '',
    floor: '',
    doorbell: '',
    postal_code: '',
    delivery_notes: '',
    latitude: null as number | null,
    longitude: null as number | null,
    formatted_address: '',
    address_type: 'home',
    is_default: false
  });
  
  // Map refs for add address modal
  const addAddressMapRef = useRef<google.maps.Map | null>(null);
  const addAddressMarkerRef = useRef<google.maps.Marker | null>(null);
  const addAddressMapContainerRef = useRef<HTMLDivElement>(null);
  const addAddressStreetRef = useRef<HTMLInputElement>(null);
  const addAddressAutocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  
  // Map refs for edit address modal
  const editAddressMapRef = useRef<google.maps.Map | null>(null);
  const editAddressMarkerRef = useRef<google.maps.Marker | null>(null);
  const editAddressMapContainerRef = useRef<HTMLDivElement>(null);
  
  // Phone suggestions
  const [phoneSuggestions, setPhoneSuggestions] = useState<Array<{phone: string, name: string, id: number}>>([]); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [showSuggestions, setShowSuggestions] = useState(false); // eslint-disable-line @typescript-eslint/no-unused-vars
  
  // Order related states
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [basket, setBasket] = useState<BasketItem[]>([]);
  const [appliedPromotionId, setAppliedPromotionId] = useState<number | null>(null);
  // ΚΑΝΕΝΑΣ προεπιλεγμένος τρόπος πληρωμής (κανόνας του κώδικα, όπως στο Take Away): ως
  // 12/10/2026 ξεκινούσε 'cash' και δεν μηδενιζόταν μετά την παραγγελία, άρα η επόμενη
  // κληρονομούσε σιωπηλά τον τρόπο της προηγούμενης (κάρτα = προφόρτωση POS στην αποστολή).
  // null = «δεν διαλέχτηκε»· η αποστολή μένει κλειστή μέχρι να διαλέξει ο ταμίας.
  const [paymentMethod, setPaymentMethod] = useState<DeliveryPaymentMethod | null>(null);
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  
  // Fiscal document type (1 = receipt, 2 = invoice)
  const [fiscalDocumentType, setFiscalDocumentType] = useState<1 | 2>(1);
  const [customerVatNumber, setCustomerVatNumber] = useState('');
  const [customerCompanyName, setCustomerCompanyName] = useState('');
  const [customerCompanyActivity, setCustomerCompanyActivity] = useState('');
  const [customerCompanyAddress, setCustomerCompanyAddress] = useState('');
  const [isLookingUpVat, setIsLookingUpVat] = useState(false);
  const [vatLookupError, setVatLookupError] = useState('');
  
  // ECR status
  const ecrEnabled = store?.ecr_provider && store.ecr_provider !== 'none';
  
  // Delivery method (self, efood, wolt) — όπως ο τρόπος πληρωμής: ΠΟΤΕ κληρονομημένος από την
  // προηγούμενη παραγγελία (e-food/Wolt = κλείσιμο κούριερ). Με μόνο τον δικό μας διανομέα
  // εννοείται χωρίς επιλογή (utils/deliveryRepeat impliedDeliveryMethod)· αλλιώς ρητή επιλογή.
  const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethod | null>(null);
  const deliveryOptions = deliveryMethodOptions(store);
  const impliedMethod = impliedDeliveryMethod(deliveryOptions);
  const deliveryChoiceNeeded = impliedMethod === null;
  const effectiveDeliveryMethod: DeliveryMethod | null = impliedMethod ?? deliveryMethod;
  // Ζώνη/χρέωση παράδοσης πριν διαλεχτεί τρόπος: όπως ως τώρα, σαν δικός μας διανομέας (αν
  // υπάρχει), ώστε το σύνολο που ακούει ο πελάτης να μην αλλάζει επειδή δεν πατήθηκε ακόμη κουμπί.
  const zoneMethod: DeliveryMethod | null = effectiveDeliveryMethod ?? (deliveryOptions.includes('self') ? 'self' : null);

  // Αποστολή σε εξέλιξη: ένα POST ανά παραγγελία, ό,τι κι αν πατηθεί δύο φορές.
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
  const submitInFlightRef = useRef(false);
  // Νέα εμφάνιση — πληρωμή με ένα πάτημα (όπως το Take Away): ο τρόπος μπαίνει στην κατάσταση
  // και η αποστολή τρέχει στην επόμενη ζωγραφιά, ώστε το submitOrder να δει τον ΝΕΟ τρόπο.
  const [quickPayMethod, setQuickPayMethod] = useState<DeliveryPaymentMethod | null>(null);
  const quickPayRef = useRef<DeliveryPaymentMethod | null>(null);
  // Νέα εμφάνιση: «Παραγγελία #D1234 · εστάλη» μετά την αποστολή, με την οθόνη έτοιμη για την επόμενη κλήση.
  const [lastSentOrder, setLastSentOrder] = useState<{ shortCode: string | null } | null>(null);
  // Αναίρεση (4″) για ό,τι αδειάζει το καλάθι: αφαίρεση γραμμής, ✕, «Νέα αναζήτηση», άλλος πελάτης.
  // Με αλλαγή πελάτη η αναίρεση γυρίζει ΚΑΙ τον πελάτη — ποτέ καλάθι ενός πελάτη σε άλλον.
  const [undoState, setUndoState] = useState<{
    label: string;
    basket: BasketItem[];
    orderDiscount: number;
    discountType: 'percent' | 'fixed';
    fixedDiscountAmount: number;
    feeOverride: number | null;
    deliveryNotes: string;
    orderMode: boolean;
    customerState?: { customer: Customer; selectedAddress: Address | null; phoneNumber: string };
    /** 'lines' = ξαναμπαίνουν μόνο οι γραμμές που έφυγαν (×, −, άδειασμα)· 'whole' = όλο το καλάθι όπως
     *  ήταν (αλλαγή πελάτη, επανάληψη που το αντικατέστησε): εκεί το τρέχον καλάθι ανήκει σε άλλη παραγγελία. */
    restore: 'lines' | 'whole';
  } | null>(null);
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Το καλάθι της τελευταίας ζωγραφιάς, για ροές που το διαβάζουν ΜΕΤΑ από await (επανάληψη παραγγελίας).
  const paintedBasketRef = useRef<BasketItem[]>([]);
  useEffect(() => { paintedBasketRef.current = basket; }, [basket]);
  // Κάθε αλλαγή πελάτη (ή αναίρεσή της) το αυξάνει: μια αναζήτηση/επανάληψη που τελειώνει ΜΕΤΑ
  // δεν γράφει πια πάνω στον πελάτη που άνοιξε στο μεταξύ.
  const customerSeqRef = useRef(0);
  
  // Popup message state
  const [popupMessage, setPopupMessage] = useState<{ title: string; message: string; type: 'error' | 'success' | 'warning' } | null>(null);
  
  // Modal states
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [showSizeModal, setShowSizeModal] = useState(false);
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedSize, setSelectedSize] = useState<ProductSize | null>(null);
  const [longPressTimer, setLongPressTimer] = useState<NodeJS.Timeout | null>(null);
  const longPressTriggeredRef = useRef(false);
  
  // Customization states
  const [removedIngredients, setRemovedIngredients] = useState<any[]>([]);
  const [addedExtras, setAddedExtras] = useState<any[]>([]);
  const [specialInstructions, setSpecialInstructions] = useState('');
  const { ingredients: productIngredients, extras: availableExtras, loading: loadingMaterials, fetchBySize: fetchIngredientsBySize, fetchByProduct: fetchIngredientsByProduct, clear: clearIngredients } = useProductIngredients();
  const [productDiscount, setProductDiscount] = useState(0);

  // Product option groups (e.g. required addon selections)
  const [productOptionGroups, setProductOptionGroups] = useState<PosOptionGroup[]>([]);
  const [pendingSelectedOptions, setPendingSelectedOptions] = useState<SelectedOptionEntry[]>([]);
  const [showOptionModal, setShowOptionModal] = useState(false);
  const [optionModalPendingSize, setOptionModalPendingSize] = useState<ProductSize | null>(null);

  // Barcode scanning state
  const [barcodeError, setBarcodeError] = useState('');
  const [scanningBarcode, setScanningBarcode] = useState(false);
  const barcodeBufferRef = useRef('');
  const barcodeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Training mode — only valid for self delivery + receipt + cash
  const [trainingModeEnabled, setTrainingModeEnabled] = useState(false);
  const [isTrainingOrder, setIsTrainingOrder] = useState(false);
  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [orderDiscount, setOrderDiscount] = useState<number>(0);
  // Numpad-driven discount entry (touchscreen-friendly — no OS keyboard).
  const [showNumpad, setShowNumpad] = useState(false);
  const [numpadTarget, setNumpadTarget] = useState<'orderPercent' | 'orderFixed'>('orderPercent');
  const [discountType, setDiscountType] = useState<'percent' | 'fixed'>('percent');
  const [fixedDiscountAmount, setFixedDiscountAmount] = useState<number>(0);
  
  // Delivery Settings (discount config)
  const [deliverySettings, setDeliverySettings] = useState<{
    discount_presets?: number[];
    discount_allow_custom_percent?: boolean;
    discount_allow_fixed_amount?: boolean;
    discount_max_percent?: number;
    discount_max_amount?: number;
  } | null>(null);
  
  // Add to basket animation
  const [addedToCartAnimation, setAddedToCartAnimation] = useState(false);
  const [lastAddedProduct, setLastAddedProduct] = useState<string>('');
  
  // Edit basket item
  const [editingBasketItem, setEditingBasketItem] = useState<BasketItem | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // Delivery zone check
  const [outOfZone, setOutOfZone] = useState(false);
  const [showOutOfZoneWarning, setShowOutOfZoneWarning] = useState(false);
  const [zoneCheckDone, setZoneCheckDone] = useState(false);
  const [deliveryFee, setDeliveryFee] = useState(0);

  // Weight-variable products
  const [showWeightModal, setShowWeightModal] = useState(false);
  const [weightInput, setWeightInput] = useState<string>('');

  // Open price products
  const [showOpenPriceModal, setShowOpenPriceModal] = useState(false);
  const [openPriceInput, setOpenPriceInput] = useState<string>('');
  // «Βάρος + τιμή κιλού»: το βάρος του πρώτου βήματος, όσο ο ταμίας δίνει την τιμή κιλού.
  const [weightForOpenPrice, setWeightForOpenPrice] = useState<number | null>(null);

  // Προτάσεις πώλησης (κεντρικά → «Προτάσεις πώλησης»): ίδια ροή με το Take Away — μία φορά
  // ανά παραγγελία, στο «Checkout», πριν ανοίξει η ολοκλήρωση (το submitOrder κρατά το
  // καλάθι του render του, γι' αυτό η πρόταση μπαίνει ΠΡΙΝ από το παράθυρο).
  const { suggestions: upsellSuggestions, recordImpression: recordUpsellImpression } =
    useUpsellSuggestions(store?.id, 'delivery');
  const [upsellPick, setUpsellPick] = useState<UpsellPick | null>(null);
  const [upsellContinue, setUpsellContinue] = useState(false);
  const upsellShownRef = useRef(false);
  const upsellTagRef = useRef<number | null>(null);
  const takeUpsellTag = () => {
    const id = upsellTagRef.current;
    upsellTagRef.current = null;
    return id ? { upsell_suggestion_id: id } : {};
  };
  useEffect(() => {
    if (basket.length === 0) upsellShownRef.current = false;
  }, [basket.length]);
  const hiddenCategoryIds = React.useMemo(() => {
    const hidden = new Set<number>();
    categories.forEach(c => {
      if (c.is_hidden) hidden.add(c.id);
      (c.subcategories || []).forEach(s => { if (c.is_hidden || s.is_hidden) hidden.add(s.id); });
    });
    return hidden;
  }, [categories]);
  
  // Grid layout settings (persisted in localStorage)
  const [categoryColumns, setCategoryColumns] = useState<number>(() => {
    const saved = localStorage.getItem('delivery_category_columns');
    return saved ? parseInt(saved) : 4;
  });
  const [productColumns, setProductColumns] = useState<number>(() => {
    const saved = localStorage.getItem('delivery_product_columns');
    return saved ? parseInt(saved) : 5;
  });
  const [showGridSettings, setShowGridSettings] = useState(false);
  
  // View navigation (categories → subcategories → products)
  const [currentView, setCurrentView] = useState<'categories' | 'subcategories' | 'products'>('categories');
  
  // Order view modal
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [viewingOrder, setViewingOrder] = useState<any>(null);
  
  // Company details modal
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [companyFormData, setCompanyFormData] = useState({
    company_name: '',
    company_vat: '',
    company_tax_office: '',
    company_commercial_title: '',
    company_address: '',
    company_city: '',
    company_postal_code: '',
    company_country_code: '',
    company_branch_code: 0
  });
  const [isLookingUpCompany, setIsLookingUpCompany] = useState(false);
  // Country picker is a type-ahead: the query text is separate state so the
  // TouchKeyboard can type into it; the submitted value stays an ISO code.
  const [companyCountryQuery, setCompanyCountryQuery] = useState(countryLabel('GR'));
  useEffect(() => {
    setCompanyCountryQuery(countryLabel(companyFormData.company_country_code || 'GR'));
  }, [companyFormData.company_country_code]);

  // Map related states
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);
  const searchBoxRef = useRef<google.maps.places.SearchBox | null>(null); // eslint-disable-line @typescript-eslint/no-unused-vars
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const newCustomerStreetRef = useRef<HTMLInputElement>(null);
  const newCustomerAutocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  
  const [mapCoordinates, setMapCoordinates] = useState<{lat: number, lng: number} | null>(null);
  const [formattedAddress, setFormattedAddress] = useState('');
  
  const [newCustomerData, setNewCustomerData] = useState({
    name: '',
    phone_secondary: '',
    email: '',
    customer_comments: '',
    address: {
      street_address: '',
      area: '',
      city: '',
      floor: '',
      doorbell: '',
      postal_code: '',
      delivery_notes: '',
      latitude: null as number | null,
      longitude: null as number | null,
      formatted_address: ''
    }
  });

  // On-screen keyboard for touch-only POS screens (customer search + new-customer
  // form). Rendered as a bottom sheet; per-field mode (numpad vs full keyboard).
  const [deliveryKb, setDeliveryKb] = useState<{ field: string; mode: 'numeric' | 'text'; label: string; maxLength?: number } | null>(null);

  const deliveryKbValue = (): string => {
    if (!deliveryKb) return '';
    switch (deliveryKb.field) {
      case 'search': return phoneNumber;
      case 'name': return newCustomerData.name;
      case 'street': return newCustomerData.address.street_address;
      case 'area': return newCustomerData.address.area;
      case 'city': return newCustomerData.address.city;
      case 'floor': return newCustomerData.address.floor;
      case 'doorbell': return newCustomerData.address.doorbell;
      case 'postal_code': return newCustomerData.address.postal_code;
      case 'phone_secondary': return newCustomerData.phone_secondary;
      case 'email': return newCustomerData.email;
      case 'comments': return newCustomerData.customer_comments;
      case 'company_vat': return companyFormData.company_vat;
      case 'company_country': return companyCountryQuery;
      case 'company_name': return companyFormData.company_name;
      case 'company_tax_office': return companyFormData.company_tax_office;
      case 'company_commercial_title': return companyFormData.company_commercial_title;
      case 'company_address': return companyFormData.company_address;
      case 'company_city': return companyFormData.company_city;
      case 'company_postal_code': return companyFormData.company_postal_code;
      case 'company_branch_code': return String(companyFormData.company_branch_code ?? '');
      default: return '';
    }
  };

  const deliveryKbChange = (next: string) => {
    if (!deliveryKb) return;
    const d = newCustomerData;
    switch (deliveryKb.field) {
      case 'search':
        handleSearchInputChange(next); // keeps the customer autocomplete suggestions flowing
        break;
      case 'name':
        // Mirror the input's own onChange: the doorbell follows the name until edited.
        setNewCustomerData({
          ...d, name: next,
          address: { ...d.address, doorbell: (d.address.doorbell === '' || d.address.doorbell === d.name) ? next : d.address.doorbell }
        });
        break;
      case 'street': {
        setNewCustomerData({ ...d, address: { ...d.address, street_address: next } });
        // Google Places Autocomplete listens for DOM input events on the element —
        // state updates alone don't trigger its suggestion dropdown.
        const el = newCustomerStreetRef.current;
        if (el) {
          el.value = next;
          el.dispatchEvent(new Event('input', { bubbles: true }));
        }
        break;
      }
      case 'area': setNewCustomerData({ ...d, address: { ...d.address, area: next } }); break;
      case 'city': setNewCustomerData({ ...d, address: { ...d.address, city: next } }); break;
      case 'floor': setNewCustomerData({ ...d, address: { ...d.address, floor: next } }); break;
      case 'doorbell': setNewCustomerData({ ...d, address: { ...d.address, doorbell: next } }); break;
      case 'postal_code': setNewCustomerData({ ...d, address: { ...d.address, postal_code: next.replace(/\D/g, '') } }); break;
      case 'phone_secondary': setNewCustomerData({ ...d, phone_secondary: next.replace(/[^\d+ ]/g, '') }); break;
      case 'email': setNewCustomerData({ ...d, email: next }); break;
      case 'comments': setNewCustomerData({ ...d, customer_comments: next }); break;
      // Foreign VAT numbers can contain letters and exceed 9 chars — the AADE
      // lookup only fires on an exact 9-digit (Greek) value anyway.
      case 'company_vat': setCompanyFormData({ ...companyFormData, company_vat: next.replace(/[^0-9A-Za-z]/g, '').toUpperCase() }); break;
      case 'company_country': setCompanyCountryQuery(next); break;
      case 'company_name': setCompanyFormData({ ...companyFormData, company_name: next }); break;
      case 'company_tax_office': setCompanyFormData({ ...companyFormData, company_tax_office: next }); break;
      case 'company_commercial_title': setCompanyFormData({ ...companyFormData, company_commercial_title: next }); break;
      case 'company_address': setCompanyFormData({ ...companyFormData, company_address: next }); break;
      case 'company_city': setCompanyFormData({ ...companyFormData, company_city: next }); break;
      case 'company_postal_code': setCompanyFormData({ ...companyFormData, company_postal_code: next.replace(/\D/g, '') }); break;
      case 'company_branch_code': setCompanyFormData({ ...companyFormData, company_branch_code: parseInt(next.replace(/\D/g, ''), 10) || 0 }); break;
      default: break;
    }
  };

  const deliveryKbDone = () => {
    const field = deliveryKb?.field;
    setDeliveryKb(null);
    if (field === 'search' && phoneNumber) searchCustomer();
    // Closing the numpad on a complete ΑΦΜ fires the AADE lookup directly.
    if (field === 'company_vat' && /^\d{9}$/.test(companyFormData.company_vat)) lookupCompanyByVat();
  };

  // Persist grid settings to localStorage
  useEffect(() => {
    localStorage.setItem('delivery_category_columns', String(categoryColumns));
  }, [categoryColumns]);
  useEffect(() => {
    localStorage.setItem('delivery_product_columns', String(productColumns));
  }, [productColumns]);

  // Auto-deactivate training mode when conditions break (e.g. user switches to card).
  // «Δεν διαλέχτηκε ακόμη» (null) δεν σπάει τον κανόνα: το κράτημα 2″ στα «Μετρητά» της νέας
  // εμφάνισης ενεργοποιεί την εκπαίδευση ΠΡΙΝ μπει ο τρόπος.
  useEffect(() => {
    if (isTrainingOrder && (
      (effectiveDeliveryMethod !== null && effectiveDeliveryMethod !== 'self') ||
      fiscalDocumentType !== 1 ||
      (paymentMethod !== null && paymentMethod !== 'cash')
    )) {
      setIsTrainingOrder(false);
    }
  }, [isTrainingOrder, effectiveDeliveryMethod, fiscalDocumentType, paymentMethod]);

  // Global barcode scanner listener — only while in order mode.
  // Scanners type very fast (<50ms/char) and end with Enter.
  useEffect(() => {
    if (!store?.barcode_enabled || !orderMode) return;

    const SCANNER_TIMEOUT_MS = 50;
    const MIN_BARCODE_LENGTH = 6;

    const handleKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;

      if (e.key === 'Enter') {
        const buf = barcodeBufferRef.current;
        if (buf.length >= MIN_BARCODE_LENGTH) {
          handleBarcodeScan(buf);
        }
        barcodeBufferRef.current = '';
        if (barcodeTimerRef.current) clearTimeout(barcodeTimerRef.current);
        return;
      }

      if (e.key.length !== 1) return;
      barcodeBufferRef.current += e.key;

      if (barcodeTimerRef.current) clearTimeout(barcodeTimerRef.current);
      barcodeTimerRef.current = setTimeout(() => {
        barcodeBufferRef.current = '';
      }, SCANNER_TIMEOUT_MS);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (barcodeTimerRef.current) clearTimeout(barcodeTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.barcode_enabled, orderMode]);

  // Check delivery zone when address is selected
  useEffect(() => {
    const checkDeliveryZone = async () => {
      if (!selectedAddress || !store?.id || zoneMethod !== 'self') {
        setOutOfZone(false);
        setDeliveryFee(0);
        setZoneCheckDone(false);
        return;
      }
      if (!selectedAddress.latitude || !selectedAddress.longitude) {
        setOutOfZone(false);
        setDeliveryFee(0);
        setZoneCheckDone(true);
        return;
      }
      try {
        const res = await fetch(`${API_BASE_URL}/api/stores/${store.id}/delivery-zones/check`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
          },
          body: JSON.stringify({
            lat: selectedAddress.latitude,
            lng: selectedAddress.longitude
          })
        });
        if (res.ok) {
          const data = await res.json();
          if (data.no_zones_defined) {
            setOutOfZone(false);
            setDeliveryFee(0);
          } else {
            setOutOfZone(!data.in_zone);
            setDeliveryFee(data.in_zone ? (data.delivery_fee || 0) : 0);
          }
        }
      } catch (err) {
        console.error('Zone check error:', err);
      }
      setZoneCheckDone(true);
    };
    checkDeliveryZone();
  }, [selectedAddress, store?.id, zoneMethod]);

  // Tear down a Places Autocomplete instance AND its dropdown. Google appends
  // the suggestions list (.pac-container) to document.body and never removes it
  // when the input unmounts — so closing the modal mid-typing left the dropdown
  // as a ghost overlay floating over EVERY screen (even Take Away) until a full
  // reload. Worse, the stale ref made the `!ref.current` guard in the init
  // functions skip re-attaching on reopen, so autocomplete went dead from the
  // 2nd customer card onwards.
  const destroyPlacesAutocomplete = (ref: React.MutableRefObject<google.maps.places.Autocomplete | null>) => {
    if (ref.current) {
      try { window.google?.maps?.event?.clearInstanceListeners(ref.current); } catch { /* best-effort */ }
      ref.current = null;
    }
    // Orphan sweep — safe here: the delivery modals are mutually exclusive and
    // no other screen with a live autocomplete is mounted at the same time.
    document.querySelectorAll('.pac-container').forEach(el => el.remove());
  };

  // Initialize Google Maps for new customer form
  useEffect(() => {
    if (showNewCustomerForm) {
      loadGoogleMaps(store?.google_maps_api_key).then(() => {
        initializeMap();
      });
    } else {
      destroyPlacesAutocomplete(newCustomerAutocompleteRef);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showNewCustomerForm]);

  // Initialize Google Maps for add address modal
  useEffect(() => {
    if (showAddressForm) {
      loadGoogleMaps(store?.google_maps_api_key).then(() => {
        initializeAddAddressMap();
      });
    } else {
      destroyPlacesAutocomplete(addAddressAutocompleteRef);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddressForm]);

  // Leaving the Delivery screen entirely — drop both instances and any ghost
  // dropdowns so nothing bleeds into the next screen.
  useEffect(() => () => {
    destroyPlacesAutocomplete(newCustomerAutocompleteRef);
    destroyPlacesAutocomplete(addAddressAutocompleteRef);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize Google Maps for edit address modal
  useEffect(() => {
    if (showEditAddressForm && editingAddress) {
      loadGoogleMaps(store?.google_maps_api_key).then(() => {
        initializeEditAddressMap();
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEditAddressForm]);

  // Manual override of the auto plastic-fee quantity. null = follow the auto
  // count; a number = the cashier waived/adjusted it (e.g. the customer brought
  // their own cup). Resets when the basket is emptied.
  const [feeOverride, setFeeOverride] = useState<number | null>(null);

  // Auto-add the code-16 single-use plastic fee whenever the basket contains
  // items flagged with auto_plastic_fee_count > 0. Sums (qty × trigger count)
  // across non-fee items and keeps a single fee row in sync. No-op unless the
  // store has a product with fee_category=16 configured.
  useEffect(() => {
    // Catalog/size prices are net of the fee in every mode, so always add the
    // fee as its own line. Platform orders (fee already in price) carve at fiscal.
    const feeProduct = products.find(p => p.fee_category === 16);
    if (!feeProduct) return;

    // Ποσό = feeUnitGross (state_fees, απόφαση 1_fees_source), γραμμή χωρίς μέγεθος·
    // χωρίς ποσό δεν μπαίνει γραμμή (απόφαση 33). Κανόνες ποσότητας στο utils/autoFee
    // (κοινό με το TakeAway· εδώ χωρίς διακόπτη auto_plastic_fee_enabled, όπως πριν).
    // Οι γραμμές επανάληψης παραγγελίας (keepHistoricalFee) κρατούν ΑΚΡΙΒΩΣ τα ιστορικά τέλη:
    // ούτε γεννούν τέλος ούτε η ιστορική γραμμή τέλους περνά στον κανόνα (F1-W6-2/F1-W6-3)·
    // ο κανόνας πιάνει μόνο ό,τι προσθέτει/αλλάζει ο ταμίας μετά.
    const next = computeAutoFeeBasket({ basket, feeProduct, feeOverride });
    // Ο ίδιος κανόνας πάνω στο ΤΡΕΧΟΝ καλάθι (utils/basketOps, όπως το Take Away): ένα είδος που μπήκε
    // στην ουρά μετά τη ζωγραφιά θα χανόταν με το `setBasket(next)` του αντιγράφου. Χωρίς τέτοιο
    // είδος (prev === basket) γράφεται ακριβώς το `next`.
    if (next !== basket) {
      setBasket(prev => (prev === basket ? next : computeAutoFeeBasket({ basket: prev, feeProduct, feeOverride })));
    }
  }, [basket, products, feeOverride]);

  // New order → forget any fee waiver.
  useEffect(() => {
    if (basket.length === 0 && feeOverride !== null) setFeeOverride(null);
  }, [basket.length, feeOverride]);

  const initializeMap = () => {
    if (!mapContainerRef.current || !window.google) return;

    // Initialize map
    mapRef.current = new google.maps.Map(mapContainerRef.current, {
      center: DEFAULT_CENTER,
      zoom: 15,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
    });

    // Initialize geocoder
    geocoderRef.current = new google.maps.Geocoder();

    // Initialize marker
    markerRef.current = new google.maps.Marker({
      map: mapRef.current,
      draggable: true,
      position: DEFAULT_CENTER,
    });

    // Handle marker drag
    markerRef.current.addListener('dragend', () => {
      const position = markerRef.current?.getPosition();
      if (position) {
        reverseGeocode(position.lat(), position.lng());
      }
    });

    // Handle map click
    mapRef.current.addListener('click', (event: google.maps.MapMouseEvent) => {
      if (event.latLng) {
        markerRef.current?.setPosition(event.latLng);
        reverseGeocode(event.latLng.lat(), event.latLng.lng());
      }
    });

    // Attach Places Autocomplete to street address input
    if (newCustomerStreetRef.current && !newCustomerAutocompleteRef.current) {
      newCustomerAutocompleteRef.current = new google.maps.places.Autocomplete(
        newCustomerStreetRef.current,
        {
          types: ['address'],
          componentRestrictions: { country: 'gr' },
          fields: ['address_components', 'geometry', 'formatted_address']
        }
      );

      newCustomerAutocompleteRef.current.addListener('place_changed', () => {
        const place = newCustomerAutocompleteRef.current?.getPlace();
        if (!place?.geometry?.location) return;

        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();

        // Parse address components
        let streetNumber = '';
        let streetName = '';
        let area = '';
        let city = '';
        let postalCode = '';

        place.address_components?.forEach(component => {
          const types = component.types;
          if (types.includes('street_number')) {
            streetNumber = component.long_name;
          } else if (types.includes('route')) {
            streetName = component.long_name;
          } else if (types.includes('neighborhood') || types.includes('sublocality') || types.includes('sublocality_level_1')) {
            area = component.long_name;
          } else if (types.includes('locality')) {
            city = component.long_name;
          } else if (types.includes('postal_code')) {
            postalCode = component.long_name;
          }
        });

        const streetAddress = streetName + (streetNumber ? ' ' + streetNumber : '');

        // Update map and marker
        mapRef.current?.setCenter(place.geometry.location);
        mapRef.current?.setZoom(17);
        markerRef.current?.setPosition(place.geometry.location);

        // Update all form fields
        setNewCustomerData(prev => ({
          ...prev,
          address: {
            ...prev.address,
            street_address: streetAddress || prev.address.street_address,
            area: area || prev.address.area,
            city: city || prev.address.city,
            postal_code: postalCode || prev.address.postal_code,
            latitude: lat,
            longitude: lng,
            formatted_address: place.formatted_address || ''
          }
        }));

        setMapCoordinates({ lat, lng });
        setFormattedAddress(place.formatted_address || '');
      });
    }
  };

  const geocodeAddress = useCallback(() => {
    if (!geocoderRef.current || !newCustomerData.address.street_address) return;

    const fullAddress = [
      newCustomerData.address.street_address,
      newCustomerData.address.area,
      newCustomerData.address.city,
      newCustomerData.address.postal_code,
      'Greece' // Default country
    ].filter(Boolean).join(', ');

    geocoderRef.current.geocode({ address: fullAddress }, (results, status) => {
      if (status === 'OK' && results && results[0]) {
        const location = results[0].geometry.location;
        const lat = location.lat();
        const lng = location.lng();
        
        // Update map and marker
        mapRef.current?.setCenter(location);
        markerRef.current?.setPosition(location);
        
        // Update state
        setMapCoordinates({ lat, lng });
        setFormattedAddress(results[0].formatted_address);
        
        // Update form data
        setNewCustomerData(prev => ({
          ...prev,
          address: {
            ...prev.address,
            latitude: lat,
            longitude: lng,
            formatted_address: results[0].formatted_address
          }
        }));
      }
    });
  }, [newCustomerData.address.street_address, newCustomerData.address.area, 
      newCustomerData.address.city, newCustomerData.address.postal_code]);

  const reverseGeocode = (lat: number, lng: number) => {
    if (!geocoderRef.current) return;

    geocoderRef.current.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results && results[0]) {
        const addressComponents = results[0].address_components;
        const formattedAddr = results[0].formatted_address;
        
        // Parse address components
        let streetNumber = '';
        let streetName = '';
        let area = '';
        let city = '';
        let postalCode = '';
        
        addressComponents?.forEach(component => {
          const types = component.types;
          if (types.includes('street_number')) {
            streetNumber = component.long_name;
          } else if (types.includes('route')) {
            streetName = component.long_name;
          } else if (types.includes('neighborhood') || types.includes('sublocality')) {
            area = component.long_name;
          } else if (types.includes('locality')) {
            city = component.long_name;
          } else if (types.includes('postal_code')) {
            postalCode = component.long_name;
          }
        });
        
        const streetAddress = streetName + (streetNumber ? ' ' + streetNumber : '');
        
        // Update form
        setNewCustomerData(prev => ({
          ...prev,
          address: {
            ...prev.address,
            street_address: streetAddress || prev.address.street_address,
            area: area || prev.address.area,
            city: city || prev.address.city,
            postal_code: postalCode || prev.address.postal_code,
            latitude: lat,
            longitude: lng,
            formatted_address: formattedAddr
          }
        }));
        
        setMapCoordinates({ lat, lng });
        setFormattedAddress(formattedAddr);
      }
    });
  };

  // Initialize map for add address modal
  const initializeAddAddressMap = () => {
    if (!addAddressMapContainerRef.current || !window.google) return;

    // Initialize map
    addAddressMapRef.current = new google.maps.Map(addAddressMapContainerRef.current, {
      center: DEFAULT_CENTER,
      zoom: 15,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
    });

    // Initialize marker
    addAddressMarkerRef.current = new google.maps.Marker({
      map: addAddressMapRef.current,
      draggable: true,
      position: DEFAULT_CENTER,
    });

    // Handle marker drag
    addAddressMarkerRef.current.addListener('dragend', () => {
      const position = addAddressMarkerRef.current?.getPosition();
      if (position) {
        reverseGeocodeForAddAddress(position.lat(), position.lng());
      }
    });

    // Handle map click
    addAddressMapRef.current.addListener('click', (event: google.maps.MapMouseEvent) => {
      if (event.latLng) {
        addAddressMarkerRef.current?.setPosition(event.latLng);
        reverseGeocodeForAddAddress(event.latLng.lat(), event.latLng.lng());
      }
    });

    // Attach Places Autocomplete to add address street input
    if (addAddressStreetRef.current && !addAddressAutocompleteRef.current) {
      addAddressAutocompleteRef.current = new google.maps.places.Autocomplete(
        addAddressStreetRef.current,
        {
          types: ['address'],
          componentRestrictions: { country: 'gr' },
          fields: ['address_components', 'geometry', 'formatted_address']
        }
      );

      addAddressAutocompleteRef.current.addListener('place_changed', () => {
        const place = addAddressAutocompleteRef.current?.getPlace();
        if (!place?.geometry?.location) return;

        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();

        let streetNumber = '';
        let streetName = '';
        let area = '';
        let city = '';
        let postalCode = '';

        place.address_components?.forEach(component => {
          const types = component.types;
          if (types.includes('street_number')) {
            streetNumber = component.long_name;
          } else if (types.includes('route')) {
            streetName = component.long_name;
          } else if (types.includes('neighborhood') || types.includes('sublocality') || types.includes('sublocality_level_1')) {
            area = component.long_name;
          } else if (types.includes('locality')) {
            city = component.long_name;
          } else if (types.includes('postal_code')) {
            postalCode = component.long_name;
          }
        });

        const streetAddress = streetName + (streetNumber ? ' ' + streetNumber : '');

        // Update map and marker
        addAddressMapRef.current?.setCenter(place.geometry.location);
        addAddressMapRef.current?.setZoom(17);
        addAddressMarkerRef.current?.setPosition(place.geometry.location);

        // Update all form fields
        setNewAddressData(prev => ({
          ...prev,
          street_address: streetAddress || prev.street_address,
          area: area || prev.area,
          city: city || prev.city,
          postal_code: postalCode || prev.postal_code,
          latitude: lat,
          longitude: lng,
          formatted_address: place.formatted_address || ''
        }));
      });
    }
  };

  // Geocode address for add address modal
  const geocodeAddAddress = () => {
    if (!geocoderRef.current || !newAddressData.street_address) {
      // Initialize geocoder if not already
      if (!geocoderRef.current && window.google) {
        geocoderRef.current = new google.maps.Geocoder();
      }
      if (!geocoderRef.current) return;
    }

    const fullAddress = [
      newAddressData.street_address,
      newAddressData.area,
      newAddressData.city,
      newAddressData.postal_code,
      'Greece'
    ].filter(Boolean).join(', ');

    geocoderRef.current.geocode({ address: fullAddress }, (results, status) => {
      if (status === 'OK' && results && results[0]) {
        const location = results[0].geometry.location;
        const lat = location.lat();
        const lng = location.lng();
        
        // Update map and marker
        addAddressMapRef.current?.setCenter(location);
        addAddressMarkerRef.current?.setPosition(location);
        
        // Update form data
        setNewAddressData(prev => ({
          ...prev,
          latitude: lat,
          longitude: lng,
          formatted_address: results[0].formatted_address
        }));
      }
    });
  };

  // Reverse geocode for add address modal
  const reverseGeocodeForAddAddress = (lat: number, lng: number) => {
    if (!geocoderRef.current) {
      if (window.google) {
        geocoderRef.current = new google.maps.Geocoder();
      }
      if (!geocoderRef.current) return;
    }

    geocoderRef.current.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results && results[0]) {
        const addressComponents = results[0].address_components;
        const formattedAddr = results[0].formatted_address;
        
        // Parse address components
        let streetNumber = '';
        let streetName = '';
        let area = '';
        let city = '';
        let postalCode = '';
        
        addressComponents?.forEach(component => {
          const types = component.types;
          if (types.includes('street_number')) {
            streetNumber = component.long_name;
          } else if (types.includes('route')) {
            streetName = component.long_name;
          } else if (types.includes('neighborhood') || types.includes('sublocality')) {
            area = component.long_name;
          } else if (types.includes('locality')) {
            city = component.long_name;
          } else if (types.includes('postal_code')) {
            postalCode = component.long_name;
          }
        });
        
        const streetAddress = streetName + (streetNumber ? ' ' + streetNumber : '');
        
        // Update form
        setNewAddressData(prev => ({
          ...prev,
          street_address: streetAddress || prev.street_address,
          area: area || prev.area,
          city: city || prev.city,
          postal_code: postalCode || prev.postal_code,
          latitude: lat,
          longitude: lng,
          formatted_address: formattedAddr
        }));
      }
    });
  };

  // Initialize map for edit address modal
  const initializeEditAddressMap = () => {
    if (!editAddressMapContainerRef.current || !window.google) return;

    // Use existing coordinates or default center
    const center = editAddressData.latitude && editAddressData.longitude
      ? { lat: editAddressData.latitude, lng: editAddressData.longitude }
      : DEFAULT_CENTER;

    // Initialize map
    editAddressMapRef.current = new google.maps.Map(editAddressMapContainerRef.current, {
      center: center,
      zoom: 15,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
    });

    // Initialize marker
    editAddressMarkerRef.current = new google.maps.Marker({
      map: editAddressMapRef.current,
      draggable: true,
      position: center,
    });

    // Handle marker drag
    editAddressMarkerRef.current.addListener('dragend', () => {
      const position = editAddressMarkerRef.current?.getPosition();
      if (position) {
        reverseGeocodeForEditAddress(position.lat(), position.lng());
      }
    });

    // Handle map click
    editAddressMapRef.current.addListener('click', (event: google.maps.MapMouseEvent) => {
      if (event.latLng) {
        editAddressMarkerRef.current?.setPosition(event.latLng);
        reverseGeocodeForEditAddress(event.latLng.lat(), event.latLng.lng());
      }
    });
  };

  // Geocode address for edit address modal
  const geocodeEditAddress = () => {
    if (!geocoderRef.current) {
      if (window.google) {
        geocoderRef.current = new google.maps.Geocoder();
      }
      if (!geocoderRef.current) return;
    }

    const fullAddress = [
      editAddressData.street_address,
      editAddressData.area,
      editAddressData.city,
      editAddressData.postal_code,
      'Greece'
    ].filter(Boolean).join(', ');

    geocoderRef.current.geocode({ address: fullAddress }, (results, status) => {
      if (status === 'OK' && results && results[0]) {
        const location = results[0].geometry.location;
        const lat = location.lat();
        const lng = location.lng();
        
        // Update map and marker
        editAddressMapRef.current?.setCenter(location);
        editAddressMarkerRef.current?.setPosition(location);
        
        // Update form data
        setEditAddressData(prev => ({
          ...prev,
          latitude: lat,
          longitude: lng,
          formatted_address: results[0].formatted_address
        }));
      }
    });
  };

  // Reverse geocode for edit address modal
  const reverseGeocodeForEditAddress = (lat: number, lng: number) => {
    if (!geocoderRef.current) {
      if (window.google) {
        geocoderRef.current = new google.maps.Geocoder();
      }
      if (!geocoderRef.current) return;
    }

    geocoderRef.current.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results && results[0]) {
        const addressComponents = results[0].address_components;
        const formattedAddr = results[0].formatted_address;
        
        // Parse address components
        let streetNumber = '';
        let streetName = '';
        let area = '';
        let city = '';
        let postalCode = '';
        
        addressComponents?.forEach(component => {
          const types = component.types;
          if (types.includes('street_number')) {
            streetNumber = component.long_name;
          } else if (types.includes('route')) {
            streetName = component.long_name;
          } else if (types.includes('neighborhood') || types.includes('sublocality')) {
            area = component.long_name;
          } else if (types.includes('locality')) {
            city = component.long_name;
          } else if (types.includes('postal_code')) {
            postalCode = component.long_name;
          }
        });
        
        const streetAddress = streetName + (streetNumber ? ' ' + streetNumber : '');
        
        // Update form
        setEditAddressData(prev => ({
          ...prev,
          street_address: streetAddress || prev.street_address,
          area: area || prev.area,
          city: city || prev.city,
          postal_code: postalCode || prev.postal_code,
          latitude: lat,
          longitude: lng,
          formatted_address: formattedAddr
        }));
      }
    });
  };

  const handlePageChange = (page: string) => {
    if (page !== 'delivery') {
      if (onPageChange) {
        onPageChange(page);
      } else {
        window.location.pathname = page === 'dashboard' ? '/' : `/${page}`;
      }
    }
  };

  // Autocomplete suggestions (for both phone and general search)
  const [autocompleteSuggestions, setAutocompleteSuggestions] = useState<Customer[]>([]);
  const [showAutocompleteSuggestions, setShowAutocompleteSuggestions] = useState(false);
  const autocompleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Fetch phone suggestions (legacy - for phone-only search)
  const fetchPhoneSuggestions = async (partialPhone: string) => { // eslint-disable-line @typescript-eslint/no-unused-vars
    if (partialPhone.length < 5) {
      setPhoneSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/stores/${store?.id}/phone-suggestions?partial=${partialPhone}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPhoneSuggestions(data.suggestions || []);
        setShowSuggestions(data.suggestions && data.suggestions.length > 0);
      }
    } catch (error) {
      console.error('Error fetching phone suggestions:', error);
    }
  };
  
  // Fetch autocomplete suggestions (for any search term)
  const fetchAutocompleteSuggestions = async (searchTerm: string) => {
    if (searchTerm.length < 3) {
      setAutocompleteSuggestions([]);
      setShowAutocompleteSuggestions(false);
      return;
    }

    try {
      // Use general search endpoint
      const queryParam = /^\d+$/.test(searchTerm) 
        ? `phone=${searchTerm}` 
        : `q=${encodeURIComponent(searchTerm)}`;
      
      const response = await fetch(
        `${API_BASE_URL}/api/stores/${store?.id}/customers/search?${queryParam}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.customers && data.customers.length > 0) {
          setAutocompleteSuggestions(data.customers);
          setShowAutocompleteSuggestions(true);
        } else if (data.customer) {
          setAutocompleteSuggestions([data.customer]);
          setShowAutocompleteSuggestions(true);
        } else {
          setAutocompleteSuggestions([]);
          setShowAutocompleteSuggestions(false);
        }
      }
    } catch (error) {
      console.error('Error fetching autocomplete suggestions:', error);
    }
  };
  
  // Debounced autocomplete
  const handleSearchInputChange = (value: string) => {
    setPhoneNumber(value);
    setShowSearchResults(false);
    
    // Clear previous timeout
    if (autocompleteTimeoutRef.current) {
      clearTimeout(autocompleteTimeoutRef.current);
    }
    
    // Debounce the autocomplete request
    autocompleteTimeoutRef.current = setTimeout(() => {
      fetchAutocompleteSuggestions(value);
    }, 300);
  };
  
  // Η διεύθυνση που προεπιλέγεται όταν ανοίγει πελάτης. Νέα εμφάνιση: της τελευταίας
  // παραγγελίας delivery → last_used_at → προεπιλεγμένη → μοναδική (utils/deliveryRepeat)·
  // κλασική: μόνο η προεπιλεγμένη, όπως πάντα.
  const initialAddressFor = (c: Customer): Address | null =>
    v2
      ? preferredAddress(c.addresses, c.recent_orders)
      : (c.addresses?.find((a: Address) => a.is_default) ?? null);
  // Άλλος πελάτης χωρίς προεπιλογή → ΚΑΜΙΑ διεύθυνση (όχι η διεύθυνση του προηγούμενου)·
  // ίδιος πελάτης (ανανέωση μετά την αποστολή) → μένει η επιλογή, όπως πάντα.
  const applyInitialAddress = (c: Customer) => {
    const addr = initialAddressFor(c);
    if (addr) setSelectedAddress(addr);
    else if (customer?.id !== c.id) setSelectedAddress(null);
  };

  const selectAutocompleteSuggestion = (selectedCustomer: Customer) => {
    setCustomer(selectedCustomer);
    setPhoneNumber(selectedCustomer.phone);
    applyInitialAddress(selectedCustomer);
    setShowAutocompleteSuggestions(false);
    setAutocompleteSuggestions([]);
    setDeliveryKb(null); // customer picked — dismiss the on-screen keyboard
  };

  // Search customer by phone, name, or address
  const [searchResults, setSearchResults] = useState<Customer[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  
  // Επιστρέφει τον πελάτη που άνοιξε (null αλλιώς). isCurrent: η αναγγελία κλήσης περνά φρουρό,
  // ώστε μια απάντηση που αργεί να μην ανοίξει πελάτη πάνω σε «Αναίρεση»/άλλη κλήση.
  const searchCustomer = async (phoneToSearch?: string, isCurrent: () => boolean = () => true): Promise<Customer | null> => {
    const searchTerm = phoneToSearch || phoneNumber;

    if (!searchTerm || searchTerm.length < 3) {
      showToast('warning', 'Γράψε τουλάχιστον 3 χαρακτήρες για αναζήτηση');
      return null;
    }

    setIsSearching(true);
    setShowSuggestions(false);
    setSearchResults([]);
    setShowSearchResults(false);
    
    try {
      // Determine if it's a phone search or general search
      const isPhoneSearch = /^\d{10,}$/.test(searchTerm);
      const queryParam = isPhoneSearch ? `phone=${searchTerm}` : `q=${encodeURIComponent(searchTerm)}`;
      
      const response = await fetch(
        `${API_BASE_URL}/api/stores/${store?.id}/customers/search?${queryParam}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
          }
        }
      );

      const data = await response.json();
      if (!isCurrent()) return null;

      if (data.customer) {
        // Single customer found
        const found: Customer = data.customer;
        setCustomer(found);
        applyInitialAddress(found);
        setDeliveryKb(null); // customer resolved — dismiss the on-screen keyboard
        return found;
      } else if (data.customers && data.customers.length > 0) {
        // Multiple customers found - show selection
        setSearchResults(data.customers);
        setShowSearchResults(true);
        setDeliveryKb(null); // picking from the results list — keyboard not needed
      } else {
        // No customer found - show new customer form (only for phone searches)
        if (isPhoneSearch) {
          setShowNewCustomerForm(true);
        } else {
          showToast('info', 'Δεν βρέθηκαν πελάτες. Δοκίμασε αναζήτηση με τηλέφωνο για να δημιουργήσεις νέο.');
        }
      }
    } catch (error) {
      console.error('Error searching customer:', error);
      showToast('error', 'Η αναζήτηση πελάτη απέτυχε');
    } finally {
      setIsSearching(false);
    }
    return null;
  };
  
  // Auto-search when navigating from incoming call popup (?phone=XXX)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const phoneParam = params.get('phone');
    if (phoneParam && store?.id) {
      setPhoneNumber(phoneParam);
      // Clean up URL param without reload
      window.history.replaceState({}, '', window.location.pathname);
      // Trigger search after a short delay to ensure component is ready
      setTimeout(() => {
        searchCustomer(phoneParam);
      }, 300);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.id]);

  const selectSearchResult = async (selectedCustomer: Customer) => {
    setShowSearchResults(false);
    setSearchResults([]);
    setPhoneNumber(selectedCustomer.phone);
    
    // Fetch full profile with recent orders and favorites
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/stores/${store?.id}/customers/${selectedCustomer.id}/profile`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
          }
        }
      );
      const data = await response.json();
      if (data.customer) {
        setCustomer(data.customer);
        applyInitialAddress(data.customer);
        return;
      }
    } catch (error) {
      console.error('Error fetching customer profile:', error);
    }

    // Fallback: use partial data if profile fetch fails
    setCustomer(selectedCustomer);
    applyInitialAddress(selectedCustomer);
  };

  // ---- Επιλογές ολοκλήρωσης: ΑΝΑ παραγγελία, ποτέ κληρονομημένες ----
  // Τρόπος πληρωμής/παράδοσης, εκπαίδευση, και τα στοιχεία τιμολογίου (ΑΦΜ ενός πελάτη δεν
  // περνά ποτέ σε παραγγελία άλλου).
  const resetCheckoutChoices = () => {
    setPaymentMethod(null);
    setDeliveryMethod(null);
    setIsTrainingOrder(false);
    quickPayRef.current = null;
    setQuickPayMethod(null);
    setFiscalDocumentType(1);
    setCustomerVatNumber('');
    setCustomerCompanyName('');
    setCustomerCompanyActivity('');
    setCustomerCompanyAddress('');
    setVatLookupError('');
  };
  // Δίχτυ ασφαλείας: όποια διαδρομή κι αν αλλάξει πελάτη, οι επιλογές της ολοκλήρωσης μηδενίζονται.
  const currentCustomerId = customer?.id ?? null;
  useEffect(() => {
    resetCheckoutChoices();
    if (currentCustomerId !== null) setLastSentOrder(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCustomerId]);

  // ---- Αναίρεση 4″ — ίδιο μοτίβο με το Take Away (offerUndo / undoLastRemoval) ----
  // `snapshot`: το καλάθι που φεύγει — από προεπιλογή της ζωγραφιάς· μετά από await, το τελευταίο.
  const offerUndo = (label: string, withCustomer = false, restore: 'lines' | 'whole' = 'lines', snapshot: BasketItem[] = basket) => {
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    setUndoState({
      label, basket: snapshot, orderDiscount, discountType, fixedDiscountAmount, feeOverride, deliveryNotes, orderMode,
      customerState: withCustomer && customer ? { customer, selectedAddress, phoneNumber } : undefined,
      restore: withCustomer ? 'whole' : restore,
    });
    undoTimerRef.current = setTimeout(() => setUndoState(null), 4000);
  };
  const dropUndo = () => {
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    undoTimerRef.current = null;
    setUndoState(null);
  };
  const undoLastRemoval = () => {
    if (!undoState || submitInFlightRef.current) return;
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    if (undoState.customerState) {
      // Πίσω στον προηγούμενο πελάτη: ό,τι άνοιγε στο μεταξύ (αναγγελία κλήσης) σταματά εδώ.
      customerSeqRef.current += 1;
      setShowNewCustomerForm(false);
      setShowSearchResults(false);
      setCustomer(undoState.customerState.customer);
      setSelectedAddress(undoState.customerState.selectedAddress);
      setPhoneNumber(undoState.customerState.phoneNumber);
      resetCheckoutChoices();
    }
    // ×/−/άδειασμα: ξαναμπαίνουν ΜΟΝΟ οι γραμμές που έφυγαν (utils/basketOps) — ως 24/10/2026 το
    // αντίγραφο έσβηνε ό,τι είχε μπει ή αλλάξει μέσα στα 4″. Αλλαγή πελάτη/επανάληψη: όλο το καλάθι.
    const snapshot = undoState.basket;
    if (undoState.restore === 'whole') setBasket(snapshot);
    else setBasket(prev => restoreRemovedLines(prev, snapshot));
    setOrderDiscount(undoState.orderDiscount);
    setDiscountType(undoState.discountType);
    setFixedDiscountAmount(undoState.fixedDiscountAmount);
    // Μαζί με το καλάθι και η επιλογή τέλους του ταμία (π.χ. χαρισμένο τέλος), αλλιώς ο
    // αυτοματισμός θα ξανάβαζε σιωπηλά το τέλος στο καλάθι που επανήλθε.
    setFeeOverride(undoState.feeOverride);
    setDeliveryNotes(undoState.deliveryNotes);
    setOrderMode(undoState.orderMode);
    setUndoState(null);
  };
  useEffect(() => () => { if (undoTimerRef.current) clearTimeout(undoTimerRef.current); }, []);

  // «Νέα αναζήτηση» / άνοιγμα άλλου πελάτη: το καλάθι ΔΕΝ μένει για τον επόμενο (ως 12/10/2026
  // το «Συνέχεια παραγγελίας (n)» έδειχνε τα είδη άλλου πελάτη). Με «Αναίρεση» 4″.
  const leaveCustomer = () => {
    customerSeqRef.current += 1;
    if (customer && basket.length > 0) {
      offerUndo(t('delivery:undo.customerLeft', { name: customer.name || customer.phone }), true);
    }
    clearDeliveryBasket();
    setDeliveryNotes('');
    resetCheckoutChoices();
    setUpsellPick(null);
    setShowCheckoutModal(false);
    setShowOutOfZoneWarning(false);
    setOrderMode(false);
    setCurrentView('categories');
    setCustomer(null);
    setPhoneNumber('');
    setSelectedAddress(null);
    // Μισοσυμπληρωμένη φόρμα νέου πελάτη (άγνωστος αριθμός) δεν περνά στην επόμενη κλήση.
    setShowNewCustomerForm(false);
    setShowSearchResults(false);
    setSearchResults([]);
    setDeliveryKb(null);
    setNewCustomerData({
      name: '', phone_secondary: '', email: '', customer_comments: '',
      address: {
        street_address: '', area: '', city: '', floor: '', doorbell: '', postal_code: '',
        delivery_notes: '', latitude: null, longitude: null, formatted_address: ''
      }
    });
  };

  // ---- Αναγγελία κλήσης → οθόνη delivery χωρίς φόρτωση σελίδας (utils/deliveryCallIntent) ----
  const deferredIntentRef = useRef<DeliveryCallIntent | null>(null);
  const openCallIntent = async (intent: DeliveryCallIntent) => {
    if (!intent.phone) return;
    // Αποστολή σε εξέλιξη: ο επόμενος πελάτης ανοίγει μόλις τελειώσει, όχι πάνω της.
    if (submitInFlightRef.current) { deferredIntentRef.current = intent; return; }
    const digits = (p?: string) => String(p || '').replace(/^\+30/, '').replace(/^\+/, '').replace(/\s+/g, '');
    const samePerson = !!customer && [customer.phone, customer.phone_secondary].some(p => p && digits(p) === intent.phone);
    if (samePerson && !intent.reorderOrderId) return; // ήδη ανοιχτός — το καλάθι του μένει
    leaveCustomer();
    const seq = customerSeqRef.current;
    const isCurrent = () => customerSeqRef.current === seq;
    setPhoneNumber(intent.phone);
    const found = await searchCustomer(intent.phone, isCurrent);
    if (!found || !intent.reorderOrderId || !isCurrent()) return;
    const ref = found.recent_orders?.find(o => o.id === intent.reorderOrderId);
    await repeatOrder({
      id: intent.reorderOrderId,
      delivery_address_id: intent.addressId ?? ref?.delivery_address_id ?? null,
      order_type: ref?.order_type ?? 'delivery',
    }, { customer: found, isCurrent });
  };
  const callIntentRef = useRef<(intent: DeliveryCallIntent) => void>(() => {});
  callIntentRef.current = (intent) => { void openCallIntent(intent); };
  useEffect(() => {
    if (!store?.id) return;
    const run = () => {
      const intent = takePendingDeliveryIntent();
      if (intent) callIntentRef.current(intent);
    };
    run(); // η σελίδα μόλις άνοιξε από την αναγγελία
    window.addEventListener(DELIVERY_INTENT_EVENT, run);
    return () => window.removeEventListener(DELIVERY_INTENT_EVENT, run);
  }, [store?.id]);
  
  // Company details functions
  const openCompanyModal = () => {
    if (customer) {
      setCompanyFormData({
        company_name: customer.company_name || '',
        company_vat: customer.company_vat || '',
        company_tax_office: customer.company_tax_office || '',
        company_commercial_title: customer.company_commercial_title || '',
        company_address: customer.company_address || '',
        company_city: customer.company_city || '',
        company_postal_code: customer.company_postal_code || '',
        company_country_code: customer.company_country_code || '',
        company_branch_code: customer.company_branch_code || 0
      });
    }
    setShowCompanyModal(true);
  };
  
  const lookupCompanyByVat = async () => {
    if (!companyFormData.company_vat || companyFormData.company_vat.length < 9) {
      showToast('warning', t('delivery:vatValidationError'));
      return;
    }
    
    setIsLookingUpCompany(true);
    
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/stores/${store?.id}/aade/lookup/${companyFormData.company_vat}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.company) {
          const company = data.company;
          setCompanyFormData({
            ...companyFormData,
            company_name: company.name || '',
            company_commercial_title: company.commercial_title || '',
            company_tax_office: company.doy_description || '',
            company_address: company.address ? 
              `${company.address.street || ''} ${company.address.number || ''}`.trim() : '',
            company_city: company.address?.city || '',
            company_postal_code: company.address?.postal_code || '',
            company_country_code: company.country_code || 'GR',
            company_branch_code: company.branch_code || 0
          });
        } else {
          showToast('warning', 'Το ΑΦΜ δεν βρέθηκε ή δεν είναι έγκυρο');
        }
      } else {
        showToast('error', 'Η αναζήτηση ΑΦΜ απέτυχε');
      }
    } catch (error) {
      console.error('Error looking up VAT:', error);
      showToast('error', 'Η αναζήτηση ΑΦΜ απέτυχε');
    } finally {
      setIsLookingUpCompany(false);
    }
  };
  
  const saveCompanyDetails = async () => {
    if (!customer) return;
    
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/stores/${store?.id}/customers/${customer.id}/company`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
          },
          body: JSON.stringify(companyFormData)
        }
      );
      
      if (response.ok) {
        // Update local customer state
        setCustomer({
          ...customer,
          ...companyFormData
        });
        setShowCompanyModal(false);
      } else {
        showToast('error', 'Η αποθήκευση στοιχείων εταιρείας απέτυχε');
      }
    } catch (error) {
      console.error('Error saving company details:', error);
      showToast('error', 'Η αποθήκευση στοιχείων εταιρείας απέτυχε');
    }
  };

  const createCustomer = async () => {
    if (!newCustomerData.name) {
      showToast('warning', 'Συμπλήρωσε όνομα πελάτη');
      return;
    }
    if (!newCustomerData.address.street_address) {
      showToast('warning', 'Συμπλήρωσε διεύθυνση παράδοσης');
      return;
    }
    if (!newCustomerData.address.doorbell) {
      showToast('warning', 'Συμπλήρωσε όνομα κουδουνιού');
      return;
    }
    
    // Warn if no coordinates - user should click map or search first
    if (!newCustomerData.address.latitude || !newCustomerData.address.longitude) {
      console.warn('⚠️ No coordinates for address. Click on map or use search button to geocode.');
    }
    
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/stores/${store?.id}/customers`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
          },
          body: JSON.stringify({
            phone: phoneNumber,
            phone_secondary: newCustomerData.phone_secondary || null,
            name: newCustomerData.name,
            email: newCustomerData.email || null,
            customer_comments: newCustomerData.customer_comments || null,
            address: newCustomerData.address
          })
        }
      );

      const data = await response.json();
      setCustomer(data.customer);
      newCustomerAutocompleteRef.current = null;
      setShowNewCustomerForm(false);
      if (data.customer.addresses && data.customer.addresses.length > 0) {
        setSelectedAddress(data.customer.addresses[0]);
      }
    } catch (error) {
      console.error('Error creating customer:', error);
      showToast('error', 'Η δημιουργία πελάτη απέτυχε');
    }
  };

  // Add new address to existing customer
  const addNewAddress = async () => {
    if (!customer) return;
    
    if (!newAddressData.street_address) {
      showToast('warning', 'Συμπλήρωσε οδό και αριθμό');
      return;
    }
    if (!newAddressData.doorbell) {
      showToast('warning', 'Συμπλήρωσε όνομα κουδουνιού');
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/stores/${store?.id}/customers/${customer.id}/addresses`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
          },
          body: JSON.stringify(newAddressData)
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Update customer with new address
        // If new address is default, remove default from others
        const existingAddresses = newAddressData.is_default 
          ? (customer.addresses || []).map(addr => ({ ...addr, is_default: false }))
          : (customer.addresses || []);
        
        setCustomer({
          ...customer,
          addresses: [...existingAddresses, data.address]
        });
        setSelectedAddress(data.address);
        addAddressAutocompleteRef.current = null;
        setShowAddressForm(false);
        // Reset form
        setNewAddressData({
          street_address: '',
          area: '',
          city: '',
          floor: '',
          doorbell: '',
          postal_code: '',
          delivery_notes: '',
          latitude: null,
          longitude: null,
          formatted_address: '',
          address_type: 'home',
          is_default: false
        });
      } else {
        showToast('error', 'Η προσθήκη διεύθυνσης απέτυχε');
      }
    } catch (error) {
      console.error('Error adding address:', error);
      showToast('error', 'Η προσθήκη διεύθυνσης απέτυχε');
    }
  };

  // Open edit address modal
  const openEditAddress = (address: Address, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent selecting the address
    setEditingAddress(address);
    setEditAddressData({
      street_address: address.street_address || '',
      area: address.area || '',
      city: address.city || '',
      floor: address.floor || '',
      doorbell: address.doorbell || '',
      postal_code: address.postal_code || '',
      delivery_notes: address.delivery_notes || '',
      latitude: address.latitude != null ? parseFloat(String(address.latitude)) || null : null,
      longitude: address.longitude != null ? parseFloat(String(address.longitude)) || null : null,
      formatted_address: address.formatted_address || '',
      address_type: address.address_type || 'home',
      is_default: address.is_default || false
    });
    setShowEditAddressForm(true);
  };

  // Update existing address
  const updateAddress = async () => {
    if (!customer || !editingAddress) return;
    
    if (!editAddressData.street_address) {
      showToast('warning', 'Συμπλήρωσε οδό και αριθμό');
      return;
    }
    if (!editAddressData.doorbell) {
      showToast('warning', 'Συμπλήρωσε όνομα κουδουνιού');
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/stores/${store?.id}/customers/${customer.id}/addresses/${editingAddress.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
          },
          body: JSON.stringify(editAddressData)
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Update customer addresses
        const updatedAddresses = customer.addresses?.map(addr => 
          addr.id === editingAddress.id ? data.address : 
          // If this address is now default, remove default from others
          (editAddressData.is_default ? { ...addr, is_default: false } : addr)
        ) || [];
        
        setCustomer({
          ...customer,
          addresses: updatedAddresses
        });
        
        // Update selected address if it was the one edited
        if (selectedAddress?.id === editingAddress.id) {
          setSelectedAddress(data.address);
        }
        
        setShowEditAddressForm(false);
        setEditingAddress(null);
      } else {
        showToast('error', 'Η ενημέρωση διεύθυνσης απέτυχε');
      }
    } catch (error) {
      console.error('Error updating address:', error);
      showToast('error', 'Η ενημέρωση διεύθυνσης απέτυχε');
    }
  };

  const formatCurrency = (amount: number | string) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return `${formatMoney((numAmount || 0))}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('el-GR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Fetch products and categories
  // Επιστρέφει τη λίστα που έβαλε στο state ([] σε αποτυχία): η επανάληψη παραγγελίας
  // (FE-5) τη χρειάζεται αμέσως, πριν ξαναζωγραφιστεί η οθόνη με το νέο products.
  const fetchProducts = async (): Promise<Product[]> => {
    setIsLoadingProducts(true);
    try {
      const [prods, cats] = await Promise.all([
        productsAPI.getProducts(store!.id),
        productsAPI.getCategories(store!.id)
      ]);

      // Ίδιος κανόνας με το Take Away (available_only στον server): έξω και τα
      // Μη Διαθέσιμα ΚΑΙ όσα έκρυψε το κατάστημα — πριν η απόκρυψη δεν ίσχυε εδώ.
      // Και τα κρυμμένα μεγέθη έξω (utils/sellableSizes).
      const posProducts = withSellableSizes<Product>(prods.filter((p: Product) => p.is_available && p.is_hidden !== true));
      setProducts(posProducts);
      const activeCategories = cats.filter((c: Category) => c.is_active);
      setCategories(activeCategories);
      
      // Auto-select the first category
      if (activeCategories.length > 0) {
        setSelectedCategory(activeCategories[0].id);
      }
      return posProducts;
    } catch (error) {
      console.error('Error fetching products:', error);
      showToast('error', 'Η φόρτωση προϊόντων απέτυχε');
      return [];
    } finally {
      setIsLoadingProducts(false);
    }
  };

  // «Πιάτα ημέρας» (migration 278): στον κατάλογο η κατηγορία πρώτη και ΜΟΝΟ τα σημερινά· η πλήρης λίστα
  // μένει για την επανάληψη παραγγελίας. Κάθε αλλαγή ξαναφέρνει τα προϊόντα (νέο πιάτο, νέα τιμή).
  const { today: dailyToday } = useDailyDishesToday(store?.id, store?.daily_dishes_enabled === true, () => { fetchProducts(); });
  const dailyCatalog = useMemo(() => applyDailyDishes(categories, products, dailyToday), [categories, products, dailyToday]);

  // Fetch delivery settings (discount config)
  const fetchDeliverySettings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/delivery/${store?.id}/settings`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setDeliverySettings(data);
      }
    } catch (error) {
      console.error('Failed to fetch delivery settings:', error);
    }
  };

  // Fetch training-mode flag (shared endpoint with TakeAway/document types)
  const fetchTrainingModeFlag = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/fiscal/stores/${store?.id}/cashier-document-types`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTrainingModeEnabled(data.trainingModeEnabled || false);
      }
    } catch (error) {
      console.error('Failed to fetch training mode flag:', error);
    }
  };

  const { hasPermission } = usePermissions();

  // Start new order or continue existing order
  const startNewOrder = async (clearBasket: boolean = true) => {
    if (!selectedAddress) {
      showToast('warning', 'Διάλεξε διεύθυνση παράδοσης');
      return;
    }
    setOrderMode(true);
    // Only clear basket if explicitly requested (new order, not continuing)
    if (clearBasket && basket.length === 0) {
      setBasket([]);
      setDeliveryNotes('');
    }
    await Promise.all([fetchProducts(), fetchDeliverySettings(), fetchTrainingModeFlag()]);
  };
  
  // Continue existing order (return to shop without clearing basket)
  const continueOrder = async () => {
    if (!selectedAddress) {
      showToast('warning', 'Διάλεξε διεύθυνση παράδοσης');
      return;
    }
    setOrderMode(true);
    await Promise.all([fetchProducts(), fetchDeliverySettings(), fetchTrainingModeFlag()]);
  };

  // Barcode scan handler — only active while orderMode is true
  const handleBarcodeScan = async (code: string) => {
    if (!code.trim() || scanningBarcode || submitInFlightRef.current) return;
    setScanningBarcode(true);
    setBarcodeError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/products/barcode-lookup?code=${encodeURIComponent(code.trim())}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` }
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        setBarcodeError(err.error || t('takeaway:barcodeProductNotFound'));
        setTimeout(() => setBarcodeError(''), 3000);
        setScanningBarcode(false);
        return;
      }
      const data = await response.json() as BarcodeLookupData;

      // Μέγεθος και τιμή της γραμμής από το utils/barcodeLine: το προεπιλεγμένο μέγεθος
      // (αποφάσεις 12/F1-7)· ζυγιστικό → ποσά του server ανά κανάλι· ανοιχτή τιμή → χωρίς μέγεθος.
      const line = resolveBarcodeLine({ data, catalog: products, channel: 'delivery' });

      if (line.kind === 'error') {
        // Τέλος χωρίς ποσό state_fees: το ίδιο μήνυμα με το πλακίδιο (απόφαση 33).
        setBarcodeError(line.reason === 'no_price'
          ? t(isFeeProduct(data.product) ? 'delivery:feeAmountMissing' : 'delivery:barcodeNoPrice')
          : t('takeaway:barcodeProductUnavailable'));
        setTimeout(() => setBarcodeError(''), 3000);
        setScanningBarcode(false);
        return;
      }

      const product = line.product;

      // Τέλος με barcode: ίδια ροή με το click (απόφαση 1 ποσό, απόφαση 33 χωρίς ποσό → καμία
      // γραμμή), ΠΡΙΝ από ζύγι/αύξηση ποσότητας.
      if (isFeeProduct(product)) {
        addFeeManually(product);
        setScanningBarcode(false);
        return;
      }

      // Το line.kind ακολουθεί το data.scan.type· γράφονται και τα δύο για τον TypeScript.
      if (data.scan.type === 'weighted' && line.kind === 'weighted') {
        // Το μέγεθος (μόνο σε canonical) πάει ως selectedSize: το κατώτατο του server είναι
        // τιμή μεγέθους × βάρος (F1-7). Η τιμή είναι το σύνολο του server για το delivery.
        const { weight, total } = line;
        const basketItem: BasketItem = {
          id: `${product.id}-${Date.now()}`,
          product,
          selectedSize: line.size,
          quantity: 1,
          price: total,
          totalPrice: total,
          removedIngredients: [],
          addedExtras: [],
          specialInstructions: '',
          weight,
          weightUnit: 'kg'
        };
        setBasket(prev => [...prev, basketItem]);
        setLastAddedProduct(`${product.name} (${weight.toFixed(3)}kg)`);
        setAddedToCartAnimation(true);
        setTimeout(() => setAddedToCartAnimation(false), 1500);
      } else if (line.kind === 'line') {
        // Κανονικό: ΑΥΣΤΗΡΟΣ κανόνας συγχώνευσης (F1-W4-3, δες utils/barcodeLine) — ποσότητα +1
        // ΜΟΝΟ σε γραμμή ίδιου προϊόντος/μεγέθους, χωρίς επιλογές/σημείωση/έκπτωση και με ίδια
        // τιμή μονάδας· αλλιώς νέα γραμμή, που τα helpers χτίζουν ΑΚΡΙΒΩΣ στην τιμή του barcode.
        const existingItem = findBarcodeMergeLine(basket, line);
        if (existingItem) {
          setBasket(prev => prev.map(item =>
            item.id === existingItem.id ? { ...item, quantity: item.quantity + 1 } : item
          ));
        } else if (line.size) {
          // discount 0: μια έκπτωση που έμεινε ανοιχτή σε modal δεν περνά στη γραμμή του barcode.
          addToBasketWithSize(product, line.size, { discount: 0 });
        } else {
          addToBasket(product, line.unitPrice);
        }
        setLastAddedProduct(product.name);
        setAddedToCartAnimation(true);
        setTimeout(() => setAddedToCartAnimation(false), 1500);
      }
    } catch (error) {
      setBarcodeError(t('takeaway:barcodeSearchError'));
      setTimeout(() => setBarcodeError(''), 3000);
    }
    setScanningBarcode(false);
  };

  // Fetch option groups for a product; pre-selects defaults
  const fetchProductOptionGroups = async (productId: number): Promise<PosOptionGroup[]> => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/products/${productId}/option-groups`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` }
      });
      if (res.ok) {
        const groups: PosOptionGroup[] = await res.json();
        setProductOptionGroups(groups);
        const defaults: SelectedOptionEntry[] = [];
        for (const g of groups) {
          const def = g.choices.find(c => c.is_default);
          if (def) defaults.push({ group_id: g.id, group_name: g.name, choice_id: def.id, choice_name: def.name, price_modifier: def.price_modifier });
        }
        setPendingSelectedOptions(defaults);
        return groups;
      }
    } catch (e) { console.error('Failed to fetch option groups:', e); }
    return [];
  };

  // Τέλη (fee_category 8/16) με το χέρι: ποσό = feeUnitGross (state_fees, απόφαση
  // 1_fees_source), ΠΑΝΤΑ χωρίς μέγεθος — ούτε modal μεγεθών, ούτε option groups, ούτε
  // ανοιχτή τιμή/ζύγι. Αλλιώς ένα τέλος με μέγεθος (7700 «ΣΑΚΟΥΛΑ») χρεωνόταν βάση +
  // μέγεθος. Χωρίς ποσό δεν μπαίνει γραμμή (απόφαση 33): μήνυμα στον ταμία και τέλος.
  // Η γραμμή φτιάχνεται στο utils/autoFee (manualFeeLine), κοινό με το TakeAway και με tests.
  const addFeeManually = (product: Product) => {
    const validation = canAddToBasket(product);
    if (!validation.allowed) {
      showToast('warning', validation.message || 'Μη έγκυρη επιλογή');
      return;
    }
    const feeLine = manualFeeLine(product);
    if (!feeLine) {
      showToast('error', t('delivery:feeAmountMissing'));
      return;
    }
    // Δεύτερο πάτημα = +1 στη χειροκίνητη γραμμή (όπως κάθε είδος)· ποτέ στη γραμμή του
    // αυτοματισμού ή στην ιστορική της επανάληψης (utils/autoFee).
    setBasket(prev => addOrMergeManualFee(prev, feeLine, autoManagedFeeLineId({
      basket: prev,
      feeProduct: products.find(p => p.fee_category === 16),
      feeOverride
    })));
    setLastAddedProduct(product.name);
    setAddedToCartAnimation(true);
    setTimeout(() => setAddedToCartAnimation(false), 1500);
  };

  // Handle product click
  const handleProductClick = async (product: Product) => {
    // Αποστολή σε εξέλιξη: το καλάθι που στέλνεται δεν αλλάζει (θα χανόταν στον μηδενισμό μετά).
    if (submitInFlightRef.current) return;
    // Guard: don't fire click if long-press already triggered
    if (longPressTriggeredRef.current) {
      longPressTriggeredRef.current = false;
      return;
    }
    if (isFeeProduct(product)) {
      addFeeManually(product);
      return;
    }
    setSelectedProduct(product);

    // «Βάρος + τιμή κιλού» (ζυγιζόμενο ΚΑΙ ελεύθερης τιμής): πρώτα το βάρος, μετά η τιμή κιλού
    // (utils/weightedOpenPrice). ΠΡΙΝ από τον κλάδο της ανοιχτής τιμής, που θα ζητούσε μόνο ποσό.
    if (isWeightedOpenPrice(product)) {
      setWeightForOpenPrice(null);
      setWeightInput('');
      setShowWeightModal(true);
      return;
    }

    // If product has open price, show price input numpad
    if (product.is_open_price) {
      setWeightForOpenPrice(null);
      setOpenPriceInput('');
      setShowOpenPriceModal(true);
      return;
    }

    // If product is weight-variable, show weight input modal
    if (product.is_weight_variable) {
      setWeightInput('');
      setShowWeightModal(true);
      return;
    }

    // Fetch option groups — quick-click shows ONLY required groups
    const groups = await fetchProductOptionGroups(product.id);
    const requiredGroups = groups.filter(g => g.is_required);

    // If product has multiple sizes, show size modal first (options come after size)
    if (product.sizes && product.sizes.length > 1) {
      setSelectedSize(defaultSize(product.sizes));
      setShowSizeModal(true);
    } else if (product.sizes && product.sizes.length === 1) {
      const onlySz = product.sizes[0];
      if (requiredGroups.length > 0) {
        setProductOptionGroups(requiredGroups);
        setOptionModalPendingSize(onlySz);
        setShowOptionModal(true);
      } else {
        addToBasketWithSize(product, onlySz);
      }
    } else {
      if (requiredGroups.length > 0) {
        setProductOptionGroups(requiredGroups);
        setOptionModalPendingSize(null);
        setShowOptionModal(true);
      } else {
        addToBasket(product);
      }
    }
  };

  // Handle long press for customization
  const handleProductLongPress = async (product: Product) => {
    if (isFeeProduct(product)) {
      addFeeManually(product);
      return;
    }
    setSelectedProduct(product);
    const initialSize = defaultSize(product.sizes);
    setSelectedSize(initialSize);
    setRemovedIngredients([]);
    setAddedExtras([]);
    setSpecialInstructions('');
    setProductDiscount(0);
    setPendingSelectedOptions([]);

    // Fetch ingredients AND ALL option groups in parallel
    await Promise.all([
      fetchProductOptionGroups(product.id),
      initialSize ? fetchSizeIngredients(initialSize.id) : (product.id ? fetchProductIngredients(product.id) : Promise.resolve())
    ]);

    setShowCustomizeModal(true);
  };
  
  // Fetch ingredients for a product size (delegated to shared hook)
  const fetchSizeIngredients = async (sizeId: number) => {
    await fetchIngredientsBySize(sizeId, store?.id);
  };
  
  // Fetch ingredients for a product (no size) (delegated to shared hook)
  const fetchProductIngredients = async (productId: number) => {
    if (store?.id) await fetchIngredientsByProduct(productId, store.id);
  };

  const handleMouseDown = (product: Product) => {
    if (submitInFlightRef.current) return;
    longPressTriggeredRef.current = false;
    const timer = setTimeout(() => {
      longPressTriggeredRef.current = true;
      handleProductLongPress(product);
    }, 500); // 500ms for long press
    setLongPressTimer(timer);
  };

  const handleMouseUp = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  // Check if product can be added to basket (no mixing services with products/merchandise)
  const canAddToBasket = (product: Product): { allowed: boolean; message?: string } => {
    if (basket.length === 0) {
      return { allowed: true };
    }

    const productType = product.product_type || 'ΠΡΟΙΟΝ';
    const isService = productType === 'ΥΠΗΡΕΣΙΑ';
    
    const hasServices = basket.some(item => item.product.product_type === 'ΥΠΗΡΕΣΙΑ');
    const hasProductsOrMerchandise = basket.some(item => 
      item.product.product_type === 'ΠΡΟΙΟΝ' || 
      item.product.product_type === 'ΕΜΠΟΡΕΥΜΑ' ||
      !item.product.product_type
    );

    if (isService && hasProductsOrMerchandise) {
      return { 
        allowed: false, 
        message: t('delivery:cannotAddServiceWithProduct') 
      };
    }

    if (!isService && hasServices) {
      return { 
        allowed: false, 
        message: t('delivery:cannotAddProductWithService') 
      };
    }

    return { allowed: true };
  };

  // Μετά την πρόταση πώλησης: πληρωμή με ένα πάτημα (νέα εμφάνιση) ή το παράθυρο ολοκλήρωσης.
  const proceedToCheckout = () => {
    if (quickPayRef.current) {
      const method = quickPayRef.current;
      quickPayRef.current = null;
      runQuickPay(method);
      return;
    }
    setShowCheckoutModal(true);
  };

  /** «Checkout»: πρώτα η πρόταση πώλησης (μία φορά ανά παραγγελία), μετά η ολοκλήρωση. */
  const startCheckout = () => {
    if (submitInFlightRef.current) return;
    if (!upsellShownRef.current) {
      const pick = pickUpsell(upsellSuggestions, basket, products, {
        canAdd: (p) => canAddToBasket(p).allowed,
        hiddenCategoryIds,
      });
      if (pick) {
        upsellShownRef.current = true;
        recordUpsellImpression(pick.suggestion.id);
        setUpsellPick(pick);
        return;
      }
    }
    proceedToCheckout();
  };

  // «Προσθήκη» = πάτημα στον κατάλογο, με τη σήμανση της πρότασης στη γραμμή.
  const handleUpsellAdd = async (product: Product) => {
    upsellTagRef.current = upsellPick ? upsellPick.suggestion.id : null;
    setUpsellPick(null);
    longPressTriggeredRef.current = false; // αλλιώς το handleProductClick το αγνοεί
    try {
      await handleProductClick(product);
    } finally {
      setUpsellContinue(true);
    }
  };

  const handleUpsellDecline = () => {
    setUpsellPick(null);
    proceedToCheckout();
  };

  useEffect(() => {
    if (!upsellContinue) return;
    if (showSizeModal || showOptionModal || showCustomizeModal || showWeightModal || showOpenPriceModal) return;
    upsellTagRef.current = null; // ακυρώθηκε χωρίς γραμμή: η σήμανση δεν περνά σε επόμενο προϊόν
    setUpsellContinue(false);
    proceedToCheckout();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upsellContinue, showSizeModal, showOptionModal, showCustomizeModal, showWeightModal, showOpenPriceModal]);

  // Add product to basket with size
  const addToBasketWithSize = (product: Product, size: ProductSize, customizations?: {
    removedIngredients?: number[];
    removedIngredientNames?: string[];
    addedExtras?: any[];
    specialInstructions?: string;
    discount?: number;
    selected_options?: SelectedOptionEntry[];
  }) => {
    const validation = canAddToBasket(product);
    if (!validation.allowed) {
      showToast('warning', validation.message || 'Μη έγκυρη επιλογή');
      return;
    }
    const extrasPrice = customizations?.addedExtras?.reduce((sum, extra) =>
      sum + ((extra.extra_price || extra.price || 0) * (extra.quantity || 1)), 0) || 0;
    const optionsPrice = customizations?.selected_options?.reduce((s, o) => s + o.price_modifier, 0) || 0;
    let totalPrice = lineUnitPrice(product, size, 'delivery') + extrasPrice + optionsPrice;

    const appliedDiscount = customizations?.discount ?? productDiscount;
    if (appliedDiscount > 0) {
      totalPrice = totalPrice * (1 - appliedDiscount / 100);
    }

    const basketItem: BasketItem = {
      id: `${product.id}-${size.id}-${Date.now()}`,
      product,
      selectedSize: size,
      quantity: 1,
      price: totalPrice,
      totalPrice,
      removedIngredients: customizations?.removedIngredients || [],
      removedIngredientNames: customizations?.removedIngredientNames || [],
      addedExtras: customizations?.addedExtras || [],
      selected_options: customizations?.selected_options?.length ? customizations.selected_options : undefined,
      specialInstructions: customizations?.specialInstructions || '',
      discount: appliedDiscount > 0 ? appliedDiscount : undefined,
      ...takeUpsellTag()
    };

    setBasket(prev => addOrMergeBasketLine(prev, basketItem));

    // Trigger add to cart animation
    setLastAddedProduct(product.name);
    setAddedToCartAnimation(true);
    setTimeout(() => setAddedToCartAnimation(false), 1500);

    setShowSizeModal(false);
    setShowCustomizeModal(false);
    setSelectedProduct(null);
    setSelectedSize(null);
    setRemovedIngredients([]);
    setAddedExtras([]);
    setSpecialInstructions('');
    setProductDiscount(0);
    setProductOptionGroups([]);
    setPendingSelectedOptions([]);
    setShowOptionModal(false);
    setOptionModalPendingSize(null);
  };

  // Add product to basket (no size). `unitPrice`: η τιμή του barcode (utils/barcodeLine) —
  // ανοιχτή τιμή/ζυγιζόμενο με κανονικό barcode μπαίνουν χωρίς μέγεθος σε 0 €, όπως ως τώρα,
  // ενώ το lineUnitPrice χωρίς μέγεθος θα τα τιμολογούσε στο προεπιλεγμένο.
  const addToBasket = (product: Product, unitPrice?: number) => {
    const validation = canAddToBasket(product);
    if (!validation.allowed) {
      showToast('warning', validation.message || 'Μη έγκυρη επιλογή');
      return;
    }
    const price = unitPrice ?? lineUnitPrice(product, null, 'delivery');
    
    const basketItem: BasketItem = {
      id: `${product.id}-${Date.now()}`,
      product,
      quantity: 1,
      price,
      totalPrice: price,
      removedIngredients: [],
      addedExtras: [],
      ...takeUpsellTag()
    };
    
    setBasket(prev => addOrMergeBasketLine(prev, basketItem));
    
    // Trigger add to cart animation
    setLastAddedProduct(product.name);
    setAddedToCartAnimation(true);
    setTimeout(() => setAddedToCartAnimation(false), 1500);
  };

  // Add weight-variable product to basket
  const addWeightVariableToBasket = (product: Product, weight: number) => {
    const validation = canAddToBasket(product);
    if (!validation.allowed) {
      showToast('warning', validation.message || 'Μη έγκυρη επιλογή');
      return;
    }

    // Τιμή κιλού = η τιμή delivery του προεπιλεγμένου μεγέθους, ίδια με το πλακίδιο.
    const pricePerUnit = productDisplayPrice(product, 'delivery');
    const totalPrice = pricePerUnit * weight;
    const weightUnit = product.selling_unit || 'KG';
    
    const basketItem: BasketItem = {
      id: `${product.id}-${Date.now()}`,
      product,
      quantity: 1,
      price: totalPrice,
      totalPrice,
      removedIngredients: [],
      addedExtras: [],
      specialInstructions: '',
      weight,
      weightUnit,
      ...takeUpsellTag()
    };

    // Πάντα νέα γραμμή (ζυγιζόμενο), στο ΤΡΕΧΟΝ καλάθι — όχι `[...basket, …]` του αντιγράφου (utils/basketOps).
    setBasket(prev => [...prev, basketItem]);

    // Trigger add to cart animation
    const unitLabel = weightUnit === 'LT' ? 'lt' : 'kg';
    setLastAddedProduct(`${product.name} (${weight.toFixed(3)}${unitLabel})`);
    setAddedToCartAnimation(true);
    setTimeout(() => setAddedToCartAnimation(false), 1500);
    
    setShowWeightModal(false);
    setSelectedProduct(null);
    setWeightInput('');
  };

  // «Βάρος + τιμή κιλού», βήμα 1: το βάρος μένει στην άκρη και ανοίγει η τιμή κιλού. Και τα δύο
  // παράθυρα αλλάζουν στο ίδιο render — το effect της πρότασης πώλησης δεν βλέπει κενό ανάμεσα.
  const takeWeightForOpenPrice = (_product: Product, weight: number) => {
    setWeightForOpenPrice(weight);
    setShowWeightModal(false);
    setWeightInput('');
    setOpenPriceInput('');
    setShowOpenPriceModal(true);
  };

  // «Βάρος + τιμή κιλού», βήμα 2: γραμμή ζυγιζόμενου (ποσότητα 1, βάρος, σύνολο = βάρος × τιμή
  // κιλού με μία στρογγυλοποίηση) — ίδια με του addWeightVariableToBasket, με την τιμή του ταμία.
  const addWeightedOpenPriceToBasket = (product: Product, weight: number, pricePerUnit: number) => {
    const validation = canAddToBasket(product);
    if (!validation.allowed) {
      showToast('warning', validation.message || 'Μη έγκυρη επιλογή');
      return;
    }
    const line = weightedOpenPriceLine(product, weight, pricePerUnit);
    if (!line) return;
    const basketItem: BasketItem = { ...line, ...takeUpsellTag() };
    setBasket(prev => [...prev, basketItem]);

    const unitLabel = basketItem.weightUnit === 'LT' ? 'lt' : 'kg';
    setLastAddedProduct(`${product.name} (${weight.toFixed(3)}${unitLabel})`);
    setAddedToCartAnimation(true);
    setTimeout(() => setAddedToCartAnimation(false), 1500);

    setShowOpenPriceModal(false);
    setSelectedProduct(null);
    setOpenPriceInput('');
    setWeightForOpenPrice(null);
  };

  // Add open-price product to basket with manually entered price
  const addOpenPriceToBasket = (product: Product, price: number) => {
    const validation = canAddToBasket(product);
    if (!validation.allowed) {
      showToast('warning', validation.message || 'Μη έγκυρη επιλογή');
      return;
    }

    const defaultSz = defaultSize(product.sizes) ?? undefined;

    const basketItem: BasketItem = {
      id: `${product.id}-${Date.now()}`,
      product,
      selectedSize: defaultSz,
      ...takeUpsellTag(),
      quantity: 1,
      price,
      totalPrice: price,
      removedIngredients: [],
      addedExtras: [],
      specialInstructions: ''
    };

    setBasket(prev => addOrMergeBasketLine(prev, basketItem));

    setLastAddedProduct(`${product.name} (${formatMoney(price)})`);
    setAddedToCartAnimation(true);
    setTimeout(() => setAddedToCartAnimation(false), 1500);

    setShowOpenPriceModal(false);
    setSelectedProduct(null);
    setOpenPriceInput('');
  };

  // Αλλαγή του ταμία σε είδος της επανάληψης παραγγελίας: αν τα ιστορικά τέλη ήταν ο κανόνας, το
  // τέλος 16 ακολουθεί πλέον τα είδη (F1-W5-11 + F1-W6-2/3, utils/reorderLine
  // followRuleAfterCashierChange)· αλλιώς (πλατφόρμα/χαρισμένο/άνισο) το `next` μένει ως έχει.
  const afterCashierChange = (prev: BasketItem[], next: BasketItem[], itemId: string) =>
    followRuleAfterCashierChange(prev, next, itemId, products.find(p => p.fee_category === 16), feeOverride);

  // Update quantity in basket — ΠΑΝΤΑ στο τρέχον καλάθι (utils/basketOps, όπως το Take Away): με το
  // αντίγραφο της ζωγραφιάς, ένα είδος που περίμενε ακόμη στην ουρά χανόταν σιωπηλά.
  const updateQuantity = (itemId: string, change: number) => {
    if (submitInFlightRef.current) return; // το καλάθι που στέλνεται δεν αλλάζει
    const line = basket.find(item => item.id === itemId);
    if (line && line.quantity + change <= 0) {
      offerUndo(t('delivery:undo.removed', { name: line.product.name }));
    }
    setBasket(prev => afterCashierChange(prev, changeLineQuantityOrRemove(prev, itemId, change), itemId));
  };

  // Remove from basket — με «Αναίρεση» 4″ (όπως το Take Away)· στο τρέχον καλάθι (ίδιος λόγος)
  const removeFromBasket = (itemId: string) => {
    if (submitInFlightRef.current) return;
    const line = basket.find(item => item.id === itemId);
    if (line) offerUndo(t('delivery:undo.removed', { name: line.product.name }));
    setBasket(prev => afterCashierChange(prev, removeBasketLine(prev, itemId), itemId));
  };

  // Open edit modal for basket item
  // Open edit modal — uses the shared ProductCustomizeModal so the cashier
  // sees the SAME fields they saw at long-press (size, ingredients, extras,
  // option groups, notes, discount). Previously the inline edit modal hid
  // option groups (ζάχαρη/γάλα/μέγεθος), losing those selections from view.
  const openEditModal = async (item: BasketItem) => {
    setEditingBasketItem(item);
    setSelectedProduct(item.product);
    setSelectedSize(item.selectedSize || null);
    setRemovedIngredients(item.removedIngredients || []);
    setAddedExtras(item.addedExtras || []);
    setSpecialInstructions(item.specialInstructions || '');
    setProductDiscount(item.discount || 0);
    setPendingSelectedOptions(item.selected_options || []);

    // Fetch option groups + ingredients in parallel. fetchProductOptionGroups
    // pre-selects defaults, so we re-apply the basket item's saved selections
    // immediately after.
    await Promise.all([
      fetchProductOptionGroups(item.product.id),
      item.selectedSize
        ? fetchSizeIngredients(item.selectedSize.id)
        : (item.product.id ? fetchProductIngredients(item.product.id) : Promise.resolve()),
    ]);
    if (item.selected_options && item.selected_options.length > 0) {
      setPendingSelectedOptions(item.selected_options);
    }

    setShowEditModal(true);
  };

  // Γραμμή ανοιχτής τιμής ή ζυγιζόμενου: το ποσό της πριν την επεξεργασία (utils/basketLineEdit).
  // Με προωθητική: το original_unit_price, γιατί το discount είναι τότε το ποσοστό της προωθητικής.
  const lineOwnOf = (item: BasketItem): EditedLineOwn | null => {
    if (!item.product.is_open_price && !item.weight) return null;
    const promo = item.applied_promotion_id != null && item.original_unit_price != null;
    return {
      amount: lineOwnAmount(
        Number(promo ? item.original_unit_price : (item.price ?? item.totalPrice)),
        promo ? 0 : item.discount,
        (item.addedExtras || []).reduce((sum, extra: any) => sum + ((extra.extra_price || extra.price || 0) * (extra.quantity || 1)), 0)
          + (item.selected_options || []).reduce((sum, o) => sum + (o.price_modifier || 0), 0)),
      sizeId: item.selectedSize?.id ?? null,
    };
  };

  // Update basket item after editing
  const updateBasketItem = (options: {
    removedIngredients?: number[];
    removedIngredientNames?: string[];
    addedExtras?: any[];
    selected_options?: SelectedOptionEntry[];
    specialInstructions?: string;
    discount?: number;
    newSize?: ProductSize; // undefined = αμετάβλητο
  }) => {
    if (!editingBasketItem || !selectedProduct) return;

    const sizeToUse = options.newSize || editingBasketItem.selectedSize;
    const extrasPrice = options.addedExtras?.reduce((sum, extra) => sum + ((extra.extra_price || extra.price || 0) * (extra.quantity || 1)), 0) || 0;
    const optionsPrice = options.selected_options?.reduce((sum, o) => sum + (o.price_modifier || 0), 0) || 0;
    // Ζυγιζόμενο/ανοιχτή τιμή: το ποσό της γραμμής· ζυγιζόμενο με άλλο μέγεθος: κιλό × βάρος.
    let totalPrice = editedLineBasePrice(selectedProduct, sizeToUse, editingBasketItem.weight, 'delivery', lineOwnOf(editingBasketItem)) + extrasPrice + optionsPrice;

    // Apply discount if provided
    if (options.discount && options.discount > 0) {
      totalPrice = totalPrice * (1 - options.discount / 100);
    }

    // Στο ΤΡΕΧΟΝ καλάθι (utils/basketOps, όπως το Take Away): ό,τι άλλο άλλαξε ή μπήκε όσο ήταν
    // ανοιχτό το παράθυρο μένει. Ίδιοι κανόνες (afterCashierEdit / afterCashierChange) με πριν.
    const editingId = editingBasketItem.id;
    setBasket(prev => {
      const next = prev.map(item => {
        if (item.id === editingId) {
          // Γραμμή επανάληψης: αν η ξανατιμολόγηση από τον κατάλογο (τιμές χωρίς τέλος) άλλαξε ποσό ή
          // μέγεθος, ο κανόνας του αυτόματου τέλους την πιάνει — εκτός αν το τέλος της είναι ήδη σε
          // ιστορική γραμμή τέλους· μόνο σημείωση/ίδιο ποσό → μένει ιστορική (F1-W6-2/F1-W6-3,
          // utils/reorderLine afterCashierEdit).
          const edited = {
            ...item,
            selectedSize: sizeToUse,
            removedIngredients: options.removedIngredients || [],
            removedIngredientNames: options.removedIngredientNames || item.removedIngredientNames || [],
            addedExtras: options.addedExtras || [],
            selected_options: options.selected_options?.length ? options.selected_options : undefined,
            specialInstructions: options.specialInstructions || '',
            discount: options.discount || 0,
            price: totalPrice
          };
          return afterCashierEdit(item, edited, prev);
        }
        return item;
      });
      return afterCashierChange(prev, next, editingId);
    });

    closeEditModal();
  };

  // Close edit modal and reset states
  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingBasketItem(null);
    setSelectedProduct(null);
    setSelectedSize(null);
    setRemovedIngredients([]);
    setAddedExtras([]);
    setSpecialInstructions('');
    setProductDiscount(0);
    setPendingSelectedOptions([]);
    setProductOptionGroups([]);
  };

  // Handle category selection (3-view navigation)
  const handleCategorySelect = (category: Category) => {
    setSelectedCategory(category.id);
    setSearchQuery('');
    
    if (category.subcategories && category.subcategories.length > 0) {
      setCurrentView('subcategories');
    } else {
      setSelectedSubcategory(null);
      setCurrentView('products');
    }
  };

  // Handle subcategory selection
  const handleSubcategorySelect = (subcategory: Category | null) => {
    setSelectedSubcategory(subcategory?.id || null);
    setSearchQuery('');
    setCurrentView('products');
  };

  // Handle back navigation
  const handleBack = () => {
    if (currentView === 'products') {
      const category = getSelectedCategory();
      if (category?.subcategories && category.subcategories.length > 0) {
        setCurrentView('subcategories');
      } else {
        setSelectedCategory(null);
        setCurrentView('categories');
      }
    } else if (currentView === 'subcategories') {
      setSelectedCategory(null);
      setSelectedSubcategory(null);
      setCurrentView('categories');
    }
  };

  // Home — jump straight back to the catalog root from any depth.
  const handleHome = () => {
    setSelectedCategory(null);
    setSelectedSubcategory(null);
    setSearchQuery('');
    setCurrentView('categories');
  };

  // Νέα εμφάνιση: «Όλα» φαίνεται και με επιλεγμένη κατηγορία — καθαρίζει και αυτήν.
  const handleAllProductsSelect = () => {
    setSelectedCategory(null);
    setSelectedSubcategory(null);
    setSearchQuery('');
    setCurrentView('products');
  };

  // Helper: Get selected category object (used by header breadcrumb)
  const getSelectedCategory = () => categories.find(c => c.id === selectedCategory);

  // State-fee rows (πλαστική σακούλα κωδ. 8, πλαστικά μιας χρήσης κωδ. 16)
  // are passthrough — they DO NOT participate in order-level discounts.
  // Και η ιστορική γραμμή τέλους της επανάληψης παραγγελίας (FE-5): απόφαση F1-W5-11
  // (utils/reorderLine isStateFeeLine).

  // Calculate subtotal (products only, before delivery fee). Order-level
  // discount applies only to the revenue portion; state fees stay full price.
  // Καθαρή συνάρτηση στο utils/deliverySubtotal (αυτούσια ίδια λογική) για να δοκιμάζεται.
  const calculateSubtotal = () => deliverySubtotal(basket, { discountType, orderDiscount, fixedDiscountAmount });

  // Calculate total (subtotal + delivery fee)
  const calculateTotal = () => {
    return calculateSubtotal() + deliveryFee;
  };

  // View order details
  const viewOrderDetails = async (orderId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/orders/${store?.id}/order/${orderId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        }
      });
      
      if (response.ok) {
        const orderData = await response.json();
        setViewingOrder(orderData);
        setShowOrderModal(true);
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
      showToast('error', 'Η φόρτωση της παραγγελίας απέτυχε');
    }
  };

  // Reorder items from a previous order («Επαναπαραγγελία» / νέα εμφάνιση «Ξανά τα ίδια»)
  // Κάθε γραμμή δένεται με το ΠΡΑΓΜΑΤΙΚΟ προϊόν/μέγεθος του τρέχοντος καταλόγου (id, μετά
  // όνομα) και κρατά την ιστορική τιμή (P1-Q9)· ό,τι δεν βρεθεί ΔΕΝ μπαίνει και ο ταμίας το
  // βλέπει σε μήνυμα. utils/reorderLine.
  // Νέα εμφάνιση: διαλέγει και τη διεύθυνση ΕΚΕΙΝΗΣ της παραγγελίας (αν υπάρχει ακόμη στον
  // πελάτη) — δεν χρειάζεται πρώτα επιλογή διεύθυνσης. ctx.customer: ο πελάτης που μόλις άνοιξε
  // η αναγγελία κλήσης (το state δεν τον έχει ακόμη)· ctx.isCurrent: φρουρός αλλαγής πελάτη.
  const repeatOrder = async (
    order: { id: number; delivery_address_id?: number | null; order_type?: string | null },
    ctx: { customer?: Customer | null; isCurrent?: () => boolean } = {},
  ) => {
    const isCurrent = ctx.isCurrent ?? (() => true);
    const cust = ctx.customer ?? customer;
    const orderAddress = v2 && order.delivery_address_id != null
      ? ((cust?.addresses || []).find(a => Number(a.id) === Number(order.delivery_address_id)) ?? null)
      : null;
    const address = orderAddress ?? (ctx.customer ? initialAddressFor(ctx.customer) : selectedAddress);
    if (!address) {
      showToast('warning', 'Διάλεξε πρώτα διεύθυνση παράδοσης');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/orders/${store?.id}/order/${order.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        }
      });
      if (!isCurrent()) return;

      if (response.ok) {
        const orderData = await response.json();
        if (!isCurrent()) return;

        // Add items from previous order to basket
        if (orderData.items && Array.isArray(orderData.items)) {
          // Ο κατάλογος ΠΡΙΝ το ταίριασμα (ως τώρα φορτωνόταν μετά, και το ταίριασμα έβγαινε κενό).
          const catalog = products.length ? products : await fetchProducts();
          if (!isCurrent()) return;
          const { lines, notFound } = buildReorderLines(orderData.items, catalog);

          // Το καλάθι που είχε ήδη ο ταμίας αντικαθίσταται — με «Αναίρεση» ΟΛΟΥ του καλαθιού (άλλη
          // παραγγελία). Μετά τα await το `basket` του render είναι παλιό: η αναίρεση κρατά ό,τι ήταν στην
          // οθόνη ΤΩΡΑ, ώστε να μη χάνεται είδος που μπήκε όσο φόρτωνε η επανάληψη.
          const replacedBasket = paintedBasketRef.current;
          if (replacedBasket.length > 0 && !ctx.customer) offerUndo(t('delivery:undo.replaced'), false, 'whole', replacedBasket);

          // Clear current discount
          setOrderDiscount(0);
          setDiscountType('percent');
          setFixedDiscountAmount(0);

          // Τα τέλη μένουν ΑΚΡΙΒΩΣ τα ιστορικά σε κάθε περίπτωση (πλατφόρμα χωρίς γραμμή τέλους,
          // χαρισμένο, ιστορική γραμμή τέλους) — F1-W6-2/F1-W6-3, buildReorderLines (keepHistoricalFee).
          // Η επιλογή τέλους του προηγούμενου καλαθιού δεν μεταφέρεται, αλλιώς θα πρόσθετε/έβγαζε
          // τέλος σε αυτό.
          setFeeOverride(null);
          setSelectedAddress(address);
          setBasket(lines);
          setOrderMode(true);

          if (notFound.length > 0) {
            showToast('warning', t('delivery:reorderItemsNotFound', { items: notFound.join(', ') }));
          }
          // Ό,τι χρειάζεται η ολοκλήρωση, όπως στη «Νέα παραγγελία»: ρυθμίσεις έκπτωσης του
          // καταστήματος και εκπαίδευση/παραστατικά (ως τώρα η επανάληψη έφερνε μόνο τον κατάλογο).
          await Promise.all([fetchDeliverySettings(), fetchTrainingModeFlag()]);
        } else {
          setOrderDiscount(0);
          setDiscountType('percent');
          setFixedDiscountAmount(0);
          setBasket([]);
        }
      } else {
        showToast('error', 'Η επανάληψη παραγγελίας απέτυχε');
      }
    } catch (error) {
      console.error('Error reordering:', error);
      showToast('error', 'Η επανάληψη παραγγελίας απέτυχε');
    }
  };

  // Lookup VAT number via AADE
  const lookupVatNumber = async () => {
    if (!customerVatNumber || customerVatNumber.length !== 9) {
      setVatLookupError(t('delivery:vatMustBe9Digits'));
      return;
    }

    setIsLookingUpVat(true);
    setVatLookupError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/stores/${store?.id}/aade/lookup/${customerVatNumber}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('delivery:vatLookupFailed'));
      }
      
      if (data.success && data.company) {
        setCustomerCompanyName(data.company.name || '');
        setCustomerCompanyActivity(data.company.activity || '');
        // Address might be an object or string
        const addr = data.company.address;
        if (typeof addr === 'object' && addr !== null) {
          const parts = [addr.street, addr.number, addr.postal_code, addr.city].filter(Boolean);
          setCustomerCompanyAddress(parts.join(', '));
        } else {
          setCustomerCompanyAddress(addr || '');
        }
      } else {
        setVatLookupError(t('delivery:vatNotFoundInAade'));
      }
    } catch (error: any) {
      console.error('VAT lookup error:', error);
      setVatLookupError(error.message || t('delivery:vatLookupFailed'));
    } finally {
      setIsLookingUpVat(false);
    }
  };

  // Submit order
  const submitOrder = async () => {
    // Μία αποστολή τη φορά: δεύτερο πάτημα (ή «Πληρωμή» ξανά) όσο τρέχει το POST δεν κάνει τίποτα.
    if (submitInFlightRef.current) return;

    if (basket.length === 0) {
      showToast('warning', 'Το καλάθι είναι άδειο');
      return;
    }

    if (!customer) {
      showToast('warning', t('delivery:selectCustomerFirst'));
      return;
    }

    if (!selectedAddress) {
      showToast('warning', t('delivery:selectDeliveryAddressFirst'));
      return;
    }

    // Τρόπος πληρωμής/παράδοσης: ΜΟΝΟ ρητή επιλογή του ταμία για ΑΥΤΗ την παραγγελία.
    if (!paymentMethod) {
      showToast('warning', t('delivery:choosePaymentMethod'));
      return;
    }
    // Σκιάζει σκόπιμα το state: από εδώ και κάτω μετρά ο τρόπος που ΙΣΧΥΕΙ (ρητός ή εννοούμενος).
    const deliveryMethod = effectiveDeliveryMethod;
    if (!deliveryMethod) {
      showToast('warning', t('delivery:chooseDeliveryMethod'));
      return;
    }

    // Validate invoice requirements
    if (fiscalDocumentType === 2 && !customerCompanyName) {
      showToast('warning', 'Κάνε πρώτα αναζήτηση ΑΦΜ για τα στοιχεία τιμολογίου');
      return;
    }

    // Check delivery zone for self-delivery
    if (deliveryMethod === 'self' && outOfZone && !showOutOfZoneWarning) {
      setShowOutOfZoneWarning(true);
      return;
    }

    submitInFlightRef.current = true;
    setIsSubmittingOrder(true);
    // Μια «Αναίρεση» που μένει ανοιχτή θα ξανάβαζε στο καλάθι είδη μιας παραγγελίας που στέλνεται.
    dropUndo();
    try {
      // If efood delivery method selected, check if Laas Mile can service this address
      if (deliveryMethod === 'efood') {
        if (!selectedAddress?.latitude || !selectedAddress?.longitude) {
          showToast('warning', t('delivery:addressNoCoordinates'));
          return;
        }

        // Call estimate-fee to check if efood can service this area
        const estimateResponse = await fetch(`${API_BASE_URL}/api/laas-mile/${store?.id}/estimate-fee`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
          },
          body: JSON.stringify({
            customer_address: selectedAddress?.street_address,
            customer_latitude: selectedAddress?.latitude,
            customer_longitude: selectedAddress?.longitude,
            total_amount: calculateTotal(),
            payment_type: paymentMethod === 'cash' ? 'cash' : 'paid' // card and iris both count as 'paid'
          })
        });

        if (!estimateResponse.ok) {
          await estimateResponse.json();
          setPopupMessage({
            title: t('delivery:laasMileUnavailableArea'),
            message: t('delivery:laasMileUnavailableMessage'),
            type: 'error'
          });
          return;
        }

        await estimateResponse.json();
      }

      // Training mode only valid for: self delivery + receipt (not invoice) + cash payment
      const isTraining = isTrainingOrder && trainingModeEnabled &&
        deliveryMethod === 'self' && fiscalDocumentType === 1 && paymentMethod === 'cash';

      const orderData = {
        orderType: 'delivery',
        paymentMethod,
        deliveryMethod,
        fiscalDocumentTypeId: fiscalDocumentType,
        isTraining,
        // Include VAT info for invoices (stored but fiscal doc generated at dispatch)
        customerVatNumber: fiscalDocumentType === 2 ? customerVatNumber : null,
        customerCompanyName: fiscalDocumentType === 2 ? customerCompanyName : null,
        customerCompanyActivity: fiscalDocumentType === 2 ? customerCompanyActivity : null,
        customerCompanyAddress: fiscalDocumentType === 2 ? customerCompanyAddress : null,
        items: basket.map(item => {
          // Σημείωση γραμμής από τις προσαρμογές· χωρίς «Size:» για το μοναδικό πωλήσιμο
          // μέγεθος (απόφαση 15, πάντα από την F1-S1 — utils/orderHelpers).
          // Το selectedSize πιο κάτω μένει αυτούσιο.
          const itemNotes = buildDeliveryItemNotes(item);

          return {
            product_id: item.product.id,
            product_name: item.product.name,
            quantity: item.quantity,
            price: item.price,
            notes: itemNotes,
            selectedSize: item.selectedSize ? {
              id: item.selectedSize.id,
              name: item.selectedSize.name,
              price_modifier: item.selectedSize.price_modifier
            } : null,
            removedIngredients: item.removedIngredients || [],
            // Ονόματα στη σειρά των ids: «Without: <ονόματα>» στη σημείωση, όχι «ID:x».
            removedIngredientNames: item.removedIngredientNames || [],
            addedExtras: item.addedExtras || [],
            selected_options: item.selected_options || [],
            specialInstructions: item.specialInstructions || '',
            discount: item.discount || 0,
            weight: item.weight || null,
            weight_unit: item.weightUnit || null,
            applied_promotion_id: item.applied_promotion_id || null,
            applied_promotion_name: item.applied_promotion_name || null,
            original_unit_price: item.original_unit_price ?? null,
            upsell_suggestion_id: item.upsell_suggestion_id || null
          };
        }),
        total: calculateTotal(),
        customer: {
          id: customer?.id,
          name: customer?.name,
          phone: customer?.phone,
          // Για το δελτίο του διανομέα (το μόνο του χαρτί): 2ο τηλέφωνο, περιοχή/πόλη/ΤΚ και οι
          // οδηγίες της ΔΙΕΥΘΥΝΣΗΣ. Οι σημειώσεις της παραγγελίας μένουν στο notes.
          phone_secondary: customer?.phone_secondary || undefined,
          address: selectedAddress?.street_address,
          floor: selectedAddress?.floor,
          doorbell: selectedAddress?.doorbell,
          area: selectedAddress?.area || undefined,
          city: selectedAddress?.city || undefined,
          postal_code: selectedAddress?.postal_code || undefined,
          delivery_notes: selectedAddress?.delivery_notes || undefined,
          notes: deliveryNotes,
          latitude: selectedAddress?.latitude,
          longitude: selectedAddress?.longitude
        },
        delivery_address_id: selectedAddress?.id,
        // Include coordinates for map display in dispatch
        latitude: selectedAddress?.latitude,
        longitude: selectedAddress?.longitude,
        createdBy: employee?.id || 1,
        storeId: store?.id,
        orderDiscount: discountType === 'percent' && orderDiscount > 0 ? orderDiscount : undefined,
        orderDiscountType: (discountType === 'fixed' && fixedDiscountAmount > 0) ? 'fixed' : (orderDiscount > 0 ? 'percent' : undefined),
        orderDiscountFixed: discountType === 'fixed' && fixedDiscountAmount > 0 ? fixedDiscountAmount : undefined,
        out_of_zone: outOfZone || false,
        delivery_fee: deliveryFee || 0
      };
      

      const response = await fetch(`${API_BASE_URL}/api/orders/${store?.id}/delivery`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        },
        body: JSON.stringify(orderData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // Check if it's an efood serviceability error
        if (errorData.code === 'ADDRESS_NOT_SERVICEABLE' || errorData.code === 'MISSING_COORDINATES' || errorData.code === 'LAAS_MILE_FAILED') {
          setPopupMessage({
            title: t('delivery:deliveryFailedTitle'),
            message: errorData.error,
            type: 'error'
          });
          // Don't clear basket - let user decide what to do
          return;
        }
        // Επανάληψη με ιστορικές τιμές (P1-Q9) κάτω από το σημερινό κατώτατο = έκπτωση, και ο
        // ταμίας χωρίς δικαίωμα έκπτωσης παίρνει 403 (utils/orderPricing). ΔΕΝ αλλάζουμε σιωπηλά
        // σε σημερινές τιμές (απόφαση χρημάτων του ιδιοκτήτη): εξήγηση, και το καλάθι μένει.
        if (response.status === 403 && errorData.error === 'discount_forbidden' && basket.some(item => item.keepHistoricalFee)) {
          setPopupMessage({
            title: t('delivery:reorderPriceFloorTitle'),
            message: t('delivery:reorderPriceFloorMessage'),
            type: 'error'
          });
          return;
        }
        // 4xx: το ελληνικό μήνυμα του server (όχι ο κωδικός «discount_forbidden»)· 5xx: όπως πριν.
        throw new Error((response.status < 500 ? (errorData.message || errorData.error) : errorData.error) || t('delivery:orderCreateFailed'));
      }

      const result = await response.json();
      
      // If efood delivery method, call Laas Mile to create the delivery
      if (deliveryMethod === 'efood' && result.order?.id) {
        try {
          const laasMileResponse = await fetch(`${API_BASE_URL}/api/laas-mile/${store?.id}/create/${result.order.id}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
            }
          });

          if (!laasMileResponse.ok) {
            const laasMileError = await laasMileResponse.json();
            console.error('❌ Laas Mile create failed:', laasMileError);
            setPopupMessage({
              title: t('delivery:laasMileIssueTitle'),
              message: t('delivery:laasMileIssueWithError', { error: laasMileError.error || t('delivery:unknown') }),
              type: 'warning'
            });
          } else {
            await laasMileResponse.json();
          }
        } catch (laasMileError) {
          console.error('❌ Laas Mile error:', laasMileError);
          setPopupMessage({
            title: t('delivery:laasMileIssueTitle'),
            message: t('delivery:laasMileIssueNoError'),
            type: 'warning'
          });
        }
      }
      
      // If wolt delivery method, call Wolt Drive to create the delivery
      if (deliveryMethod === 'wolt' && result.order?.id) {
        try {
          const woltDriveResponse = await fetch(`${API_BASE_URL}/api/wolt-drive/${store?.id}/create/${result.order.id}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
            },
            body: JSON.stringify({
              dropoff: {
                street: selectedAddress?.street_address,
                city: selectedAddress?.city || '',
                post_code: selectedAddress?.postal_code || '',
                lat: selectedAddress?.latitude,
                lon: selectedAddress?.longitude
              },
              recipient_name: customer?.name,
              recipient_phone: customer?.phone,
              comment: selectedAddress?.delivery_notes || deliveryNotes
            })
          });

          if (!woltDriveResponse.ok) {
            const woltDriveError = await woltDriveResponse.json();
            console.error('❌ Wolt Drive create failed:', woltDriveError);
            setPopupMessage({
              title: t('delivery:woltDriveIssueTitle'),
              message: t('delivery:woltDriveIssueWithError', { error: woltDriveError.error || t('delivery:unknown') }),
              type: 'warning'
            });
          } else {
            await woltDriveResponse.json();
          }
        } catch (woltDriveError) {
          console.error('❌ Wolt Drive error:', woltDriveError);
          setPopupMessage({
            title: t('delivery:woltDriveIssueTitle'),
            message: t('delivery:woltDriveIssueNoError'),
            type: 'warning'
          });
        }
      }
      
      // Reset everything - only after successful order creation
      setBasket([]);
      setOrderMode(false);
      setDeliveryNotes('');
      setFiscalDocumentType(1);
      setCustomerVatNumber('');
      setCustomerCompanyName('');
      setCustomerCompanyActivity('');
      setCustomerCompanyAddress('');
      setVatLookupError('');
      setOrderDiscount(0);
      setDiscountType('percent');
      setFixedDiscountAmount(0);
      setIsTrainingOrder(false);
      // Η επόμενη παραγγελία διαλέγει ξανά τρόπο πληρωμής και παράδοσης — ποτέ κληρονομιά.
      setPaymentMethod(null);
      setDeliveryMethod(null);
      quickPayRef.current = null;
      setQuickPayMethod(null);
      dropUndo();

      if (v2) {
        // Νέα εμφάνιση: «Παραγγελία #D1234 · εστάλη» και η οθόνη έτοιμη για την επόμενη κλήση.
        customerSeqRef.current += 1;
        setLastSentOrder({ shortCode: result.order?.shortCode ?? null });
        setCurrentView('categories');
        setCustomer(null);
        setPhoneNumber('');
        setSelectedAddress(null);
      } else {
        // Refresh customer to show new order
        await searchCustomer();
      }

    } catch (error: any) {
      console.error('Error submitting order:', error);
      setPopupMessage({
        title: t('delivery:errorTitle'),
        message: error.message || t('delivery:orderCreateFailed'),
        type: 'error'
      });
    } finally {
      submitInFlightRef.current = false;
      setIsSubmittingOrder(false);
      // Κλήση που ήρθε όσο έφευγε η παραγγελία: ανοίγει τώρα.
      const deferred = deferredIntentRef.current;
      deferredIntentRef.current = null;
      if (deferred) setTimeout(() => callIntentRef.current(deferred), 0);
    }
  };

  // ---- Νέα εμφάνιση: πληρωμή με ένα πάτημα («Μετρητά» / «Κάρτα» στο πάνελ) ----
  // Τρέχει ΜΕΤΑ τη ζωγραφιά που έβαλε τον τρόπο, ώστε το submitOrder — ίδιο με το «Αποστολή»
  // του παραθύρου — να διαβάσει τη νέα τιμή.
  useEffect(() => {
    if (!quickPayMethod || paymentMethod !== quickPayMethod) return;
    setQuickPayMethod(null);
    void submitOrder();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickPayMethod, paymentMethod]);

  const runQuickPay = (method: DeliveryPaymentMethod) => {
    setPaymentMethod(method);
    // Τρόπος παράδοσης που θέλει ρητή επιλογή, ή τιμολόγιο (στοιχεία ΑΦΜ): το παράθυρο
    // ολοκλήρωσης, με τον τρόπο πληρωμής που μόλις πατήθηκε ήδη επιλεγμένο.
    if (effectiveDeliveryMethod === null || fiscalDocumentType === 2) {
      setShowCheckoutModal(true);
      return;
    }
    setQuickPayMethod(method);
  };
  // Ίδια σειρά με την «Πληρωμή»: πρώτα η πρόταση πώλησης (startCheckout), μετά ο τρόπος.
  const startQuickPay = (method: DeliveryPaymentMethod) => {
    if (basket.length === 0 || !customer || !selectedAddress || submitInFlightRef.current) return;
    setShowBasketMenu(false);
    quickPayRef.current = method;
    startCheckout();
  };
  // Μετρητά: κράτημα 2″ = εκπαιδευτική παραγγελία, όπως το «Αποστολή» του παραθύρου
  // (μόνο δικός μας διανομέας + απόδειξη + μετρητά).
  const cashHoldStart = () => {
    if (basket.length === 0 || submitInFlightRef.current) return;
    if (trainingModeEnabled && effectiveDeliveryMethod === 'self' && fiscalDocumentType === 1) {
      holdTimerRef.current = setTimeout(() => { setIsTrainingOrder(true); }, 2000);
    }
  };
  const cashHoldEnd = (pay: boolean) => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    if (pay) startQuickPay('cash');
  };

  // Κοινά κομμάτια των δύο εμφανίσεων (κλασική / νέα) — ίδια λογική, άλλη μορφή.
  const deliveryGridPopover = showGridSettings && (
    <div className="absolute right-0 top-full mt-2 w-72 bg-surface-2 border border-line-strong rounded-xl shadow-2xl shadow-black/40 z-50 p-4">
      <h3 className="text-t1 font-bold text-sm mb-4 uppercase tracking-wide">{t('delivery:gridSettings')}</h3>
      
      {/* Category Columns */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <label className="text-t2 text-sm font-medium">{t('delivery:categoriesLabel')}</label>
          <span className="text-blue-600 dark:text-blue-400 font-bold text-sm">{t('delivery:columnsCount', { count: categoryColumns })}</span>
        </div>
        <div className="flex gap-1">
          {[2, 3, 4, 5, 6].map(n => (
            <button
              key={n}
              onClick={() => setCategoryColumns(n)}
              className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${
                categoryColumns === n 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-surface-3 text-t2 hover:bg-surface-4'
              }`}
            >
              {n}
            </button>
          ))}
        </div>
      </div>

      {/* Product Columns */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-2">
          <label className="text-t2 text-sm font-medium">{t('products:title')}</label>
          <span className="text-blue-600 dark:text-blue-400 font-bold text-sm">{t('delivery:columnsCount', { count: productColumns })}</span>
        </div>
        <div className="flex gap-1">
          {[2, 3, 4, 5, 6, 7, 8].map(n => (
            <button
              key={n}
              onClick={() => setProductColumns(n)}
              className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${
                productColumns === n 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-surface-3 text-t2 hover:bg-surface-4'
              }`}
            >
              {n}
            </button>
          ))}
        </div>
      </div>

      <div className="pt-3 border-t border-line-strong">
        <p className="text-t4 text-xs">{t('delivery:gridSettingsHint')}</p>
      </div>
    </div>
  );

  const promotionBanner = store?.id ? (
    <PromotionBanner
      storeId={store.id}
      items={basket.map(item => ({
        product_id: item.product.id,
        quantity: item.quantity,
        unit_price: item.original_unit_price ?? (item.price ?? item.totalPrice ?? 0)
      }))}
      appliedPromotionId={appliedPromotionId}
      onApply={(promo: EligiblePromotion) => {
        // Ίδιος υπολογισμός με πριν, στο ΤΡΕΧΟΝ καλάθι (utils/basketOps, όπως το Take Away): με το
        // αντίγραφο της ζωγραφιάς χανόταν ό,τι περίμενε ακόμη στην ουρά.
        setBasket(prev => {
          let working = prev.map(item => {
            if (item.applied_promotion_id && item.original_unit_price != null) {
              return {
                ...item,
                price: item.original_unit_price,
                totalPrice: item.original_unit_price,
                discount: 0,
                applied_promotion_id: null,
                applied_promotion_name: null,
                original_unit_price: null
              };
            }
            return item;
          });
          const linesByProduct = new Map<number, typeof promo.lines[number]>();
          for (const ln of promo.lines) linesByProduct.set(ln.product_id, ln);
          working = working.map(item => {
            const line = linesByProduct.get(item.product.id);
            if (!line) return item;
            const original = item.price ?? item.totalPrice ?? 0;
            const newPrice = line.new_unit_price;
            const discountPct = original > 0 ? Math.round(((original - newPrice) / original) * 1000) / 10 : 0;
            return {
              ...item,
              price: newPrice,
              totalPrice: newPrice,
              discount: discountPct,
              applied_promotion_id: promo.promotion_id,
              applied_promotion_name: promo.name,
              original_unit_price: original
            };
          });
          return working;
        });
        setAppliedPromotionId(promo.promotion_id);
      }}
      onRemove={() => {
        // Και εδώ στο τρέχον καλάθι: το PromotionBanner το καλεί και μόνο του, μετά από αίτημα δικτύου.
        setBasket(prev => prev.map(item => {
          if (!item.applied_promotion_id || item.original_unit_price == null) return item;
          return {
            ...item,
            price: item.original_unit_price,
            totalPrice: item.original_unit_price,
            discount: 0,
            applied_promotion_id: null,
            applied_promotion_name: null,
            original_unit_price: null
          };
        }));
        setAppliedPromotionId(null);
      }}
    />
  ) : null;

  const renderDeliveryRows = (rowLook: 'classic' | 'v2') => {
    // Ίδια πηγή ποσού με την αυτόματη γραμμή (feeUnitGross)· χωρίς ποσό → καμία σημείωση.
    const feeProduct = products.find(p => p.fee_category === 16);
    const feeUnit = autoFeeNoteUnit(feeProduct);
    // ΜΟΝΟ η γραμμή που ενημερώνει ο αυτοματισμός είναι «αυτόματη» (utils/autoFee). Ως 19/10/2026
    // ήταν κάθε γραμμή κωδ. 16 εκτός επανάληψης: ένα χειροκίνητο τέλος δεν έφευγε με × ούτε
    // ενωνόταν σε δεύτερο πάτημα.
    const managedFeeId = autoManagedFeeLineId({ basket, feeProduct, feeOverride });
    return basket.map((item) => {
      // Catalog/size prices are net of the plastic fee; the
      // fee rides as its own visible basket line (and a
      // rec_type=2 line on the receipt). Cashier can waive it.
      // Trigger products carry a small note so the cashier
      // sees they bear a fee (amount is on the fee line).
      // Επανάληψη (keepHistoricalFee, F1-W6-2/F1-W6-3): η ιστορική γραμμή τέλους
      // ΔΕΝ είναι του αυτοματισμού (± αλλάζει τη δική της ποσότητα), και τα είδη
      // της δεν έχουν σημείωση αφού δεν γεννούν τέλος.
      const managedFee = item.id === managedFeeId;
      // Η δική μας γραμμή: × / ± = επιλογή τέλους του ταμία (feeOverride), όπως πάντα.
      const ownAutoFee = managedFee && isOwnAutoFeeLine(item);
      // Γραμμή που υιοθέτησε ο αυτοματισμός (χειροκίνητη, ή η ενωμένη της επανάληψης): η αλλαγή
      // γίνεται στη γραμμή και το feeOverride 0 «παγώνει» τον κανόνα — αλλιώς ο επόμενος γύρος
      // θα την ξαναγύριζε. Η «Αναίρεση» επαναφέρει και το feeOverride (offerUndo).
      const adoptedFee = managedFee && !ownAutoFee;
      const perUnitFee = item.keepHistoricalFee ? 0 : (item.product.auto_plastic_fee_count || 0) * feeUnit;
      const infoNote = item.product.fee_category !== 16 && perUnitFee > 0
        ? `+ τέλος ${formatMoney(perUnitFee)} / τεμ.`
        : '';
      return (
        <BasketItemRow
          key={item.id}
          item={item}
          onRemove={ownAutoFee
            ? () => setFeeOverride(0)
            : adoptedFee ? id => { setFeeOverride(0); removeFromBasket(id); } : removeFromBasket}
          onQuantityChange={ownAutoFee
            ? (_id, delta) => setFeeOverride(Math.max(0, item.quantity + delta))
            : adoptedFee ? (id, delta) => { setFeeOverride(0); updateQuantity(id, delta); } : updateQuantity}
          onClick={openEditModal}
          getUnitPrice={p => productDisplayPrice(p, 'delivery')}
          priceField="price"
          infoNote={infoNote}
          look={rowLook}
        />
      );
    });
  };

  const basketGrossSubtotal = basket.reduce((total, item) => {
    const price = typeof item.price === 'number' ? item.price : parseFloat(String(item.price)) || 0;
    return total + (price * item.quantity);
  }, 0);
  const deliveryLineCount = basket.filter(b => b.product.fee_category !== 16).length;
  const deliveryUnitCount = basket.filter(b => b.product.fee_category !== 16).reduce((sum, b) => sum + (b.weight ? 1 : b.quantity), 0);
  const clearDeliveryBasket = () => { setBasket([]); setOrderDiscount(0); setDiscountType('percent'); setFixedDiscountAmount(0); };
  // «Άδειασμα» — με «Αναίρεση» 4″ (ως 12/10/2026 έσβηνε τα πάντα χωρίς ερώτηση ή επιστροφή).
  const clearBasketWithUndo = () => {
    if (submitInFlightRef.current) return;
    if (basket.length > 0) offerUndo(t('delivery:undo.cleared'));
    clearDeliveryBasket();
  };
  // ✕ «Ακύρωση παραγγελίας» (και οι δύο εμφανίσεις): πίσω στον πελάτη με άδειο καλάθι — με «Αναίρεση».
  const cancelOrderWithUndo = () => {
    if (submitInFlightRef.current) return;
    if (basket.length > 0) offerUndo(t('delivery:undo.cleared'));
    setOrderMode(false);
    clearDeliveryBasket();
    setCurrentView('categories');
  };
  // Η λωρίδα «Αναίρεση»: στο πάνελ της νέας εμφάνισης όσο είναι ανοιχτή η παραγγελία, αλλιώς
  // στο κάτω μέρος της οθόνης (κλασική, καρτέλα πελάτη, αναζήτηση).
  const renderUndoBar = (placement: 'panel' | 'floating') => undoState && (
    <div
      role="status"
      data-testid="delivery-undo"
      className={`${placement === 'panel'
        ? 'mx-5 mb-3'
        : 'fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] w-[min(30rem,calc(100vw-2rem))] shadow-xl shadow-black/20'
      } flex items-center justify-between gap-3 rounded-xl bg-t1 text-surface-2 pl-4 pr-1.5 min-h-[3.5rem] text-sm`}
    >
      <span className="truncate">{undoState.label}</span>
      <button
        type="button"
        onClick={undoLastRemoval}
        disabled={isSubmittingOrder}
        className="shrink-0 inline-flex items-center gap-1.5 h-11 px-3 rounded-lg font-semibold hover:bg-white/10 dark:hover:bg-black/10 disabled:opacity-50"
      >
        <Undo2 className="h-4 w-4" />
        {t('delivery:undo.action')}
      </button>
    </div>
  );
  // Μη διαθέσιμα όσο φεύγει η παραγγελία: τα είδη της δεν αλλάζουν στη μέση της αποστολής.
  const basketBusyClass = isSubmittingOrder ? 'pointer-events-none opacity-60' : '';
  // «Αποστολή» του παραθύρου: ρητός τρόπος πληρωμής ΚΑΙ (όπου δεν εννοείται) τρόπος παράδοσης.
  const checkoutSendable = basket.length > 0 && !!customer && !!selectedAddress
    && paymentMethod !== null && effectiveDeliveryMethod !== null
    && !(fiscalDocumentType === 2 && !customerCompanyName)
    && !isSubmittingOrder;
  const v2OrderTypeLabel = (type?: string | null) =>
    type === 'takeaway' ? t('common:orderType.takeaway')
      : type === 'pickup' ? t('common:orderType.pickup')
      : (type === 'dine_in' || type === 'dinein') ? t('common:orderType.dineIn')
      : (type || t('delivery:v2.otherOrderType'));

  return (
    <div className="cashier-workspace cashier-delivery min-h-screen bg-surface-1">
      <Navigation currentPage="delivery" onPageChange={handlePageChange} />
      
      {/* Out of Zone Warning Modal */}
      {showOutOfZoneWarning && (
        <div className="cashier-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="cashier-dialog bg-surface-1 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden border border-amber-500/30">
            <div className="p-4 bg-amber-500">
              <h3 className="text-xl font-bold light:text-white flex items-center gap-2 dark:text-t1">
                <AlertTriangle className="h-6 w-6" />
                {t('delivery:outOfServiceZone')}
              </h3>
            </div>
            <div className="p-6">
              <p className="text-t2 text-base">
                {t('delivery:outOfZoneAddressMsg1')} <span className="font-semibold text-t1">{selectedAddress?.street_address}</span> {t('delivery:outOfZoneAddressMsg2')}
              </p>
              <p className="text-t3 text-sm mt-2">
                {t('delivery:continueAnyway')}
              </p>
            </div>
            <div className="px-6 pb-6 flex gap-3">
              <button
                onClick={() => setShowOutOfZoneWarning(false)}
                className="flex-1 py-3 text-base font-semibold bg-surface-2 hover:bg-surface-3 text-t2 rounded-xl transition-colors"
              >
                {t('common:cancel')}
              </button>
              <button
                onClick={() => {
                  setShowOutOfZoneWarning(false);
                  submitOrder();
                }}
                className="flex-1 py-3 text-base font-semibold bg-amber-500 hover:bg-amber-600 text-white rounded-xl transition-colors"
              >
                {t('delivery:continue')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Popup Message Modal */}
      {popupMessage && (
        <div className="cashier-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="cashier-dialog bg-surface-2 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className={`p-4 ${
              popupMessage.type === 'error' ? 'bg-red-500' : 
              popupMessage.type === 'warning' ? 'bg-yellow-500' : 'bg-green-500'
            }`}>
              <h3 className="text-xl font-bold light:text-white dark:text-t1">{popupMessage.title}</h3>
            </div>
            <div className="p-6">
              <p className="text-t2 text-lg">{popupMessage.message}</p>
            </div>
            <div className="px-6 pb-6">
              <Button
                onClick={() => setPopupMessage(null)}
                className={`w-full py-3 text-lg font-semibold ${
                  popupMessage.type === 'error' ? 'bg-red-500 hover:bg-red-600' : 
                  popupMessage.type === 'warning' ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-green-500 hover:bg-green-600'
                } text-white`}
              >
                OK
              </Button>
            </div>
          </div>
        </div>
      )}
      
      <div className="max-w-7xl mx-auto p-6">
        {/* Phone Lookup Screen - Touch Friendly */}
        {!customer && !showNewCustomerForm && (
          <div className="flex flex-col items-center justify-center gap-4 min-h-[60vh]">
            {/* Νέα εμφάνιση: η παραγγελία που μόλις έφυγε — η οθόνη είναι ήδη έτοιμη για την επόμενη κλήση */}
            {v2 && lastSentOrder && (
              <div
                role="status"
                data-testid="delivery-last-sent"
                className="w-full max-w-lg flex items-center gap-3 rounded-xl border border-line bg-surface-2 pl-4 pr-1.5 min-h-[3.5rem]"
              >
                <span className="w-8 h-8 shrink-0 rounded-full bg-brand-soft text-brand flex items-center justify-center">
                  <Check className="h-5 w-5" />
                </span>
                <span className="flex-1 min-w-0 truncate text-base font-semibold text-t1">
                  {lastSentOrder.shortCode
                    ? t('delivery:v2.sent', { code: lastSentOrder.shortCode })
                    : t('delivery:v2.sentNoCode')}
                </span>
                <button
                  type="button"
                  onClick={() => setLastSentOrder(null)}
                  aria-label={t('common:close')}
                  className="w-11 h-11 shrink-0 rounded-lg text-t3 hover:text-t1 hover:bg-surface-3 flex items-center justify-center"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            )}
            <Card className="cashier-customer-search w-full max-w-lg p-6 bg-surface-2 dark:bg-surface-1 border-line">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-blue-100 dark:bg-blue-900/40 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Search className="h-10 w-10 text-blue-600 dark:text-blue-400" />
                </div>
                <h2 className="text-2xl font-bold text-t1">{t('delivery:searchTitle')}</h2>
                <p className="text-t3 mt-2">{t('delivery:searchSubtitle')}</p>
              </div>
              
              <div className="space-y-5">
                <div className="relative">
                  <Label className="text-sm font-medium text-t2 mb-2 block">Αναζήτηση</Label>
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-6 w-6 text-t3" />
                    <input
                      type="text"
                      placeholder="Τηλέφωνο, όνομα, διεύθυνση, περιοχή..."
                      value={phoneNumber}
                      onChange={(e) => handleSearchInputChange(e.target.value)}
                      onFocus={() => setDeliveryKb({ field: 'search', mode: 'text', label: t('delivery:searchCustomer') })}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          setShowAutocompleteSuggestions(false);
                          searchCustomer();
                        }
                      }}
                      onBlur={() => setTimeout(() => setShowAutocompleteSuggestions(false), 200)}
                      className="w-full pl-14 pr-4 py-4 text-xl font-medium bg-surface-3 border-2 border-line-strong rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder-t3 text-t1"
                      autoFocus
                    />
                  </div>
                  
                  {/* Autocomplete suggestions dropdown */}
                  {showAutocompleteSuggestions && autocompleteSuggestions.length > 0 && (
                    <div className="absolute z-10 w-full mt-2 bg-surface-2 border-2 border-line-strong rounded-xl shadow-lg max-h-80 overflow-y-auto">
                      {autocompleteSuggestions.map((suggestion) => (
                        <button
                          key={suggestion.id}
                          className="w-full px-5 py-4 text-left hover:bg-surface-3 active:bg-surface-4 border-b border-line-strong last:border-b-0 transition-colors"
                          onMouseDown={(e) => e.preventDefault()} // Prevent blur before click
                          onClick={() => selectAutocompleteSuggestion(suggestion)}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="font-bold text-lg text-t1">{suggestion.name || t('delivery:unknownCustomer')}</div>
                              <div className="text-sm text-blue-600 dark:text-blue-400 font-medium">{suggestion.phone}</div>
                              {suggestion.addresses && suggestion.addresses.length > 0 && (
                                <div className="text-sm text-t3 mt-1">
                                  📍 {suggestion.addresses[0].street_address}
                                  {suggestion.addresses[0].area && `, ${suggestion.addresses[0].area}`}
                                </div>
                              )}
                            </div>
                            <div className="text-right ml-3">
                              <span className="text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-full">
                                {t('delivery:addressCountShort', { count: Number(suggestion.address_count || suggestion.addresses?.length || 0) })}
                              </span>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                
                <button
                  onClick={() => searchCustomer()}
                  disabled={isSearching || !phoneNumber}
                  className={`w-full flex items-center justify-center gap-3 py-4 rounded-xl text-lg font-bold transition-all active:scale-95 ${
                    isSearching || !phoneNumber
                      ? 'bg-surface-3 text-t3 dark:text-t4 cursor-not-allowed'
                      : 'bg-blue-600 text-white shadow-lg hover:bg-blue-700 active:bg-blue-800'
                  }`}
                >
                  {isSearching ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      {t('delivery:searching')}
                    </>
                  ) : (
                    <>
                      <Search className="h-6 w-6" />
                      {t('delivery:searchCustomer')}
                    </>
                  )}
                </button>
                
                {/* Search Results - Multiple customers found */}
                {showSearchResults && searchResults.length > 0 && (
                  <div className="mt-4 border-2 border-line-strong rounded-xl overflow-hidden">
                    <div className="bg-surface-3 px-4 py-2 border-b border-line-strong">
                      <span className="font-medium text-t2">{t('delivery:customersFound', { count: searchResults.length })}</span>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {searchResults.map((result) => (
                        <button
                          key={result.id}
                          className="w-full px-4 py-3 text-left hover:bg-surface-3 active:bg-surface-4 border-b border-line-strong last:border-b-0 transition-colors"
                          onClick={() => selectSearchResult(result)}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="font-bold text-t1">{result.name || t('delivery:unknownCustomer')}</div>
                              <div className="text-sm text-t2">{result.phone}</div>
                              {result.addresses && result.addresses.length > 0 && (
                                <div className="text-xs text-t3 mt-1">
                                  {result.addresses[0].street_address}, {result.addresses[0].area}
                                </div>
                              )}
                            </div>
                            <span className="text-xs bg-surface-4 text-t2 px-2 py-1 rounded">
                              {t('delivery:addressesCount', { count: Number(result.address_count) || 0 })}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </div>
        )}

        {/* New Customer Form with Map */}
        {showNewCustomerForm && (
          <div className="bg-surface-0 rounded-2xl border border-line/60 shadow-2xl shadow-black/15 dark:shadow-black/50 overflow-hidden">
            {/* Header */}
            <div className="px-5 pt-5 pb-4 border-b border-line/60">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-blue-600/20 flex items-center justify-center">
                    <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-t1">{t('delivery:newCustomer')}</h2>
                    <p className="text-xs text-t3 dark:text-t4 mt-0.5">{phoneNumber}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    newCustomerAutocompleteRef.current = null;
                    setShowNewCustomerForm(false);
                    setPhoneNumber('');
                  }}
                  className="w-9 h-9 rounded-xl bg-surface-2 hover:bg-surface-3 flex items-center justify-center text-t2 transition-all active:scale-95 touch-manipulation shrink-0"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="max-h-[calc(90vh-140px)] overflow-y-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                {/* Form Section */}
                <div className="p-5 space-y-5 lg:border-r border-line/60">
                  {/* Customer Name */}
                  <div>
                    <label className="block text-[11px] uppercase tracking-wider text-t3 dark:text-t4 font-semibold mb-1.5">
                      Όνομα πελάτη <span className="text-red-600 dark:text-red-400">*</span>
                    </label>
                    <input
                      value={newCustomerData.name}
                      onChange={(e) => {
                        const name = e.target.value;
                        setNewCustomerData({
                          ...newCustomerData,
                          name: name,
                          address: {
                            ...newCustomerData.address,
                            doorbell: newCustomerData.address.doorbell === '' || 
                                      newCustomerData.address.doorbell === newCustomerData.name 
                                      ? name : newCustomerData.address.doorbell
                          }
                        });
                      }}
                      placeholder="Ονοματεπώνυμο"
                      autoFocus
                      onFocus={() => setDeliveryKb({ field: 'name', mode: 'text', label: 'Όνομα πελάτη' })}
                      className="w-full px-3.5 py-2.5 bg-surface-2 dark:bg-surface-1 border border-line dark:border-line/60 rounded-xl text-sm text-t1 placeholder-t4 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/40 transition-all"
                    />
                  </div>

                  {/* Section divider: Address */}
                  <div className="flex items-center gap-2 pt-1">
                    <MapPin className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                    <span className="text-[11px] uppercase tracking-wider text-blue-600 dark:text-blue-400 font-semibold">{t('delivery:deliveryAddress')}</span>
                    <div className="flex-1 h-px bg-line dark:bg-surface-1"></div>
                  </div>

                  {/* Street Address with search */}
                  <div>
                    <label className="block text-[11px] uppercase tracking-wider text-t3 dark:text-t4 font-semibold mb-1.5">
                      Διεύθυνση <span className="text-red-600 dark:text-red-400">*</span>
                    </label>
                    <div className="flex gap-2">
                      <input
                        ref={newCustomerStreetRef}
                        value={newCustomerData.address.street_address}
                        onChange={(e) => setNewCustomerData({
                          ...newCustomerData,
                          address: {...newCustomerData.address, street_address: e.target.value}
                        })}
                        placeholder="Πληκτρολογήστε διεύθυνση..."
                        autoComplete="one-time-code"
                        onFocus={() => setDeliveryKb({ field: 'street', mode: 'text', label: 'Διεύθυνση' })}
                        className="flex-1 px-3.5 py-2.5 bg-surface-2 dark:bg-surface-1 border border-line dark:border-line/60 rounded-xl text-sm text-t1 placeholder-t4 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/40 transition-all"
                      />
                      <button
                        onClick={geocodeAddress}
                        title="Αναζήτηση στον χάρτη"
                        className="px-3 py-2.5 rounded-xl bg-blue-600/15 hover:bg-blue-600/25 text-blue-600 dark:text-blue-400 border border-blue-500/20 transition-all touch-manipulation active:scale-95"
                      >
                        <Search className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Area + City */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] uppercase tracking-wider text-t3 dark:text-t4 font-semibold mb-1.5">Περιοχή</label>
                      <input
                        value={newCustomerData.address.area}
                        onChange={(e) => setNewCustomerData({
                          ...newCustomerData,
                          address: {...newCustomerData.address, area: e.target.value}
                        })}
                        placeholder="Γειτονιά"
                        autoComplete="one-time-code"
                        onFocus={() => setDeliveryKb({ field: 'area', mode: 'text', label: 'Περιοχή' })}
                        className="w-full px-3.5 py-2.5 bg-surface-2 dark:bg-surface-1 border border-line dark:border-line/60 rounded-xl text-sm text-t1 placeholder-t4 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/40 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] uppercase tracking-wider text-t3 dark:text-t4 font-semibold mb-1.5">Πόλη</label>
                      <input
                        value={newCustomerData.address.city}
                        onChange={(e) => setNewCustomerData({
                          ...newCustomerData,
                          address: {...newCustomerData.address, city: e.target.value}
                        })}
                        placeholder="Πόλη"
                        autoComplete="one-time-code"
                        onFocus={() => setDeliveryKb({ field: 'city', mode: 'text', label: 'Πόλη' })}
                        className="w-full px-3.5 py-2.5 bg-surface-2 dark:bg-surface-1 border border-line dark:border-line/60 rounded-xl text-sm text-t1 placeholder-t4 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/40 transition-all"
                      />
                    </div>
                  </div>

                  {/* Floor + Doorbell + Τ.Κ. */}
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-[11px] uppercase tracking-wider text-t3 dark:text-t4 font-semibold mb-1.5">Όροφος</label>
                      <input
                        value={newCustomerData.address.floor}
                        onChange={(e) => setNewCustomerData({
                          ...newCustomerData,
                          address: {...newCustomerData.address, floor: e.target.value}
                        })}
                        placeholder="π.χ. 3ος"
                        autoComplete="one-time-code"
                        onFocus={() => setDeliveryKb({ field: 'floor', mode: 'text', label: 'Όροφος' })}
                        className="w-full px-3.5 py-2.5 bg-surface-2 dark:bg-surface-1 border border-line dark:border-line/60 rounded-xl text-sm text-t1 placeholder-t4 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/40 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] uppercase tracking-wider text-t3 dark:text-t4 font-semibold mb-1.5">
                        Κουδούνι <span className="text-red-600 dark:text-red-400">*</span>
                      </label>
                      <input
                        value={newCustomerData.address.doorbell}
                        onChange={(e) => setNewCustomerData({
                          ...newCustomerData,
                          address: {...newCustomerData.address, doorbell: e.target.value}
                        })}
                        placeholder="Όνομα στο κουδούνι"
                        autoComplete="one-time-code"
                        onFocus={() => setDeliveryKb({ field: 'doorbell', mode: 'text', label: 'Κουδούνι' })}
                        className="w-full px-3.5 py-2.5 bg-surface-2 dark:bg-surface-1 border border-line dark:border-line/60 rounded-xl text-sm text-t1 placeholder-t4 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/40 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] uppercase tracking-wider text-t3 dark:text-t4 font-semibold mb-1.5">{t('delivery:postalCodeShort')}</label>
                      <input
                        value={newCustomerData.address.postal_code}
                        onChange={(e) => setNewCustomerData({
                          ...newCustomerData,
                          address: {...newCustomerData.address, postal_code: e.target.value}
                        })}
                        placeholder="12345"
                        autoComplete="one-time-code"
                        onFocus={() => setDeliveryKb({ field: 'postal_code', mode: 'numeric', label: 'Τ.Κ.', maxLength: 5 })}
                        className="w-full px-3.5 py-2.5 bg-surface-2 dark:bg-surface-1 border border-line dark:border-line/60 rounded-xl text-sm text-t1 placeholder-t4 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/40 transition-all"
                      />
                    </div>
                  </div>

                  {/* Coordinates badge */}
                  {formattedAddress && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                      <div className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center">
                        <Check className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                      </div>
                      <div>
                        <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">{formattedAddress}</span>
                        {mapCoordinates && (
                          <p className="text-[10px] text-emerald-500/70 mt-0.5">
                            {mapCoordinates.lat.toFixed(6)}, {mapCoordinates.lng.toFixed(6)}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Section divider: Extra Info */}
                  <div className="flex items-center gap-2 pt-1">
                    <Phone className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                    <span className="text-[11px] uppercase tracking-wider text-emerald-600 dark:text-emerald-400 font-semibold">{t('delivery:additionalDetails')}</span>
                    <div className="flex-1 h-px bg-line dark:bg-surface-1"></div>
                  </div>

                  {/* Secondary phone + email intentionally NOT collected at creation —
                      they can be added later from the customer card edit. Keeping the
                      create form to the fields the cashier actually needs mid-call. */}

                  {/* Customer Comments */}
                  <div>
                    <label className="block text-[11px] uppercase tracking-wider text-t3 dark:text-t4 font-semibold mb-1.5">{t('delivery:customerCommentsLabel')}</label>
                    <textarea
                      value={newCustomerData.customer_comments}
                      onChange={(e) => setNewCustomerData({...newCustomerData, customer_comments: e.target.value})}
                      onFocus={() => setDeliveryKb({ field: 'comments', mode: 'text', label: t('delivery:customerCommentsLabel') })}
                      placeholder="Αλλεργίες, προτιμήσεις, οδηγίες..."
                      className="w-full px-3.5 py-2.5 bg-surface-2 dark:bg-surface-1 border border-line dark:border-line/60 rounded-xl text-sm text-t1 placeholder-t4 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/40 transition-all resize-none"
                      rows={2}
                    />
                  </div>
                </div>

                {/* Map Section */}
                <div className="p-5 space-y-3 bg-surface-0/50">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] uppercase tracking-wider text-t3 dark:text-t4 font-semibold">{t('delivery:deliveryLocationLabel')}</span>
                    <span className="text-[10px] text-t3 dark:text-t4 font-medium">Πατήστε στον χάρτη ή σύρετε τον δείκτη</span>
                  </div>
                  <div 
                    ref={mapContainerRef}
                    className="w-full h-[320px] lg:h-[calc(100%-60px)] rounded-xl border border-line/60 overflow-hidden shadow-inner"
                    style={{ minHeight: '400px' }}
                  />
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="px-5 py-4 border-t border-line/60 bg-surface-0/80 flex gap-2.5">
              <button
                onClick={() => {
                  newCustomerAutocompleteRef.current = null;
                  setShowNewCustomerForm(false);
                  setPhoneNumber('');
                }}
                className="flex-1 px-4 py-3 rounded-xl bg-surface-2 dark:bg-surface-1 hover:bg-surface-3 dark:hover:bg-surface-2 border border-line dark:border-line/60 text-sm font-semibold text-t2 hover:text-t1 transition-all touch-manipulation active:scale-[0.97]"
              >
                Άκυρο
              </button>
              <button
                onClick={createCustomer}
                disabled={!newCustomerData.address.street_address || !newCustomerData.address.doorbell}
                className={`flex-1 px-4 py-3 rounded-xl text-sm font-semibold transition-all touch-manipulation active:scale-[0.97] ${
                  !newCustomerData.address.street_address || !newCustomerData.address.doorbell
                    ? 'bg-surface-3 dark:bg-surface-1 text-t3 dark:text-t4 cursor-not-allowed border border-line/60'
                    : 'bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-600/20'
                }`}
              >
                <User className="w-4 h-4 inline mr-1.5" />
                Δημιουργία πελάτη
              </button>
            </div>
          </div>
        )}

        {/* Customer Profile - Redesigned */}
        {customer && !orderMode && (
          <div className="cashier-customer-profile space-y-4">
            {/* Header Card - Unified customer info + KPIs */}
            <div className="bg-surface-0/80 dark:bg-surface-1/80 backdrop-blur-sm rounded-2xl border border-line dark:border-line/60 overflow-hidden">
              {/* Top: Customer identity + New Search */}
              <div className="p-5 pb-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-4 min-w-0">
                    {/* Avatar */}
                    <div className="cashier-avatar w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-600/20 shrink-0">
                      <span className="light:text-white font-bold text-xl dark:text-t1">
                        {customer.name?.charAt(0)?.toUpperCase() || '?'}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h1 className="text-xl font-bold text-t1 truncate">{customer.name}</h1>
                        {customer.is_vip && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-500/20 text-amber-800 dark:text-amber-300 border border-amber-500/30">
                            <Star className="h-3 w-3" />
                            VIP
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-sm text-t3">
                        <span className="flex items-center gap-1.5">
                          <Phone className="h-3.5 w-3.5" />
                          {customer.phone}
                        </span>
                        {customer.phone_secondary && (
                          <span className="text-t4">• {customer.phone_secondary}</span>
                        )}
                      </div>
                      {customer.email && (
                        <p className="text-xs text-t3 dark:text-t4 mt-0.5">{customer.email}</p>
                      )}
                    </div>
                  </div>
                  <button
                    // Νέα αναζήτηση = άλλος πελάτης: το καλάθι αδειάζει (με «Αναίρεση»), μαζί
                    // με τρόπο πληρωμής/παράδοσης και στοιχεία τιμολογίου.
                    onClick={leaveCustomer}
                    data-testid="delivery-new-search"
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-surface-2 dark:bg-surface-2/60 hover:bg-surface-3 active:bg-surface-4 text-t2 hover:text-t1 transition-all text-sm font-medium shrink-0 touch-manipulation active:scale-95"
                  >
                    <Search className="h-4 w-4" />
                    {t('delivery:newSearch')}
                  </button>
                </div>

                {/* Comments/Notes */}
                {(customer.customer_comments || customer.hidden_comments) && (
                  <div className="flex gap-2 mt-3 flex-wrap">
                    {customer.customer_comments && (
                      <div className="flex-1 min-w-[200px] px-3 py-2 bg-blue-500/10 rounded-xl text-sm text-blue-700 dark:text-blue-300 border border-blue-500/20">
                        <span className="font-semibold text-blue-600 dark:text-blue-400">{t('delivery:notesLabel')}</span> {customer.customer_comments}
                      </div>
                    )}
                    {customer.hidden_comments && (
                      <div className="flex-1 min-w-[200px] px-3 py-2 bg-red-500/10 rounded-xl text-sm text-red-700 dark:text-red-300 border border-red-500/20">
                        <span className="font-semibold text-red-600 dark:text-red-400">⚠ {t('delivery:internalLabel')}</span> {customer.hidden_comments}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* KPI Strip */}
              <div className="grid grid-cols-4 border-t border-line/60">
                <div className="px-4 py-3 text-center border-r border-line/40">
                  <p className="text-[11px] uppercase tracking-wider text-t3 dark:text-t4 font-medium">{t('delivery:orders')}</p>
                  <p className="text-lg font-bold text-t1 mt-0.5">{customer.total_orders || 0}</p>
                </div>
                <div className="px-4 py-3 text-center border-r border-line/40">
                  <p className="text-[11px] uppercase tracking-wider text-t3 dark:text-t4 font-medium">{t('delivery:totalSpent')}</p>
                  <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400 mt-0.5">{formatCurrency(customer.total_spent)}</p>
                </div>
                <div className="px-4 py-3 text-center border-r border-line/40">
                  <p className="text-[11px] uppercase tracking-wider text-t3 dark:text-t4 font-medium">{t('delivery:averageOrder')}</p>
                  <p className="text-lg font-bold text-t1 mt-0.5">{formatCurrency(customer.average_order_value)}</p>
                </div>
                <div className="px-4 py-3 text-center">
                  <p className="text-[11px] uppercase tracking-wider text-t3 dark:text-t4 font-medium">{t('delivery:loyaltyPoints')}</p>
                  <p className="text-lg font-bold text-amber-700 dark:text-amber-400 mt-0.5">{customer.loyalty_points || 0}</p>
                </div>
              </div>
            </div>

            {/* Two Column Layout: Addresses + Recent Orders */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Addresses */}
              <div className="bg-surface-0/80 dark:bg-surface-1/80 backdrop-blur-sm rounded-2xl border border-line dark:border-line/60 overflow-hidden">
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-line/60">
                  <h2 className="text-sm font-bold text-t1 uppercase tracking-wider flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    {t('delivery:deliveryAddresses')}
                  </h2>
                  <button
                    onClick={() => setShowAddressForm(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600/20 hover:bg-blue-600/30 text-blue-600 dark:text-blue-400 text-xs font-bold transition-colors touch-manipulation active:scale-95"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    {t('common:add')}
                  </button>
                </div>
                
                <div className="p-3">
                  {(!customer.addresses || customer.addresses.length === 0) ? (
                    <div className="text-center py-8 text-t4">
                      <MapPin className="h-10 w-10 mx-auto mb-2 text-t4" />
                      <p className="text-sm">{t('delivery:noSavedAddresses')}</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {customer.addresses.map((address) => {
                        const isSelected = selectedAddress?.id === address.id;
                        return (
                          <div
                            key={address.id}
                            data-selected={isSelected}
                            className={`cashier-address relative p-3.5 rounded-xl cursor-pointer transition-all touch-manipulation active:scale-[0.98] ${
                              isSelected
                                ? 'bg-blue-600/15 border-2 border-blue-500/60 shadow-lg shadow-blue-500/10'
                                : 'bg-surface-2 dark:bg-surface-2/40 border-2 border-transparent hover:border-line-strong hover:bg-surface-3 dark:hover:bg-surface-2/60'
                            }`}
                            onClick={() => setSelectedAddress(address)}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-center gap-2.5 min-w-0">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                                  isSelected ? 'bg-blue-600/30 text-blue-600 dark:text-blue-400' : 'bg-surface-3 dark:bg-surface-3/50 text-t3'
                                }`}>
                                  {address.address_type === 'home' ? <Home className="h-4 w-4" /> : 
                                   address.address_type === 'work' ? <Briefcase className="h-4 w-4" /> : 
                                   <MapPin className="h-4 w-4" />}
                                </div>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className={`font-semibold text-sm ${isSelected ? 'text-blue-700 dark:text-blue-300' : 'text-t1'}`}>
                                      {address.label || addressTypeLabel(address.address_type) || t('common:address')}
                                    </span>
                                    {address.is_default && (
                                      <span className="text-[10px] font-bold uppercase bg-blue-500/20 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded">{t('delivery:defaultAddress')}</span>
                                    )}
                                  </div>
                                  <p className="text-sm text-t2 truncate">{address.street_address}</p>
                                  {address.area && <p className="text-xs text-t4">{address.area}</p>}
                                </div>
                              </div>
                              <button
                                onClick={(e) => openEditAddress(address, e)}
                                className="p-1.5 rounded-lg hover:bg-surface-3 dark:hover:bg-surface-3/60 text-t4 hover:text-t1 transition-colors shrink-0 touch-manipulation"
                              >
                                <Edit className="h-3.5 w-3.5" />
                              </button>
                            </div>
                            {(address.floor || address.doorbell) && (
                              <p className="text-xs text-t4 mt-1.5 ml-[42px]">
                                {address.floor && t('delivery:floorColon', { value: address.floor })}
                                {address.floor && address.doorbell && ' • '}
                                {address.doorbell && t('delivery:doorbellColon', { value: address.doorbell })}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Recent Orders */}
              <div className="bg-surface-0/80 dark:bg-surface-1/80 backdrop-blur-sm rounded-2xl border border-line dark:border-line/60 overflow-hidden">
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-line/60">
                  <h2 className="text-sm font-bold text-t1 uppercase tracking-wider flex items-center gap-2">
                    <Clipboard className="h-4 w-4 text-violet-600 dark:text-violet-400" />
                    {t('delivery:recentOrders')}
                  </h2>
                </div>
                
                <div className="p-3">
                  {(!customer.recent_orders || customer.recent_orders.length === 0) ? (
                    <div className="text-center py-8 text-t4">
                      <Package className="h-10 w-10 mx-auto mb-2 text-t4" />
                      <p className="text-sm">{t('delivery:noRecentOrders')}</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {customer.recent_orders.slice(0, 5).map((order) => v2 ? (
                        // Νέα εμφάνιση: «Ξανά τα ίδια» ΜΟΝΟ σε παραγγελίες delivery (με τη διεύθυνσή τους)·
                        // οι άλλες σημαδεύονται και μένουν μόνο για προβολή — οι ιστορικές τιμές τους
                        // είναι άλλου καναλιού (take away/τραπέζι), όχι τιμές delivery.
                        <div
                          key={order.id}
                          data-testid={`delivery-recent-${order.id}`}
                          className={`p-3.5 rounded-xl border ${order.order_type === 'delivery' ? 'bg-surface-2 border-line' : 'bg-surface-1 border-dashed border-line text-t3'}`}
                        >
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="font-bold text-sm text-t1">#{order.short_code}</span>
                              {order.order_type !== 'delivery' && (
                                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-surface-3 text-t2 shrink-0">
                                  {v2OrderTypeLabel(order.order_type)}
                                </span>
                              )}
                              <span className="text-xs text-t3 truncate">{orderStatusLabel(order.status)}</span>
                            </div>
                            <span className="text-sm font-semibold text-t1 tabular-nums shrink-0">{formatEuro(Number(order.total_amount) || 0)}</span>
                          </div>
                          <p className="text-xs text-t3 truncate">
                            {formatDate(order.order_date)} • {t('delivery:itemsCount', { count: Number(order.items_count) || 0 })}
                            {order.street_address && <span className="ml-1">• {order.street_address}</span>}
                          </p>
                          <div className="flex gap-2 mt-2.5">
                            <button
                              type="button"
                              onClick={() => viewOrderDetails(order.id)}
                              className="h-11 px-4 rounded-xl border border-line bg-surface-2 hover:bg-surface-3 text-sm font-medium text-t2 active:scale-95 transition-all"
                            >
                              {t('common:view')}
                            </button>
                            {order.order_type === 'delivery' && (
                              <button
                                type="button"
                                data-testid={`delivery-repeat-${order.id}`}
                                onClick={() => repeatOrder(order)}
                                className="flex-1 h-11 px-4 rounded-xl bg-brand text-brand-fg text-sm font-semibold flex items-center justify-center gap-2 hover:brightness-110 active:scale-[0.98] transition-all"
                              >
                                <RefreshCw className="h-4 w-4" />
                                {t('delivery:v2.repeat')}
                              </button>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div key={order.id} className="p-3.5 bg-surface-2 dark:bg-surface-2/40 rounded-xl border border-transparent hover:border-line-strong/60 transition-all">
                          <div className="flex items-center justify-between mb-1.5">
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-sm text-t1">#{order.short_code}</span>
                              <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                                order.status === 'completed' || order.status === 'dispatched' ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' :
                                order.status === 'pending' ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400' :
                                'bg-surface-3 dark:bg-surface-3/50 text-t3'
                              }`}>
                                {orderStatusLabel(order.status)}
                              </span>
                            </div>
                            <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">{formatCurrency(order.total_amount)}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <p className="text-xs text-t4">
                              {formatDate(order.order_date)} • {t('delivery:itemsCount', { count: Number(order.items_count) || 0 })}
                              {order.street_address && <span className="ml-1">• {order.street_address}</span>}
                            </p>
                            <div className="flex gap-1.5">
                              <button
                                onClick={() => viewOrderDetails(order.id)}
                                className="px-2.5 py-1 rounded-lg bg-surface-3 dark:bg-surface-3/40 hover:bg-surface-3 dark:hover:bg-surface-3/70 text-xs font-medium text-t2 hover:text-t1 transition-colors touch-manipulation active:scale-95"
                              >
                                {t('common:view')}
                              </button>
                              <button
                                onClick={() => repeatOrder(order)}
                                className="px-2.5 py-1 rounded-lg bg-blue-600/15 hover:bg-blue-600/25 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors touch-manipulation active:scale-95"
                              >
                                <RefreshCw className="h-3 w-3 inline mr-1" />
                                {t('delivery:reorder')}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Company/Invoice Details - Collapsible inline */}
            <div className="bg-surface-0/80 dark:bg-surface-1/80 backdrop-blur-sm rounded-2xl border border-line dark:border-line/60 overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3.5 border-b border-line/60">
                <h2 className="text-sm font-bold text-t1 uppercase tracking-wider flex items-center gap-2">
                  <FileText className="h-4 w-4 text-t3" />
                  {t('delivery:companyDetailsTitle')}
                  <span className="text-[10px] font-normal normal-case text-t4">{t('delivery:forInvoices')}</span>
                </h2>
                <button
                  onClick={openCompanyModal}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface-2 dark:bg-surface-2/60 hover:bg-surface-3 text-t3 hover:text-t1 text-xs font-bold transition-colors touch-manipulation active:scale-95"
                >
                  <Edit className="h-3 w-3" />
                  {customer.company_vat ? t('common:edit') : t('common:add')}
                </button>
              </div>
              
              <div className="p-3">
                {customer.company_vat ? (
                  <div className="p-3.5 bg-surface-2 dark:bg-surface-2/40 rounded-xl border border-line-strong/30">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-bold text-t1">{customer.company_name}</p>
                        {customer.company_commercial_title && customer.company_commercial_title !== customer.company_name && (
                          <p className="text-sm text-t2">{customer.company_commercial_title}</p>
                        )}
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm">
                          <span className="text-t3">{t('delivery:vatShort')}: <span className="text-t1 font-medium">{customer.company_vat}</span></span>
                          {customer.company_tax_office && (
                            <span className="text-t3">{t('delivery:taxOfficeShort')}: <span className="text-t2">{customer.company_tax_office}</span></span>
                          )}
                        </div>
                        {customer.company_address && (
                          <p className="text-xs text-t4 mt-1.5">
                            {customer.company_address}
                            {customer.company_city && `, ${customer.company_city}`}
                            {customer.company_postal_code && ` ${customer.company_postal_code}`}
                          </p>
                        )}
                        <div className="flex gap-3 mt-1 text-xs text-t4">
                          {customer.company_country_code && <span>{t('common:country')}: {customer.company_country_code}</span>}
                          {customer.company_branch_code !== undefined && <span>{t('delivery:branchLabel')}: {customer.company_branch_code}</span>}
                        </div>
                      </div>
                      <span className="text-[10px] font-bold uppercase bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 px-2 py-0.5 rounded-full shrink-0">{t('delivery:savedBadge')}</span>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4 text-t4">
                    <p className="text-sm">{t('delivery:noCompanyDetails')}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Sticky Action Bar */}
            <div className="sticky bottom-0 bg-surface-0/95 backdrop-blur-md rounded-2xl border border-line/60 p-4 shadow-2xl shadow-black/10 dark:shadow-black/30">
              <div className="flex items-center justify-between gap-4">
                {selectedAddress ? (
                  <div className="flex items-center gap-2 text-sm min-w-0">
                    <MapPin className={`h-4 w-4 shrink-0 ${outOfZone ? 'text-amber-700 dark:text-amber-400' : 'text-blue-600 dark:text-blue-400'}`} />
                    <span className="truncate text-t3">
                      {t('delivery:deliverTo')} <span className="font-medium text-t2">{selectedAddress.street_address}</span>
                      {selectedAddress.area && <span className="text-t4"> ({selectedAddress.area})</span>}
                    </span>
                    {outOfZone && zoneCheckDone && (
                      <span className="shrink-0 flex items-center gap-1 px-2 py-0.5 bg-amber-500/20 border border-amber-500/30 rounded-full text-xs text-amber-700 dark:text-amber-400 font-medium">
                        <AlertTriangle className="h-3 w-3" />
                        {t('delivery:outOfZoneBadge')}
                      </span>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-amber-700/80 dark:text-amber-400/80 flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    {t('delivery:selectAddressFirstHint')}
                  </p>
                )}
                
                {basket.length > 0 ? (
                  <button
                    onClick={() => continueOrder()}
                    disabled={!selectedAddress}
                    className={`cashier-primary flex items-center justify-center gap-2.5 px-8 py-3.5 rounded-xl text-base font-bold transition-all touch-manipulation active:scale-95 shrink-0 ${
                      selectedAddress
                        ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/25 hover:bg-emerald-500'
                        : 'bg-surface-2 text-t4 cursor-not-allowed'
                    }`}
                  >
                    <ShoppingCart className="h-5 w-5" />
                    {t('delivery:continueOrder', { count: basket.length })}
                  </button>
                ) : (
                  <button
                    onClick={() => startNewOrder()}
                    disabled={!selectedAddress}
                    className={`cashier-primary flex items-center justify-center gap-2.5 px-8 py-3.5 rounded-xl text-base font-bold transition-all touch-manipulation active:scale-95 shrink-0 ${
                      selectedAddress
                        ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/25 hover:bg-blue-500'
                        : 'bg-surface-2 text-t4 cursor-not-allowed'
                    }`}
                  >
                    <ShoppingCart className="h-5 w-5" />
                    {t('delivery:newOrder')}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Order Mode - TakeAway-Style Product Selection */}
        {orderMode && customer && (
          <div className="fixed inset-0 top-[64px] bg-surface-1 z-40 flex flex-col overflow-hidden">
            {/* Main Content - Left Products + Right Basket */}
            <div className="flex flex-1 overflow-hidden">
              
              {/* ============== LEFT SECTION: Categories/Subcategories/Products ============== */}
              <div className="flex-1 flex flex-col overflow-hidden">
                
                {/* Νέα εμφάνιση: τίτλος + πελάτης, αναζήτηση πάντα ανοιχτή, γρανάζι, ακύρωση */}
                {v2 && (
                  <div className="shrink-0 px-5 pt-5 pb-4 flex items-center gap-3 bg-surface-1">
                    <button
                      onClick={() => setOrderMode(false)}
                      disabled={isSubmittingOrder}
                      title={t('delivery:v2.backToCustomer', { defaultValue: 'Πίσω στον πελάτη' })}
                      aria-label={t('delivery:v2.backToCustomer', { defaultValue: 'Πίσω στον πελάτη' })}
                      className="w-11 h-11 shrink-0 rounded-xl border border-line bg-surface-2 text-t2 hover:bg-surface-3 flex items-center justify-center active:scale-95 transition-all disabled:opacity-40"
                    >
                      <User className="h-5 w-5" />
                    </button>
                    <div className="min-w-0 mr-auto">
                      <h1 className="text-2xl font-bold text-t1 leading-tight">{t('delivery:title')}</h1>
                      <p className="text-sm text-t3 mt-0.5 truncate">{customer.name} · {selectedAddress?.street_address}</p>
                    </div>
                    {store?.barcode_enabled && (barcodeError || scanningBarcode) && (
                      <div className={`flex items-center gap-2 px-3 h-10 rounded-xl border text-sm font-medium ${
                        barcodeError
                          ? 'bg-red-50 dark:bg-red-900/30 border-red-500 text-red-700 dark:text-red-300'
                          : 'bg-surface-2 border-line text-t2'
                      }`}>
                        {scanningBarcode
                          ? <><Loader2 className="h-4 w-4 animate-spin" /> {t('delivery:searching')}</>
                          : barcodeError}
                      </div>
                    )}
                    <div className="relative w-full max-w-sm">
                      <Search aria-hidden="true" className="absolute left-3.5 top-1/2 -translate-y-1/2 text-t3 h-5 w-5" />
                      <input
                        type="text"
                        placeholder={t('takeaway:v2.searchPlaceholder', { defaultValue: 'Αναζήτηση προϊόντος…' })}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full h-11 pl-11 pr-10 rounded-xl bg-surface-2 border border-line text-base text-t1 placeholder:text-t3 focus:outline-none focus:ring-2 focus:ring-brand"
                      />
                      {searchQuery && (
                        <button
                          type="button"
                          onClick={() => setSearchQuery('')}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-t3 hover:text-t1 hover:bg-surface-3"
                          aria-label={t('common:close', { defaultValue: 'Κλείσιμο' })}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    <div className="relative shrink-0">
                      <button
                        onClick={() => setShowGridSettings(!showGridSettings)}
                        aria-label={t('delivery:gridSettings')}
                        aria-expanded={showGridSettings}
                        className={`w-11 h-11 rounded-xl border flex items-center justify-center transition-colors active:scale-95 ${showGridSettings ? 'bg-brand-soft border-brand-line text-brand' : 'bg-surface-2 border-line text-t2 hover:bg-surface-3'}`}
                      >
                        <Settings className="h-5 w-5" />
                      </button>
                      {deliveryGridPopover}
                    </div>
                    <button
                      onClick={cancelOrderWithUndo}
                      disabled={isSubmittingOrder}
                      data-testid="delivery-cancel-order"
                      title={t('delivery:v2.cancelOrder', { defaultValue: 'Ακύρωση παραγγελίας' })}
                      aria-label={t('delivery:v2.cancelOrder', { defaultValue: 'Ακύρωση παραγγελίας' })}
                      className="w-11 h-11 shrink-0 rounded-xl border border-line bg-surface-2 text-t3 hover:text-red-600 hover:border-red-300 dark:hover:text-red-400 flex items-center justify-center active:scale-95 transition-all disabled:opacity-40"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                )}

                {/* Top Bar with Back, Title, Search, Grid Settings */}
                {!v2 && (
                <div className="cashier-toolbar bg-surface-2 border-b border-line-strong px-4 py-3 flex items-center gap-3">
                  {/* Back / Home — back to one-level-up; Home jumps to catalog root.
                      When at root, Back returns to the customer screen. */}
                  <button
                    onClick={() => {
                      if (currentView !== 'categories') {
                        handleBack();
                      } else if (!submitInFlightRef.current) {
                        setOrderMode(false);
                      }
                    }}
                    className="p-3 bg-surface-3 hover:bg-surface-4 rounded-xl transition-all active:scale-95 text-t2"
                    title={currentView !== 'categories' ? t('common:back') : t('delivery:backToCustomer')}
                  >
                    {currentView !== 'categories' ? (
                      <ChevronRight className="h-6 w-6 rotate-180" />
                    ) : (
                      <User className="h-6 w-6" />
                    )}
                  </button>
                  {currentView !== 'categories' && (
                    <button
                      onClick={handleHome}
                      className="p-3 bg-surface-3 hover:bg-surface-4 rounded-xl transition-all active:scale-95 text-t2"
                      title={t('common:home', { defaultValue: 'Αρχή' })}
                    >
                      <Home className="h-6 w-6" />
                    </button>
                  )}

                  {/* Title — hidden while the search bar is expanded */}
                  {!searchOpen && (
                    <div className="flex-shrink-0">
                      <h1 className="text-lg font-bold text-t1">
                        {currentView === 'categories' && t('delivery:categoriesLabel')}
                        {currentView === 'subcategories' && getSelectedCategory()?.name}
                        {currentView === 'products' && (selectedSubcategory
                          ? getSelectedCategory()?.subcategories?.find(s => s.id === selectedSubcategory)?.name
                          : getSelectedCategory()?.name)}
                      </h1>
                      <p className="text-xs text-t3">{customer.name} • {selectedAddress?.street_address}</p>
                    </div>
                  )}

                  {/* Barcode scanner feedback — global listener handles input */}
                  {!searchOpen && store?.barcode_enabled && (barcodeError || scanningBarcode) && (
                    <div className={`flex items-center gap-2 px-3 h-10 rounded-xl border text-sm font-medium ${
                      barcodeError
                        ? 'bg-red-50 dark:bg-red-900/30 border-red-500 text-red-700 dark:text-red-300'
                        : 'bg-violet-50 dark:bg-violet-900/30 border-violet-500 text-violet-700 dark:text-violet-300'
                    }`}>
                      {scanningBarcode
                        ? <><Loader2 className="h-4 w-4 animate-spin" /> {t('delivery:searching')}</>
                        : barcodeError}
                    </div>
                  )}

                  {!searchOpen && <div className="flex-1" />}

                  {/* Search — collapsed icon by default, expands inline on click */}
                  {searchOpen ? (
                    <div className="flex-1 relative">
                      <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-t3 h-5 w-5" />
                      <input
                        type="text"
                        autoFocus
                        placeholder={currentView === 'products' ? t('products:searchProducts') : t('common:searchPlaceholder')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-12 pr-12 h-12 text-base rounded-xl bg-surface-3 border-2 border-line-strong text-t1 placeholder:text-t3 focus:border-blue-400 focus:ring-blue-400 focus:outline-none"
                      />
                      <button
                        onClick={() => { setSearchQuery(''); setSearchOpen(false); }}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 p-2 text-t3 hover:text-t1"
                        title={t('common:close', { defaultValue: 'Κλείσιμο' })}
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setSearchOpen(true)}
                      className="p-3 bg-surface-3 hover:bg-surface-4 rounded-xl transition-all active:scale-95 text-t2"
                      title={t('common:searchPlaceholder', { defaultValue: 'Αναζήτηση' })}
                    >
                      <Search className="h-6 w-6" />
                    </button>
                  )}

                  {/* Grid Settings Button */}
                  <div className="relative">
                    <button
                      onClick={() => setShowGridSettings(!showGridSettings)}
                      aria-label={t('delivery:gridSettings')}
                      aria-expanded={showGridSettings}
                      className={`p-3 rounded-xl transition-all active:scale-95 ${showGridSettings ? 'bg-blue-600 text-white' : 'bg-surface-3 hover:bg-surface-4 text-t2'}`}
                    >
                      <Settings className="h-6 w-6" />
                    </button>

                    {deliveryGridPopover}
                  </div>

                  {/* Cancel Order Button — με «Αναίρεση» 4″ */}
                  <button
                    onClick={cancelOrderWithUndo}
                    disabled={isSubmittingOrder}
                    data-testid="delivery-cancel-order"
                    className="p-3 bg-surface-3 hover:bg-red-600/80 rounded-xl transition-all active:scale-95 text-t2 hover:text-white disabled:opacity-40"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
                )}

                {/* Content Area — shared catalog */}
                <ProductCatalog
                  categories={dailyCatalog.categories}
                  products={dailyCatalog.products}
                  loading={isLoadingProducts}
                  currentView={currentView}
                  selectedCategory={selectedCategory}
                  selectedSubcategory={selectedSubcategory}
                  searchTerm={searchQuery}
                  categoryColumns={categoryColumns}
                  productColumns={productColumns}
                  onCategorySelect={handleCategorySelect}
                  onSubcategorySelect={handleSubcategorySelect}
                  onProductClick={handleProductClick}
                  onProductMouseDown={handleMouseDown}
                  onProductMouseUp={handleMouseUp}
                  getProductPrice={p => productDisplayPrice(p, 'delivery')}
                  translationNamespace="delivery"
                  look={v2 ? catalogLookOf(catalogPrefs) : 'classic'}
                  onBack={v2 ? handleBack : undefined}
                  onHome={v2 ? handleHome : undefined}
                  onAllProductsSelect={handleAllProductsSelect}
                />
              </div>

              {/* ============== RIGHT SECTION: Basket ============== */}
              {!v2 && (
              <div className="cashier-basket w-96 bg-surface-0 border-l border-line flex flex-col">
                {/* Basket Header */}
                <div className="cashier-basket-header p-4 border-b border-line bg-surface-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="cashier-basket-icon w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                        <ShoppingCart className="h-5 w-5 text-t1" />
                      </div>
                      <div>
                        <h2 className="text-lg font-bold text-t1">{t('takeaway:cart')}</h2>
                        {basket.length > 0 && (
                          <span className="text-sm text-t3">{t('delivery:itemsCount', { count: basket.filter(b => b.product.fee_category !== 16).length })}</span>
                        )}
                      </div>
                    </div>
                    {basket.length > 0 && (
                      <button
                        onClick={clearBasketWithUndo}
                        disabled={isSubmittingOrder}
                        className="px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400hover:text-red-700 dark:hover:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-all active:scale-95 disabled:opacity-40"
                      >
                        {t('takeaway:clearBasket')}
                      </button>
                    )}
                  </div>
                </div>

                {/* Basket Items */}
                <div className="flex-1 overflow-y-auto p-4">
                  {basket.length > 0 && promotionBanner}
                  {basket.length === 0 ? (
                    <div className="cashier-basket-empty text-center py-12">
                      <div className="cashier-empty-icon w-20 h-20 bg-surface-1 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <ShoppingCart className="h-10 w-10 text-t4" />
                      </div>
                      <p className="font-medium text-t3">{t('delivery:emptyBasket')}</p>
                      <p className="text-sm mt-1 text-t4">{t('delivery:emptyBasketHint')}</p>
                    </div>
                  ) : (
                    <div className={`space-y-2 ${basketBusyClass}`} aria-busy={isSubmittingOrder}>
                      {renderDeliveryRows('classic')}
                    </div>
                  )}
                </div>

                {/* Total & Checkout */}
                {basket.length > 0 && (
                  <div className="cashier-basket-footer border-t border-line p-4 space-y-3 bg-surface-1">
                    {/* Totals */}
                    <div className="bg-surface-2 rounded-xl p-3 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-t3">{t('common:subtotal')}</span>
                        <span className="font-medium text-t1">{formatMoney(basketGrossSubtotal)}</span>
                      </div>
                      {discountType === 'percent' && orderDiscount > 0 && (
                        <div className="flex justify-between text-sm text-green-600 dark:text-green-400">
                          <span>{t('delivery:discountWithPercent', { percent: orderDiscount })}</span>
                          <span className="font-medium">-{formatMoney((basketGrossSubtotal * orderDiscount / 100))}</span>
                        </div>
                      )}
                      {discountType === 'fixed' && fixedDiscountAmount > 0 && (
                        <div className="flex justify-between text-sm text-green-600 dark:text-green-400">
                          <span>{t('common:discount')}</span>
                          <span className="font-medium">-{formatMoney(fixedDiscountAmount)}</span>
                        </div>
                      )}
                      {deliveryFee > 0 && (
                        <div className="flex justify-between text-sm text-amber-700 dark:text-amber-400">
                          <span>{t('delivery:deliveryFee')}</span>
                          <span className="font-medium">+{formatMoney(deliveryFee)}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-bold text-lg pt-2 border-t border-line-strong text-t1">
                        <span>{t('common:total')}</span>
                        <span className="cashier-total text-green-600 dark:text-green-400">{formatMoney(calculateTotal())}</span>
                      </div>
                    </div>

                    {/* Checkout Button */}
                    <button
                      onClick={startCheckout}
                      data-testid="delivery-checkout"
                      disabled={basket.length === 0 || !customer || !selectedAddress || isSubmittingOrder}
                      aria-busy={isSubmittingOrder}
                      className="cashier-primary w-full py-5 px-6 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:bg-surface-3 disabled:text-t3 text-white font-bold text-lg flex items-center justify-center gap-3 active:scale-[0.98] transition-all shadow-lg shadow-blue-600/30"
                    >
                      {isSubmittingOrder
                        ? <><Loader2 className="h-6 w-6 animate-spin" />{t('delivery:sending')}</>
                        : <><Truck className="h-6 w-6" />{t('takeaway:checkout')}</>}
                    </button>
                  </div>
                )}
              </div>
              )}

              {/* ============== Νέα εμφάνιση: «Η παραγγελία» ============== */}
              {v2 && (
              <div className="xv2-basket w-[26rem] shrink-0 bg-surface-2 border-l border-line border-t-[3px] border-t-brand flex flex-col">
                <div className="px-5 pt-5 pb-4">
                  <h2 className="text-xl font-bold text-t1 leading-tight">{t('takeaway:v2.order')}</h2>
                  <p className="text-sm text-t3 mt-0.5">
                    {t('takeaway:v2.lines', { count: deliveryLineCount })} · {t('takeaway:v2.units', { count: deliveryUnitCount })}
                  </p>
                </div>
                <div className={`grid ${BASKET_V2_COLUMNS} gap-x-2 px-5 py-2 border-y border-line bg-surface-1 text-xs font-medium text-t3`}>
                  <span>{t('takeaway:v2.colProduct')}</span>
                  <span className="text-center">{t('takeaway:v2.colQuantity')}</span>
                  <span className="text-right">{t('takeaway:v2.colTotal')}</span>
                  <span />
                </div>
                <div className={`flex-1 min-h-0 overflow-y-auto px-5 ${basketBusyClass}`} aria-busy={isSubmittingOrder}>
                  {basket.length > 0 && promotionBanner && <div className="pt-3">{promotionBanner}</div>}
                  {basket.length === 0 ? (
                    <p className="text-center text-sm text-t3 py-10">{t('takeaway:v2.emptyHint')}</p>
                  ) : (
                    renderDeliveryRows('v2')
                  )}
                </div>
                {renderUndoBar('panel')}
                <div className="border-t border-line px-5 pt-4 pb-5 space-y-3">
                  {basket.length > 0 && (
                    <div className="space-y-1.5 text-sm">
                      <div className="flex justify-between"><span className="text-t3">{t('common:subtotal')}</span><span className="tabular-nums text-t2">{formatEuro(basketGrossSubtotal)}</span></div>
                      {discountType === 'percent' && orderDiscount > 0 && (
                        <div className="flex justify-between"><span className="text-t3">{t('delivery:discountWithPercent', { percent: orderDiscount })}</span><span className="tabular-nums text-t1">−{formatEuro(basketGrossSubtotal * orderDiscount / 100)}</span></div>
                      )}
                      {discountType === 'fixed' && fixedDiscountAmount > 0 && (
                        <div className="flex justify-between"><span className="text-t3">{t('common:discount')}</span><span className="tabular-nums text-t1">−{formatEuro(fixedDiscountAmount)}</span></div>
                      )}
                      {deliveryFee > 0 && (
                        <div className="flex justify-between"><span className="text-t3">{t('delivery:deliveryFee')}</span><span className="tabular-nums text-t1">+{formatEuro(deliveryFee)}</span></div>
                      )}
                    </div>
                  )}
                  <div className="flex items-end justify-between gap-3">
                    <div>
                      <div className="text-base font-semibold text-t1">{t('takeaway:v2.total')}</div>
                      <div className="text-sm text-t3">{t('takeaway:v2.units', { count: deliveryUnitCount })}</div>
                    </div>
                    <div className="text-[1.75rem] leading-none font-bold text-t1 tabular-nums">{formatEuro(calculateTotal())}</div>
                  </div>
                  {/* Πληρωμή με ένα πάτημα (όπως το Take Away): ο τρόπος ΕΙΝΑΙ η αποστολή· κανένας
                      προεπιλεγμένος. IRIS, τιμολόγιο, έκπτωση, σημειώσεις, τρόπος παράδοσης → «⋯». */}
                  {(() => {
                    const payDisabled = basket.length === 0 || !customer || !selectedAddress || isSubmittingOrder;
                    const sendingLabel = (
                      <span className="flex items-center gap-2 text-lg"><Loader2 className="h-6 w-6 animate-spin" />{t('delivery:sending')}</span>
                    );
                    return (
                      <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_3.5rem] gap-2">
                        <button
                          type="button"
                          data-testid="delivery-pay-cash"
                          disabled={payDisabled}
                          aria-busy={isSubmittingOrder && paymentMethod === 'cash'}
                          onPointerDown={cashHoldStart}
                          // Αφή: το click που ακολουθεί δεν πρέπει να κλείσει το παράθυρο που ίσως ανοίγει (15/10/2026).
                          onPointerUp={e => { swallowGhostClick(e); cashHoldEnd(!payDisabled); }}
                          onPointerLeave={() => cashHoldEnd(false)}
                          onPointerCancel={() => cashHoldEnd(false)}
                          onContextMenu={e => e.preventDefault()}
                          className={`select-none touch-manipulation h-[4.5rem] rounded-xl font-semibold flex flex-col items-center justify-center gap-0.5 active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                            isTrainingOrder ? 'bg-orange-500 text-white' : 'bg-brand text-brand-fg hover:brightness-110'
                          }`}
                        >
                          {isSubmittingOrder && paymentMethod === 'cash' ? sendingLabel : (
                            <>
                              <span className="flex items-center gap-2 text-lg"><Banknote className="h-6 w-6" />{t('delivery:cash')}</span>
                              <span className="text-base tabular-nums opacity-90">{formatEuro(calculateTotal())}</span>
                            </>
                          )}
                        </button>
                        <button
                          type="button"
                          data-testid="delivery-pay-card"
                          disabled={payDisabled}
                          aria-busy={isSubmittingOrder && paymentMethod === 'card'}
                          onClick={() => startQuickPay('card')}
                          className="select-none touch-manipulation h-[4.5rem] rounded-xl bg-brand text-brand-fg font-semibold flex flex-col items-center justify-center gap-0.5 hover:brightness-110 active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {isSubmittingOrder && paymentMethod === 'card' ? sendingLabel : (
                            <>
                              <span className="flex items-center gap-2 text-lg"><CreditCard className="h-6 w-6" />{t('delivery:card')}</span>
                              <span className="text-base tabular-nums opacity-90">{formatEuro(calculateTotal())}</span>
                            </>
                          )}
                        </button>
                        <div className="relative">
                          <button
                            type="button"
                            onClick={() => setShowBasketMenu(open => !open)}
                            aria-label={t('takeaway:v2.more')}
                            aria-expanded={showBasketMenu}
                            disabled={basket.length === 0 || isSubmittingOrder}
                            className="w-14 h-[4.5rem] rounded-xl border border-line bg-surface-2 text-t2 flex items-center justify-center hover:bg-surface-3 active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            {isSubmittingOrder && paymentMethod === 'iris' ? <Loader2 className="h-5 w-5 animate-spin" /> : <Ellipsis className="h-5 w-5" />}
                          </button>
                          {showBasketMenu && basket.length > 0 && !isSubmittingOrder && (
                            <>
                              <div className="fixed inset-0 z-40" onClick={() => setShowBasketMenu(false)} />
                              <div className="absolute right-0 bottom-full mb-2 w-72 z-50 rounded-xl border border-line bg-surface-2 shadow-xl shadow-black/10 dark:shadow-black/40 py-1.5">
                                <button
                                  type="button"
                                  data-testid="delivery-checkout"
                                  disabled={!customer || !selectedAddress}
                                  onClick={() => { setShowBasketMenu(false); startCheckout(); }}
                                  className="w-full min-h-[44px] flex items-center gap-3 px-4 py-3 text-sm font-medium text-t1 hover:bg-surface-3 text-left disabled:opacity-40"
                                >
                                  <Receipt className="h-4 w-4 text-t3" />
                                  {t('delivery:v2.payOther')}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => { setShowBasketMenu(false); clearBasketWithUndo(); }}
                                  className="w-full min-h-[44px] flex items-center gap-3 px-4 py-3 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 text-left"
                                >
                                  <X className="h-4 w-4" />
                                  {t('takeaway:v2.clear')}
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
              )}
            </div>
          </div>
        )}

        {upsellPick && (
          <UpsellPrompt
            message={upsellPick.suggestion.message}
            products={upsellPick.products}
            channel="delivery"
            onAdd={handleUpsellAdd}
            onDecline={handleUpsellDecline}
          />
        )}

        {/* Checkout Modal */}
        {showCheckoutModal && (
          <div 
            className="cashier-checkout-overlay fixed inset-0 flex items-center justify-center z-50 p-4"
            onClick={() => setShowCheckoutModal(false)}
          >
            <div 
              className="cashier-dialog cashier-delivery-checkout bg-surface-1 rounded-2xl w-full max-w-md max-h-[90vh] overflow-hidden shadow-2xl flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="cashier-dialog-header p-4 border-b border-line-strong bg-gradient-to-r from-blue-600 to-blue-700 flex items-center justify-between shrink-0">
                <div>
                  <h2 className="text-lg font-bold text-t1">{t('delivery:checkoutTitle')}</h2>
                  <p className="text-blue-800 dark:text-blue-200 text-sm">{t('delivery:basketItemsCount', { count: basket.length, amount: formatMoney(calculateTotal()) })}</p>
                </div>
                <button
                  onClick={() => setShowCheckoutModal(false)}
                  className="w-10 h-10 rounded-xl bg-blue-500/40 hover:bg-blue-500/60 flex items-center justify-center active:scale-95 transition-all"
                >
                  <X className="h-5 w-5 text-t1" />
                </button>
              </div>

              {/* Content - Scrollable */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">

                {/* Customer & Address Summary */}
                <div className="bg-surface-2 dark:bg-surface-2/50 rounded-xl p-3 space-y-1">
                  <div className="flex items-center gap-2 text-sm">
                    <User className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    <span className="font-medium text-t1">{customer?.name}</span>
                    <span className="text-t3">• {customer?.phone}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-green-600 dark:text-green-400" />
                    <span className="text-t2 truncate">{selectedAddress?.street_address}{selectedAddress?.area ? `, ${selectedAddress.area}` : ''}</span>
                  </div>
                </div>

                {/* Order Discount */}
                {hasPermission('delivery_apply_discount') && (
                <div>
                  <Label className="text-xs text-t3 font-semibold">{t('common:discount')}</Label>
                  
                  {/* Discount type tabs (show only if fixed amount is allowed) */}
                  {deliverySettings?.discount_allow_fixed_amount && (
                    <div className="flex gap-2 mt-1.5 mb-2">
                      <button
                        aria-pressed={discountType === 'percent'}
                        onClick={() => { setDiscountType('percent'); setFixedDiscountAmount(0); }}
                        className={`cashier-choice flex-1 py-2 rounded-lg text-xs font-semibold transition-all ${
                          discountType === 'percent' ? 'bg-purple-600 text-white' : 'bg-surface-2 text-t3 hover:bg-surface-3'
                        }`}
                      >
                        {t('delivery:discountPercentageLabel')}
                      </button>
                      <button
                        aria-pressed={discountType === 'fixed'}
                        onClick={() => { setDiscountType('fixed'); setOrderDiscount(0); }}
                        className={`cashier-choice flex-1 py-2 rounded-lg text-xs font-semibold transition-all ${
                          discountType === 'fixed' ? 'bg-purple-600 text-white' : 'bg-surface-2 text-t3 hover:bg-surface-3'
                        }`}
                      >
                        {t('delivery:discountAmountEuro')}
                      </button>
                    </div>
                  )}

                  {discountType === 'percent' ? (
                    <div className="space-y-2 mt-1.5">
                      <div className="flex gap-2 flex-wrap">
                        <button
                          aria-pressed={orderDiscount === 0}
                          onClick={() => setOrderDiscount(0)}
                          className={`cashier-choice flex-1 min-w-[45px] py-2.5 rounded-xl text-sm font-bold transition-all active:scale-95 ${
                            orderDiscount === 0 ? 'bg-green-600 text-white shadow-md' : 'bg-surface-2 text-t2 hover:bg-surface-3'
                          }`}
                        >
                          0%
                        </button>
                        {(deliverySettings?.discount_presets || [5, 10, 15, 20]).map((preset) => (
                          <button
                            key={preset}
                            onClick={() => setOrderDiscount(Math.min(preset, deliverySettings?.discount_max_percent || 100))}
                            className={`flex-1 min-w-[45px] py-2.5 rounded-xl text-sm font-bold transition-all active:scale-95 ${
                              orderDiscount === preset ? 'bg-green-600 text-white shadow-md' : 'bg-surface-2 text-t2 hover:bg-surface-3'
                            }`}
                          >
                            {preset}%
                          </button>
                        ))}
                      </div>
                      {/* Custom percent — opens shared numpad */}
                      {deliverySettings?.discount_allow_custom_percent && (
                        <button
                          type="button"
                          onClick={() => { setNumpadTarget('orderPercent'); setShowNumpad(true); }}
                          className="flex items-center gap-2 h-10 px-3 rounded-xl border-2 border-line-strong bg-surface-2 hover:border-purple-500 active:scale-95 transition-all"
                        >
                          <span className="text-t1 font-bold">
                            {orderDiscount > 0 && !(deliverySettings?.discount_presets || []).includes(orderDiscount) ? orderDiscount : '—'}
                          </span>
                          <span className="text-t3 font-bold">%</span>
                        </button>
                      )}
                    </div>
                  ) : (
                    /* Fixed amount — opens shared numpad */
                    <div className="flex items-center gap-2 mt-1.5">
                      <button
                        type="button"
                        onClick={() => { setNumpadTarget('orderFixed'); setShowNumpad(true); }}
                        className="flex items-center gap-2 h-10 px-3 rounded-xl border-2 border-line-strong bg-surface-2 hover:border-purple-500 active:scale-95 transition-all"
                      >
                        <span className="text-t1 font-bold">
                          {fixedDiscountAmount > 0 ? fixedDiscountAmount.toFixed(2) : '—'}
                        </span>
                        <span className="text-t3 font-bold">€</span>
                      </button>
                    </div>
                  )}
                </div>
                )}

                {/* Totals */}
                <div className="bg-surface-2 rounded-xl p-3 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-t3">{t('common:subtotal')}</span>
                    <span className="font-medium text-t1">{formatMoney(basketGrossSubtotal)}</span>
                  </div>
                  {discountType === 'percent' && orderDiscount > 0 && (
                    <div className="flex justify-between text-sm text-green-600 dark:text-green-400">
                      <span>{t('delivery:discountWithPercent', { percent: orderDiscount })}</span>
                      <span className="font-medium">-{formatMoney(basketGrossSubtotal * orderDiscount / 100)}</span>
                    </div>
                  )}
                  {discountType === 'fixed' && fixedDiscountAmount > 0 && (
                    <div className="flex justify-between text-sm text-green-600 dark:text-green-400">
                      <span>{t('common:discount')}</span>
                      <span className="font-medium">-{formatMoney(fixedDiscountAmount)}</span>
                    </div>
                  )}
                  {deliveryFee > 0 && (
                    <div className="flex justify-between text-sm text-amber-700 dark:text-amber-400">
                      <span>{t('delivery:deliveryFee')}</span>
                      <span className="font-medium">+{formatMoney(deliveryFee)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-lg pt-2 border-t border-line-strong text-t1">
                    <span>{t('common:total')}</span>
                    <span className="cashier-total text-green-600 dark:text-green-400">{formatMoney(calculateTotal())}</span>
                  </div>
                </div>

                {/* Delivery Notes */}
                <div>
                  <Label className="text-xs text-t3 font-semibold">{t('delivery:deliveryNotes')}</Label>
                  <textarea
                    value={deliveryNotes}
                    onChange={(e) => setDeliveryNotes(e.target.value)}
                    placeholder={t('delivery:specialInstructionsPlaceholder')}
                    className="w-full px-3 py-2.5 bg-surface-2 border border-line-strong rounded-xl text-sm text-t1 placeholder-t4 mt-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={2}
                  />
                </div>

                {/* Delivery Method — εμφανίζεται όταν ΔΕΝ εννοείται (κάτι πέρα από τον δικό μας
                    διανομέα): ρητή επιλογή σε κάθε παραγγελία, κανένα προεπιλεγμένο κουμπί */}
                {deliveryChoiceNeeded && (
                  <div data-testid="delivery-method-choice">
                    <Label className="text-xs text-t3 font-semibold">{t('delivery:deliveryMethod')}</Label>
                    {deliveryMethod === null && (
                      <p className="text-xs font-medium text-amber-700 dark:text-amber-400 mt-1">{t('delivery:chooseDeliveryMethod')}</p>
                    )}
                    <div className="flex gap-2 mt-1.5">
                      {store?.delivery_self && (
                        <button
                          aria-pressed={deliveryMethod === 'self'}
                          onClick={() => setDeliveryMethod('self')}
                          className={`cashier-choice flex-1 flex items-center justify-center gap-1.5 px-3 py-3 rounded-xl text-sm font-semibold transition-all active:scale-95 ${
                            deliveryMethod === 'self'
                              ? 'bg-blue-600 text-white shadow-md'
                              : 'bg-surface-2 text-t2 hover:bg-surface-3 border border-line-strong'
                          }`}
                        >
                          <Truck className="h-4 w-4" />
                          {t('delivery:selfDelivery')}
                        </button>
                      )}
                      {store?.delivery_efood && (
                        <button
                          aria-pressed={deliveryMethod === 'efood'}
                          onClick={() => setDeliveryMethod('efood')}
                          className={`cashier-choice flex-1 flex items-center justify-center gap-1.5 px-3 py-3 rounded-xl text-sm font-semibold transition-all active:scale-95 ${
                            deliveryMethod === 'efood'
                              ? 'bg-orange-500 text-white shadow-md'
                              : 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 hover:bg-orange-200 dark:hover:bg-orange-900/50 border border-orange-300 dark:border-orange-700/50'
                          }`}
                        >
                          e-food
                        </button>
                      )}
                      {store?.delivery_wolt && (
                        <button
                          aria-pressed={deliveryMethod === 'wolt'}
                          onClick={() => setDeliveryMethod('wolt')}
                          className={`cashier-choice flex-1 flex items-center justify-center gap-1.5 px-3 py-3 rounded-xl text-sm font-semibold transition-all active:scale-95 ${
                            deliveryMethod === 'wolt'
                              ? 'bg-cyan-500 text-white shadow-md'
                              : 'bg-cyan-50 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400 hover:bg-cyan-200 dark:hover:bg-cyan-900/50 border border-cyan-300 dark:border-cyan-700/50'
                          }`}
                        >
                          Wolt
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Payment Method — κανένας προεπιλεγμένος (ούτε κληρονομημένος από την προηγούμενη) */}
                <div>
                  <Label className="text-xs text-t3 font-semibold">{t('delivery:paymentMethodLabel')}</Label>
                  {paymentMethod === null && (
                    <p className="text-xs font-medium text-amber-700 dark:text-amber-400 mt-1">{t('delivery:choosePaymentMethod')}</p>
                  )}
                  <div className="flex gap-2 mt-1.5">
                    <button
                      aria-pressed={paymentMethod === 'cash'}
                      onClick={() => setPaymentMethod('cash')}
                      className={`cashier-choice flex-1 flex items-center justify-center gap-1.5 px-3 py-3 rounded-xl text-sm font-semibold transition-all active:scale-95 ${
                        paymentMethod === 'cash'
                          ? 'bg-green-600 text-white shadow-md'
                          : 'bg-surface-2 text-t2 hover:bg-surface-3 border border-line-strong'
                      }`}
                    >
                      <Banknote className="h-4 w-4" />
                      {t('delivery:cash')}
                    </button>
                    <button
                      aria-pressed={paymentMethod === 'card'}
                      onClick={() => setPaymentMethod('card')}
                      className={`cashier-choice flex-1 flex items-center justify-center gap-1.5 px-3 py-3 rounded-xl text-sm font-semibold transition-all active:scale-95 ${
                        paymentMethod === 'card'
                          ? 'bg-yellow-500 text-white shadow-md'
                          : 'bg-surface-2 text-t2 hover:bg-surface-3 border border-line-strong'
                      }`}
                    >
                      <CreditCard className="h-4 w-4" />
                      {t('delivery:card')}
                    </button>
                    <button
                      aria-pressed={paymentMethod === 'iris'}
                      onClick={() => setPaymentMethod('iris')}
                      className={`cashier-choice px-3 py-3 rounded-xl text-sm font-semibold transition-all active:scale-95 ${
                        paymentMethod === 'iris'
                          ? 'bg-cyan-600 text-white shadow-md'
                          : 'bg-surface-2 text-t2 hover:bg-surface-3 border border-line-strong'
                      }`}
                    >
                      IRIS
                    </button>
                  </div>
                </div>

                {/* Fiscal Document Type - only show if ECR is enabled */}
                {ecrEnabled && (
                  <div>
                    <Label className="text-xs text-t3 font-semibold">{t('delivery:fiscalTypeLabel')}</Label>
                    <div className="flex gap-2 mt-1.5">
                      <button
                        aria-pressed={fiscalDocumentType === 1}
                        onClick={() => {
                          setFiscalDocumentType(1);
                          setCustomerVatNumber('');
                          setCustomerCompanyName('');
                          setCustomerCompanyActivity('');
                          setCustomerCompanyAddress('');
                          setVatLookupError('');
                        }}
                        className={`cashier-choice flex-1 flex items-center justify-center gap-1.5 px-3 py-3 rounded-xl text-sm font-semibold transition-all active:scale-95 ${
                          fiscalDocumentType === 1
                            ? 'bg-blue-600 text-white shadow-md'
                            : 'bg-surface-2 text-t2 hover:bg-surface-3 border border-line-strong'
                        }`}
                      >
                        <Receipt className="h-4 w-4" />
                        {t('delivery:receiptLabel')}
                      </button>
                      <button
                        aria-pressed={fiscalDocumentType === 2}
                        onClick={() => setFiscalDocumentType(2)}
                        className={`cashier-choice flex-1 flex items-center justify-center gap-1.5 px-3 py-3 rounded-xl text-sm font-semibold transition-all active:scale-95 ${
                          fiscalDocumentType === 2
                            ? 'bg-blue-600 text-white shadow-md'
                            : 'bg-surface-2 text-t2 hover:bg-surface-3 border border-line-strong'
                        }`}
                      >
                        <FileText className="h-4 w-4" />
                        {t('delivery:invoiceLabel')}
                      </button>
                    </div>
                  </div>
                )}

                {/* Invoice VAT Details */}
                {ecrEnabled && fiscalDocumentType === 2 && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-300 dark:border-blue-700/50 space-y-3">
                    <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
                      <Building2 className="h-4 w-4" />
                      <span className="text-sm font-medium">{t('delivery:invoiceDetailsLabel')}</span>
                    </div>
                    
                    {customer?.company_vat && !customerVatNumber && (
                      <div className="p-2.5 bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700/50 rounded-xl">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-green-600 dark:text-green-400 font-medium">{t('delivery:savedDetailsLabel')}</span>
                          <button
                            className="text-xs px-2 py-1 rounded-lg bg-green-600/30 text-green-600 dark:text-green-400 hover:bg-green-600/50 transition-all"
                            onClick={() => {
                              setCustomerVatNumber(customer.company_vat || '');
                              setCustomerCompanyName(customer.company_name || '');
                              setCustomerCompanyActivity(customer.company_commercial_title || '');
                              setCustomerCompanyAddress(
                                [customer.company_address, customer.company_city, customer.company_postal_code]
                                  .filter(Boolean).join(', ')
                              );
                            }}
                          >
                            {t('delivery:useBtn')}
                          </button>
                        </div>
                        <p className="text-sm font-medium text-t1">{customer.company_name}</p>
                        <p className="text-xs text-t3">{t('delivery:vatShort')}: {customer.company_vat}</p>
                      </div>
                    )}
                    
                    <div>
                      <Label className="text-xs text-t3">{t('delivery:vatShort')}</Label>
                      <div className="flex gap-2 mt-1">
                        <input
                          type="text"
                          value={customerVatNumber}
                          onChange={(e) => setCustomerVatNumber(e.target.value.replace(/\D/g, '').slice(0, 9))}
                          placeholder={t('delivery:nineDigitsPlaceholder')}
                          className="flex-1 px-3 py-2.5 bg-surface-2 border border-line-strong rounded-xl text-sm text-t1 placeholder-t4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          maxLength={9}
                        />
                        <button
                          onClick={lookupVatNumber}
                          disabled={isLookingUpVat || customerVatNumber.length !== 9}
                          className="cashier-primary px-3 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:bg-surface-3 disabled:text-t4 text-white transition-all active:scale-95"
                        >
                          {isLookingUpVat ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Search className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                      {vatLookupError && (
                        <p className="text-xs text-red-600 dark:text-red-400 mt-1">{vatLookupError}</p>
                      )}
                    </div>

                    {customerCompanyName && (
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="text-t3 text-xs">{t('delivery:companyNameLabel')}:</span>
                          <p className="font-medium text-t1">{customerCompanyName}</p>
                        </div>
                        {customerCompanyActivity && (
                          <div>
                            <span className="text-t3 text-xs">{t('delivery:activityLabel')}:</span>
                            <p className="text-t2">{customerCompanyActivity}</p>
                          </div>
                        )}
                        {customerCompanyAddress && (
                          <div>
                            <span className="text-t3 text-xs">{t('delivery:addressLabel')}:</span>
                            <p className="text-t2">{customerCompanyAddress}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Footer - Action Buttons */}
              <div className="p-4 border-t border-line-strong bg-surface-1/80 space-y-3 shrink-0">
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowCheckoutModal(false)}
                    className="flex-1 py-4 px-6 rounded-xl border-2 border-line-strong font-semibold text-t3 hover:bg-surface-2 active:scale-[0.98] transition-all"
                  >
                    {t('common:back')}
                  </button>
                  <button
                    // Training mode: hold for 2s to activate (only when self delivery + receipt + cash)
                    data-testid="delivery-send"
                    onPointerDown={() => {
                      if (!checkoutSendable) return;
                      if (trainingModeEnabled && effectiveDeliveryMethod === 'self' && fiscalDocumentType === 1 && paymentMethod === 'cash') {
                        holdTimerRef.current = setTimeout(() => {
                          setIsTrainingOrder(true);
                        }, 2000);
                      }
                    }}
                    onPointerUp={(e) => {
                      if (holdTimerRef.current) {
                        clearTimeout(holdTimerRef.current);
                        holdTimerRef.current = null;
                      }
                      // Το React δίνει pointer events και σε κλειστό κουμπί: ο έλεγχος ΕΔΩ, όχι μόνο στο disabled.
                      if (!checkoutSendable) return;
                      // Το παράθυρο κλείνει εδώ· το click της αφής θα έπεφτε σε ό,τι είναι από κάτω.
                      swallowGhostClick(e);
                      setShowCheckoutModal(false);
                      submitOrder();
                    }}
                    onPointerLeave={() => {
                      if (holdTimerRef.current) {
                        clearTimeout(holdTimerRef.current);
                        holdTimerRef.current = null;
                      }
                    }}
                    disabled={!checkoutSendable}
                    className={`${isTrainingOrder ? '' : 'cashier-primary'} flex-1 py-4 px-6 rounded-xl font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-all select-none shadow-lg ${
                      isTrainingOrder
                        ? 'bg-orange-500 hover:bg-orange-600 text-white shadow-orange-600/30'
                        : 'bg-blue-600 hover:bg-blue-500 disabled:bg-surface-3 disabled:text-t3 text-white shadow-blue-600/30'
                    }`}
                  >
                    <Truck className="h-5 w-5" />
                    {t('delivery:sendBtn')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Size Selection Modal — select-confirm UX */}
        {showSizeModal && selectedProduct && (
          <ProductSizeModal
            channel="delivery"
            product={selectedProduct}
            selectedSize={selectedSize}
            onSelectSize={setSelectedSize}
            onConfirm={() => {
              if (selectedSize && selectedProduct) {
                const requiredGroups = productOptionGroups.filter(g => g.is_required);
                if (requiredGroups.length > 0) {
                  // Narrow to required-only for quick-click flow (mirrors TakeAway)
                  setProductOptionGroups(requiredGroups);
                  setOptionModalPendingSize(selectedSize);
                  setShowSizeModal(false);
                  setShowOptionModal(true);
                } else {
                  addToBasketWithSize(selectedProduct, selectedSize);
                }
              }
            }}
            onClose={() => {
              setShowSizeModal(false);
              setSelectedProduct(null);
              setSelectedSize(null);
            }}
            mode="select-confirm"
            translationNamespace="delivery"
          />
        )}

        {/* Option Groups Modal — quick-click flow (required groups only) */}
        {showOptionModal && selectedProduct && (
          <ProductOptionsModal
            channel="delivery"
            product={selectedProduct}
            pendingSize={optionModalPendingSize}
            groups={productOptionGroups}
            selectedOptions={pendingSelectedOptions}
            onChange={setPendingSelectedOptions}
            onClose={() => {
              setShowOptionModal(false);
              setProductOptionGroups([]);
              setPendingSelectedOptions([]);
              setOptionModalPendingSize(null);
              setSelectedProduct(null);
            }}
            onConfirm={() => {
              const missing = productOptionGroups.filter(g => g.is_required && !pendingSelectedOptions.some(o => o.group_id === g.id));
              if (missing.length > 0) {
                showToast('warning', t('delivery:pleaseSelect', { groups: missing.map(g => g.name).join(', '), defaultValue: `Λείπουν επιλογές: ${missing.map(g => g.name).join(', ')}` }));
                return;
              }
              if (optionModalPendingSize) {
                addToBasketWithSize(selectedProduct, optionModalPendingSize, { selected_options: pendingSelectedOptions });
              } else {
                // No-size product with options
                const validation = canAddToBasket(selectedProduct);
                if (!validation.allowed) { showToast('warning', validation.message || 'Μη έγκυρη επιλογή'); return; }
                const optionsPrice = pendingSelectedOptions.reduce((s, o) => s + o.price_modifier, 0);
                const price = lineUnitPrice(selectedProduct, null, 'delivery');
                const basketItem: BasketItem = {
                  id: `${selectedProduct.id}-${Date.now()}`,
                  product: selectedProduct,
                  selectedSize: null,
                  quantity: 1,
                  price: price + optionsPrice,
                  totalPrice: price + optionsPrice,
                  removedIngredients: [],
                  addedExtras: [],
                  selected_options: pendingSelectedOptions.length ? [...pendingSelectedOptions] : undefined,
                  specialInstructions: ''
                };
                setBasket(prev => addOrMergeBasketLine(prev, basketItem));
                setLastAddedProduct(selectedProduct.name);
                setAddedToCartAnimation(true);
                setTimeout(() => setAddedToCartAnimation(false), 1500);
                setShowOptionModal(false);
                setProductOptionGroups([]);
                setPendingSelectedOptions([]);
                setOptionModalPendingSize(null);
                setSelectedProduct(null);
              }
            }}
            translationNamespace="delivery"
          />
        )}

        {/* Customize Modal */}
        {showCustomizeModal && selectedProduct && (
          <div
            className="cashier-overlay fixed inset-0 flex items-center justify-center z-50 p-2 sm:p-4"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)', backdropFilter: 'blur(4px)' }}
            onClick={() => {
              setShowCustomizeModal(false);
              setSelectedProduct(null);
              setSelectedSize(null);
              setRemovedIngredients([]);
              setAddedExtras([]);
              setSpecialInstructions('');
              setProductDiscount(0);
              setProductOptionGroups([]);
              setPendingSelectedOptions([]);
            }}
          >
            <div
              className="cashier-dialog bg-surface-1 rounded-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden shadow-2xl flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="cashier-dialog-header p-4 border-b border-line-strong bg-surface-2 flex items-center justify-between shrink-0">
                <div>
                  <h2 className="text-lg font-bold text-t1">{t('delivery:customizeTitle')}</h2>
                  <p className="text-sm text-t3">{selectedProduct.name}</p>
                </div>
                <button
                  onClick={() => {
                    setShowCustomizeModal(false);
                    setSelectedProduct(null);
                    setSelectedSize(null);
                    setRemovedIngredients([]);
                    setAddedExtras([]);
                    setSpecialInstructions('');
                    setProductDiscount(0);
                    setProductOptionGroups([]);
                    setPendingSelectedOptions([]);
                  }}
                  className="w-10 h-10 rounded-xl bg-surface-3 hover:bg-surface-4 flex items-center justify-center active:scale-95 transition-all"
                >
                  <X className="h-5 w-5 text-t2" />
                </button>
              </div>

              {/* Content - Scrollable */}
              <div className="flex-1 overflow-y-auto p-4">
              
              {/* Size Selection if multiple sizes */}
              {selectedProduct.sizes && selectedProduct.sizes.length > 1 && (
                <div className="mb-5">
                  <Label className="text-base font-bold mb-3 block text-t2">{t('delivery:sizeLabel')}</Label>
                  <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3">
                    {selectedProduct.sizes.map((size) => (
                      <button
                        key={size.id}
                        onClick={async () => {
                          setSelectedSize(size);
                          await fetchSizeIngredients(size.id);
                        }}
                        className={`p-4 rounded-xl border-2 transition-all active:scale-[0.97] min-h-[96px] flex flex-col items-center justify-center ${
                          selectedSize?.id === size.id
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 shadow-md'
                            : 'border-line-strong hover:border-blue-400 hover:bg-surface-2'
                        }`}
                      >
                        <div className="font-bold text-lg text-t1">{size.name}</div>
                        <div className="text-blue-600 dark:text-blue-400 font-semibold text-sm mt-1">
                          {formatMoney(sizeChannelPrice(size, 'delivery'))}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Base Ingredients (Removable) - Only show in warehouse mode */}
              {store?.warehouse !== false && productIngredients.filter(i => i.is_default !== false && !i.is_extra_available && i.is_removable).length > 0 && (
                <div className="mb-6">
                  <Label className="text-base font-bold mb-3 block text-t2">{t('products:ingredients')}</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {productIngredients
                      .filter(ingredient => ingredient.is_default !== false && !ingredient.is_extra_available && ingredient.is_removable)
                      .map((ingredient) => {
                        const ingId = ingredient.raw_material_id || ingredient.id;
                        const isRemoved = removedIngredients.includes(ingId);
                        return (
                          <button
                            key={ingId}
                            onClick={() => {
                              if (isRemoved) {
                                setRemovedIngredients(removedIngredients.filter((id: any) => id !== ingId));
                              } else {
                                setRemovedIngredients([...removedIngredients, ingId]);
                              }
                            }}
                            className={`p-3 rounded-xl border-2 transition-all active:scale-[0.97] text-left ${
                              isRemoved
                                ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 line-through text-red-600 dark:text-red-400'
                                : 'border-line-strong hover:border-line-strong'
                            }`}
                          >
                            <span className="font-medium text-sm text-t2">{ingredient.name}</span>
                          </button>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* Extra Ingredients - Only show in warehouse mode */}
              {store?.warehouse !== false && availableExtras.length > 0 && (
                <div className="mb-6">
                  <Label className="text-base font-bold mb-3 block text-t2">{t('delivery:extrasLabel')}</Label>
                  <div className="space-y-2">
                    {availableExtras.map((extra) => {
                      const extraId = extra.raw_material_id || extra.id;
                      const existingExtra = addedExtras.find((e: any) => e.raw_material_id === extraId);
                      const quantity = existingExtra?.quantity || 0;
                      
                      return (
                        <div key={extraId} className="flex items-center justify-between p-3 bg-surface-2 rounded-xl">
                          <div>
                            <span className="font-medium text-sm text-t2">{extra.name}</span>
                            {(extra.extra_price || extra.price) > 0 && (
                              <span className="text-green-600 dark:text-green-400 text-sm ml-2">+{formatMoney(Number(extra.extra_price || extra.price || 0))}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => {
                                if (quantity > 0) {
                                  if (quantity === 1) {
                                    setAddedExtras(addedExtras.filter((e: any) => e.raw_material_id !== extraId));
                                  } else {
                                    setAddedExtras(addedExtras.map((e: any) => 
                                      e.raw_material_id === extraId ? { ...e, quantity: e.quantity - 1 } : e
                                    ));
                                  }
                                }
                              }}
                              className="w-8 h-8 rounded-lg bg-surface-4 hover:bg-slate-400 flex items-center justify-center active:scale-95 text-t1"
                              disabled={quantity === 0}
                            >
                              <Minus className="h-4 w-4" />
                            </button>
                            <span className="font-bold w-6 text-center text-t1">{quantity}</span>
                            <button
                              onClick={() => {
                                if (quantity === 0) {
                                  setAddedExtras([...addedExtras, {
                                    raw_material_id: extraId,
                                    name: extra.name,
                                    quantity: 1,
                                    price: Number(extra.extra_price || extra.price || 0)
                                  }]);
                                } else if (quantity < 5) {
                                  setAddedExtras(addedExtras.map((e: any) => 
                                    e.raw_material_id === extraId ? { ...e, quantity: e.quantity + 1 } : e
                                  ));
                                }
                              }}
                              className={`w-8 h-8 rounded-lg flex items-center justify-center active:scale-95 ${
                                quantity < 5 ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-surface-3 text-t4'
                              }`}
                              disabled={quantity >= 5}
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Simple Ingredients for Non-Warehouse Stores — κανόνες «χωρίς»/«Έξτρα»: utils/simpleIngredients */}
              {store?.warehouse === false && selectedProduct?.simple_ingredients && selectedProduct.simple_ingredients.length > 0 && (
                <>
                  {/* Base Ingredients (Removable) — ταυτότητα = store_ingredient_id, όχι θέση στη λίστα */}
                  {removableSimpleIngredients(selectedProduct.simple_ingredients).length > 0 && (
                    <div className="mb-6">
                      <Label className="text-base font-bold mb-3 block text-t2">{t('products:ingredients')}</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {removableSimpleIngredients(selectedProduct.simple_ingredients)
                          .map((ingredient) => {
                            const ingredientId = Number(ingredient.store_ingredient_id);
                            const isRemoved = removedIngredients.includes(ingredientId);
                            return (
                              <button
                                key={`base-${ingredientId}`}
                                onClick={() => {
                                  if (isRemoved) {
                                    setRemovedIngredients(removedIngredients.filter(id => id !== ingredientId));
                                  } else {
                                    setRemovedIngredients([...removedIngredients, ingredientId]);
                                  }
                                }}
                                className={`p-3 rounded-xl border-2 transition-all active:scale-[0.97] text-left ${
                                  isRemoved 
                                    ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 line-through' 
                                    : 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/30 text-t2'
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                                    isRemoved ? 'bg-red-500' : 'bg-green-500'
                                  }`}>
                                    {isRemoved ? (
                                      <X className="h-3 w-3 text-t1" />
                                    ) : (
                                      <Check className="h-3 w-3 text-t1" />
                                    )}
                                  </div>
                                  <span className="font-medium">{ingredient.name}</span>
                                </div>
                              </button>
                            );
                          })}
                      </div>
                    </div>
                  )}

                  {/* Extra Ingredients (Add More) - Grouped by category — όχι τα προεπιλεγμένα 0 € */}
                  {extraSimpleIngredients(selectedProduct.simple_ingredients).length > 0 && (
                  <div className="mb-6">
                    <Label className="text-base font-bold mb-3 block text-t2">{t('delivery:extrasLabel')}</Label>
                    <div className="space-y-4">
                      {(() => {
                        const ingredients = extraSimpleIngredients(selectedProduct.simple_ingredients);
                        const categories = Array.from(new Set(ingredients.map(ing => ing.category || t('common:other'))));
                        return categories.map(cat => (
                          <div key={cat}>
                            <p className="text-xs font-semibold text-t3 uppercase tracking-wider mb-2">{cat}</p>
                            <div className="space-y-2">
                              {ingredients
                                .filter(ing => (ing.category || t('common:other')) === cat)
                                .map((ingredient, idx) => {
                                  const existingExtra = addedExtras.find(e => e.name === ingredient.name);
                                  const quantity = existingExtra?.quantity || 0;
                                  return (
                                    <div key={idx} className="flex items-center justify-between p-3 bg-surface-2 rounded-xl border-2 border-line-strong">
                                      <div className="flex-1">
                                        <span className="font-medium text-t2">{ingredient.name}</span>
                                        {Number(ingredient.extra_price || 0) > 0 && (
                                          <span className="text-sm text-green-600 dark:text-green-400 ml-2">+{formatMoney(Number(ingredient.extra_price))}</span>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-1">
                                        <button
                                          onClick={() => {
                                            if (quantity > 0) {
                                              if (quantity === 1) {
                                                setAddedExtras(addedExtras.filter(e => e.name !== ingredient.name));
                                              } else {
                                                setAddedExtras(addedExtras.map(e => 
                                                  e.name === ingredient.name ? { ...e, quantity: e.quantity - 1 } : e
                                                ));
                                              }
                                            }
                                          }}
                                          className={`w-8 h-8 rounded-lg flex items-center justify-center active:scale-95 ${
                                            quantity > 0 ? 'bg-surface-4 text-t1' : 'bg-surface-3 text-t4'
                                          }`}
                                          disabled={quantity === 0}
                                        >
                                          <Minus className="h-4 w-4" />
                                        </button>
                                        <span className="font-bold w-6 text-center text-t1">{quantity}</span>
                                        <button
                                          onClick={() => {
                                            if (quantity === 0) {
                                              setAddedExtras([...addedExtras, {
                                                raw_material_id: 0,
                                                name: ingredient.name,
                                                quantity: 1,
                                                price: Number(ingredient.extra_price || 0)
                                              }]);
                                            } else if (quantity < 5) {
                                              setAddedExtras(addedExtras.map(e => 
                                                e.name === ingredient.name ? { ...e, quantity: e.quantity + 1 } : e
                                              ));
                                            }
                                          }}
                                          className={`w-8 h-8 rounded-lg flex items-center justify-center active:scale-95 ${
                                            quantity < 5 ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-surface-3 text-t4'
                                          }`}
                                          disabled={quantity >= 5}
                                        >
                                          <Plus className="h-4 w-4" />
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                  )}
                </>
              )}

              {/* Option Groups — ALL groups shown in customize modal */}
              {productOptionGroups.length > 0 && (
                <div className="mb-5">
                  <Label className="text-base font-bold mb-3 block text-t2">{t('delivery:options')}</Label>
                  <div className="space-y-5">
                    {productOptionGroups.map(group => {
                      const choiceCount = group.choices.length;
                      const choiceGridCols =
                        choiceCount <= 1 ? 'grid-cols-1' :
                        choiceCount === 2 ? 'grid-cols-2' :
                        choiceCount === 3 ? 'grid-cols-3' :
                        choiceCount === 4 ? 'grid-cols-2 sm:grid-cols-4' :
                        'grid-cols-3 sm:grid-cols-4 lg:grid-cols-5';
                      return (
                      <div key={group.id}>
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-base font-bold text-t1">{group.name}</span>
                          {group.is_required && (
                            <span className="text-xs bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 px-2 py-0.5 rounded-full font-semibold">
                              {t('delivery:required')}
                            </span>
                          )}
                          {group.selection_type === 'multiple' && (
                            <span className="text-xs text-t4">
                              {t('delivery:multiple')}
                            </span>
                          )}
                        </div>
                        <div className={`grid ${choiceGridCols} gap-3`}>
                          {group.choices.map(choice => {
                            const isSelected = pendingSelectedOptions.some(o => o.group_id === group.id && o.choice_id === choice.id);
                            return (
                              <button
                                key={choice.id}
                                type="button"
                                onClick={() => {
                                  const opt: SelectedOptionEntry = { group_id: group.id, group_name: group.name, choice_id: choice.id, choice_name: choice.name, price_modifier: choice.price_modifier };
                                  if (group.selection_type === 'single') {
                                    setPendingSelectedOptions(prev => [...prev.filter(o => o.group_id !== group.id), opt]);
                                  } else {
                                    setPendingSelectedOptions(prev =>
                                      isSelected ? prev.filter(o => !(o.group_id === group.id && o.choice_id === choice.id)) : [...prev, opt]
                                    );
                                  }
                                }}
                                className={`p-4 rounded-xl border-2 transition-all active:scale-[0.97] min-h-[96px] flex flex-col items-center justify-center text-center ${
                                  isSelected ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 shadow-md' : 'border-line-strong hover:border-line-strong hover:bg-surface-2'
                                }`}
                              >
                                <span className="font-bold text-lg text-t1">{choice.name}</span>
                                {choice.price_modifier > 0 && <span className="text-sm mt-1 font-bold text-blue-600 dark:text-blue-400">+{formatMoney(choice.price_modifier)}</span>}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="mb-6">
                <Label className="text-base font-bold mb-3 block text-t2">{t('common:notes')}</Label>
                <textarea
                  value={specialInstructions}
                  onChange={(e) => setSpecialInstructions(e.target.value)}
                  placeholder={t('delivery:specialInstructionsPlaceholder')}
                  className="w-full p-3 bg-surface-2 border border-line-strong rounded-xl text-t1 placeholder-t4 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  rows={2}
                />
              </div>

              {/* Product Discount */}
              <div className="mb-6">
                <Label className="text-base font-bold mb-3 block text-t2">{t('common:discount')}</Label>
                <div className="flex items-center gap-2 flex-wrap">
                  {[0, 10, 20, 30, 50].map(percent => (
                    <button
                      key={percent}
                      onClick={() => setProductDiscount(percent)}
                      className={`h-12 px-5 rounded-xl font-bold transition-all active:scale-95 ${
                        productDiscount === percent
                          ? 'bg-blue-600 text-white'
                          : 'bg-surface-3 text-t2 hover:bg-surface-4'
                      }`}
                    >
                      {percent}%
                    </button>
                  ))}
                  <div className="flex items-center border-2 border-line-strong rounded-xl overflow-hidden bg-surface-2">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={productDiscount}
                      onChange={(e) => setProductDiscount(Math.min(100, Math.max(0, Number(e.target.value) || 0)))}
                      className="w-16 h-12 text-center font-bold text-lg border-0 focus:outline-none bg-surface-2 text-t1"
                    />
                    <span className="pr-3 font-bold text-t3">%</span>
                  </div>
                  {productDiscount > 0 && selectedProduct && (
                    <span className="text-base text-green-600 dark:text-green-400 font-bold ml-2">
                      -{formatMoney(((lineUnitPrice(selectedProduct, selectedSize, 'delivery') + addedExtras.reduce((sum, e) => sum + ((e.extra_price || e.price || 0) * (e.quantity || 1)), 0)) * productDiscount / 100))}
                    </span>
                  )}
                </div>
              </div>

              </div>{/* End scrollable content */}

              {/* Footer - Sticky */}
              <div className="p-4 border-t border-line-strong bg-surface-2 shrink-0">
                {/* Price Summary */}
                {selectedProduct && (
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-t3">
                      <span className="text-sm">{selectedSize ? `${selectedSize.name}: ` : ''}{formatMoney(lineUnitPrice(selectedProduct, selectedSize, 'delivery'))}</span>
                      {addedExtras.length > 0 && (
                        <span className="text-sm text-green-600 dark:text-green-400 ml-2">| Extras: +{formatMoney(addedExtras.reduce((sum, e) => sum + ((e.extra_price || e.price || 0) * (e.quantity || 1)), 0))}</span>
                      )}
                      {productDiscount > 0 && (
                        <span className="text-sm text-red-500 ml-2">| -{productDiscount}%</span>
                      )}
                    </div>
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {formatMoney((() => {
                        const subtotal = lineUnitPrice(selectedProduct, selectedSize, 'delivery') +
                          addedExtras.reduce((sum, extra) => sum + ((extra.extra_price || extra.price || 0) * (extra.quantity || 1)), 0);
                        return subtotal * (1 - productDiscount / 100);
                      })())}
                    </div>
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowCustomizeModal(false);
                      setSelectedProduct(null);
                      setSelectedSize(null);
                      setRemovedIngredients([]);
                      setAddedExtras([]);
                      setSpecialInstructions('');
                      setProductDiscount(0);
                      setProductOptionGroups([]);
                      setPendingSelectedOptions([]);
                    }}
                    className="flex-1 h-14 rounded-xl border-2 border-line-strong font-bold text-t3 hover:bg-surface-2 active:scale-[0.98] transition-all"
                  >
                    {t('common:cancel')}
                  </button>
                  <button
                    onClick={() => {
                      if (selectedProduct) {
                        // Block if a required option group has no selection
                        const missing = productOptionGroups.filter(g => g.is_required && !pendingSelectedOptions.some(o => o.group_id === g.id));
                        if (missing.length > 0) {
                          showToast('warning', t('delivery:pleaseSelect', { groups: missing.map(g => g.name).join(', '), defaultValue: `Λείπουν επιλογές: ${missing.map(g => g.name).join(', ')}` }));
                          return;
                        }
                        const customizations = {
                          removedIngredients,
                          // Απλά υλικά: ονόματα στη σειρά των ids — σημείωση «Without: …» και δελτίο
                          // κουζίνας με ονόματα αντί για «ID:<θέση>» (backend utils/removedIngredientLabels).
                          removedIngredientNames: store?.warehouse === false && selectedProduct.simple_ingredients
                            ? simpleIngredientNames(selectedProduct.simple_ingredients, removedIngredients)
                            : [],
                          addedExtras,
                          specialInstructions,
                          discount: productDiscount,
                          selected_options: pendingSelectedOptions.length ? pendingSelectedOptions : undefined
                        };

                        if (selectedSize) {
                          addToBasketWithSize(selectedProduct, selectedSize, customizations);
                        } else if (selectedProduct.sizes && selectedProduct.sizes.length === 1) {
                          addToBasketWithSize(selectedProduct, selectedProduct.sizes[0], customizations);
                        } else {
                          // For products without sizes, still apply customizations
                          const validation = canAddToBasket(selectedProduct);
                          if (!validation.allowed) {
                            showToast('warning', validation.message || 'Μη έγκυρη επιλογή');
                            return;
                          }
                          const extrasPrice = addedExtras.reduce((sum, extra) =>
                            sum + ((extra.extra_price || extra.price || 0) * (extra.quantity || 1)), 0);
                          const optionsPrice = pendingSelectedOptions.reduce((s, o) => s + o.price_modifier, 0);
                          const price = lineUnitPrice(selectedProduct, null, 'delivery');

                          let totalPrice = price + extrasPrice + optionsPrice;
                          if (productDiscount > 0) {
                            totalPrice = totalPrice * (1 - productDiscount / 100);
                          }

                          const basketItem: BasketItem = {
                            id: `${selectedProduct.id}-${Date.now()}`,
                            product: selectedProduct,
                            quantity: 1,
                            price: totalPrice,
                            totalPrice,
                            removedIngredients,
                            removedIngredientNames: customizations.removedIngredientNames,
                            addedExtras,
                            selected_options: pendingSelectedOptions.length ? [...pendingSelectedOptions] : undefined,
                            specialInstructions,
                            discount: productDiscount > 0 ? productDiscount : undefined
                          };

                          setBasket(prev => addOrMergeBasketLine(prev, basketItem));

                          // Trigger add to cart animation
                          setLastAddedProduct(selectedProduct.name);
                          setAddedToCartAnimation(true);
                          setTimeout(() => setAddedToCartAnimation(false), 1500);

                          setShowCustomizeModal(false);
                          setSelectedProduct(null);
                          setRemovedIngredients([]);
                          setAddedExtras([]);
                          setSpecialInstructions('');
                          setProductDiscount(0);
                          setProductOptionGroups([]);
                          setPendingSelectedOptions([]);
                        }
                      }
                    }}
                    className="cashier-primary flex-1 h-14 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl active:scale-[0.98] transition-all text-base"
                  >
                    {t('delivery:addToBasket')}
                  </button>
                </div>
              </div>
            </div>{/* End inner modal */}
          </div>
        )}

        {/* Edit Basket Item Modal — shared ProductCustomizeModal for parity with
            the long-press add flow (size + ingredients + extras + option groups
            + notes + discount). Previously this used a simplified inline modal
            that hid option groups, dropping ζάχαρη/γάλα/μέγεθος selections from
            view when editing. */}
        {showEditModal && editingBasketItem && selectedProduct && (
          <ProductCustomizeModal
            channel="delivery"
            isEditing
            open={showEditModal}
            product={selectedProduct}
            initialSize={selectedSize}
            initialRemovedIngredients={removedIngredients}
            initialAddedExtras={addedExtras as any}
            initialSelectedOptions={pendingSelectedOptions}
            initialSpecialInstructions={specialInstructions}
            initialDiscount={productDiscount}
            weight={editingBasketItem?.weight ?? null}
            lineOwn={editingBasketItem ? lineOwnOf(editingBasketItem) : null}
            productIngredients={productIngredients as any}
            productOptionGroups={productOptionGroups}
            loadingMaterials={loadingMaterials}
            warehouseMode={store?.warehouse !== false}
            discountPresets={deliverySettings?.discount_presets || [5, 10, 15, 20]}
            maxDiscountPercent={deliverySettings?.discount_max_percent || 100}
            allowCustomDiscount={deliverySettings?.discount_allow_custom_percent !== false}
            translationNamespace="delivery"
            onSizeChange={(size) => { setSelectedSize(size); fetchSizeIngredients(size.id); }}
            onClose={closeEditModal}
            onConfirm={(r: ProductCustomizeResult) => {
              updateBasketItem({
                removedIngredients: r.removedIngredients,
                removedIngredientNames: r.removedIngredientNames,
                addedExtras: r.addedExtras as any,
                selected_options: r.selectedOptions.length ? r.selectedOptions : undefined,
                specialInstructions: r.specialInstructions,
                discount: r.discount,
                newSize: r.size || undefined,
              });
            }}
          />
        )}

        {/* Weight Input Modal */}
        {showWeightModal && selectedProduct && (
          <WeightModal
            product={selectedProduct}
            // «Βάρος + τιμή κιλού»: καμία τιμή καταλόγου — η τιμή κιλού μπαίνει στο επόμενο βήμα.
            pricePerUnit={isWeightedOpenPrice(selectedProduct) ? null : productDisplayPrice(selectedProduct, 'delivery')}
            onConfirm={isWeightedOpenPrice(selectedProduct) ? takeWeightForOpenPrice : addWeightVariableToBasket}
            onClose={() => {
              setShowWeightModal(false);
              setSelectedProduct(null);
              setWeightInput('');
            }}
          />
        )}

        {/* Open Price Modal */}
        {showOpenPriceModal && selectedProduct && (
          <OpenPriceModal
            product={selectedProduct}
            weight={weightForOpenPrice}
            weightUnit={weightUnitOf(selectedProduct)}
            onConfirm={(product, price) => weightForOpenPrice !== null
              ? addWeightedOpenPriceToBasket(product, weightForOpenPrice, price)
              : addOpenPriceToBasket(product, price)}
            onClose={() => {
              setShowOpenPriceModal(false);
              setSelectedProduct(null);
              setOpenPriceInput('');
              setWeightForOpenPrice(null);
            }}
          />
        )}

        {/* Order Details Modal */}
        {showOrderModal && viewingOrder && (
          <div className="cashier-overlay fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => { setShowOrderModal(false); setViewingOrder(null); }}>
            <div 
              className="cashier-dialog w-full max-w-lg bg-surface-0 rounded-2xl border border-line/60 shadow-2xl shadow-black/15 dark:shadow-black/50 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="cashier-dialog-header relative px-5 pt-5 pb-4 border-b border-line/60">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center shadow-lg ${
                      viewingOrder.order_type === 'delivery' ? 'bg-gradient-to-br from-blue-500 to-blue-700' :
                      viewingOrder.order_type === 'takeaway' ? 'bg-gradient-to-br from-amber-500 to-amber-700' :
                      'bg-gradient-to-br from-violet-500 to-violet-700'
                    }`}>
                      {viewingOrder.order_type === 'delivery' ? <Truck className="w-5 h-5 light:text-white dark:text-t1" /> :
                       viewingOrder.order_type === 'takeaway' ? <ShoppingBag className="w-5 h-5 light:text-white dark:text-t1" /> :
                       <Receipt className="w-5 h-5 light:text-white dark:text-t1" />}
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-t1">#{viewingOrder.short_code}</h2>
                      <p className="text-xs text-t3 dark:text-t4 mt-0.5">
                        {new Date(viewingOrder.created_at).toLocaleDateString('el-GR', { day: '2-digit', month: 'short', year: 'numeric' })}
                        {' • '}
                        {new Date(viewingOrder.created_at).toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-bold uppercase px-2.5 py-1 rounded-full tracking-wide ${
                      viewingOrder.status === 'completed' || viewingOrder.status === 'dispatched' ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 ring-1 ring-emerald-500/20' :
                      viewingOrder.status === 'pending' ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400 ring-1 ring-amber-500/20' :
                      viewingOrder.status === 'accepted' ? 'bg-blue-500/15 text-blue-600 dark:text-blue-400 ring-1 ring-blue-500/20' :
                      viewingOrder.status === 'cancelled' ? 'bg-red-500/15 text-red-600 dark:text-red-400 ring-1 ring-red-500/20' :
                      'bg-surface-3 dark:bg-surface-3/30 text-t3 ring-1 ring-line-strong/20'
                    }`}>
                      {orderStatusLabel(viewingOrder.status)}
                    </span>
                    <button
                      onClick={() => { setShowOrderModal(false); setViewingOrder(null); }}
                      className="p-1.5 rounded-lg hover:bg-surface-1 text-t4 hover:text-t2 transition-colors touch-manipulation"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Order meta chips */}
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-lg ${
                    viewingOrder.order_type === 'delivery' ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400' :
                    viewingOrder.order_type === 'takeaway' ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400' :
                    'bg-violet-500/10 text-violet-600 dark:text-violet-400'
                  }`}>
                    {viewingOrder.order_type === 'delivery' ? <Truck className="w-3 h-3" /> :
                     viewingOrder.order_type === 'takeaway' ? <ShoppingBag className="w-3 h-3" /> :
                     <Receipt className="w-3 h-3" />}
                    {viewingOrder.order_type === 'delivery' ? t('common:orderType.delivery') : viewingOrder.order_type === 'takeaway' ? t('common:orderType.takeaway') : viewingOrder.order_type}
                  </span>
                  {viewingOrder.payment_type && (
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-lg bg-surface-2 dark:bg-surface-2/50 text-t3">
                      {viewingOrder.payment_type === 'cash' ? <Banknote className="w-3 h-3" /> : <CreditCard className="w-3 h-3" />}
                      {viewingOrder.payment_type === 'cash' ? t('delivery:cash') : ['card','pos'].includes(viewingOrder.payment_type) ? 'POS' : ['online','prepaid','card_online','credit_card'].includes(viewingOrder.payment_type) ? t('delivery:online') : viewingOrder.payment_type}
                    </span>
                  )}
                  {viewingOrder.items && (
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-lg bg-surface-2 dark:bg-surface-2/50 text-t3">
                      <Package className="w-3 h-3" />
                      {t('delivery:itemsCount', { count: viewingOrder.items.length })}
                    </span>
                  )}
                </div>
              </div>

              {/* Scrollable content */}
              <div className="max-h-[55vh] overflow-y-auto">
                {/* Delivery Address */}
                {viewingOrder.order_type === 'delivery' && (viewingOrder.street_address || viewingOrder.delivery_address) && (
                  <div className="px-5 py-3.5 border-b border-line/80">
                    <div className="flex items-start gap-2.5">
                      <MapPin className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm text-t2 font-medium">
                          {viewingOrder.street_address || viewingOrder.delivery_address}
                        </p>
                        {viewingOrder.area && (
                          <p className="text-xs text-t3 dark:text-t4 mt-0.5">{viewingOrder.area}</p>
                        )}
                        {(viewingOrder.floor || viewingOrder.doorbell) && (
                          <p className="text-xs text-t3 dark:text-t4 mt-0.5">
                            {viewingOrder.floor && t('delivery:floorColon', { value: viewingOrder.floor })}
                            {viewingOrder.floor && viewingOrder.doorbell && ' • '}
                            {viewingOrder.doorbell && t('delivery:doorbellColon', { value: viewingOrder.doorbell })}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* «Αλλαγή διανομέα» (πάντα διαθέσιμη, απόφαση ιδιοκτήτη 12/10/2026): σε διανομή, με δικό μας διανομέα. */}
                {viewingOrder.order_type === 'delivery' && viewingOrder.status === 'dispatched' && store?.id && (
                  <OrderDriver key={viewingOrder.id} storeId={store.id} orderId={viewingOrder.id} shortCode={viewingOrder.short_code} />
                )}

                {/* Special instructions */}
                {viewingOrder.special_instructions && (
                  <div className="px-5 py-3 border-b border-line/80">
                    <div className="flex items-start gap-2.5">
                      <FileText className="w-4 h-4 text-amber-700 dark:text-amber-400 mt-0.5 shrink-0" />
                      <p className="text-sm text-amber-800/80 dark:text-amber-300/80 italic">{viewingOrder.special_instructions}</p>
                    </div>
                  </div>
                )}

                {/* Items list */}
                <div className="px-5 py-3">
                  <div className="space-y-1">
                    {viewingOrder.items && viewingOrder.items.map((item: any, index: number) => (
                      <div key={index} className="flex items-start gap-3 py-2.5 border-b border-line/50 last:border-0">
                        <div className="w-7 h-7 rounded-lg bg-surface-1 flex items-center justify-center shrink-0 mt-0.5">
                          <span className="text-xs font-bold text-t3">{item.quantity}x</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-t1">{item.product_name}</p>
                          {item.notes && (
                            <p className="text-[11px] text-t4 mt-0.5 leading-relaxed">{item.notes}</p>
                          )}
                        </div>
                        <span className="text-sm font-semibold text-t2 shrink-0">
                          {formatCurrency(parseFloat(String(item.total_price)) || (parseFloat(String(item.unit_price)) * item.quantity))}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Footer: Total + Actions */}
              <div className="border-t border-line/60 bg-surface-0/80">
                <div className="px-5 py-3.5 flex items-center justify-between">
                  <span className="text-sm font-medium text-t3">Σύνολο</span>
                  <span className="text-xl font-bold text-emerald-600 dark:text-emerald-400">{formatCurrency(parseFloat(String(viewingOrder.total_amount)))}</span>
                </div>
                <div className="px-5 pb-5 flex gap-2.5">
                  <button
                    onClick={() => { setShowOrderModal(false); setViewingOrder(null); }}
                    className="flex-1 px-4 py-3 rounded-xl bg-surface-2 dark:bg-surface-1 hover:bg-surface-3 dark:hover:bg-surface-2 border border-line dark:border-line/60 text-sm font-semibold text-t2 hover:text-t1 transition-all touch-manipulation active:scale-[0.97]"
                  >
                    Κλείσιμο
                  </button>
                  {/* Νέα εμφάνιση: «Ξανά τα ίδια» μόνο για delivery (τιμές του ίδιου καναλιού) */}
                  {(!v2 || viewingOrder.order_type === 'delivery') && (
                  <button
                    onClick={() => {
                      const ref = customer?.recent_orders?.find(o => o.id === viewingOrder.id);
                      setShowOrderModal(false);
                      setViewingOrder(null);
                      repeatOrder({
                        id: viewingOrder.id,
                        delivery_address_id: ref?.delivery_address_id ?? null,
                        order_type: viewingOrder.order_type,
                      });
                    }}
                    className="cashier-primary flex-1 px-4 py-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-sm font-semibold text-white transition-all touch-manipulation active:scale-[0.97] flex items-center justify-center gap-2 shadow-lg shadow-blue-600/20"
                  >
                    <RefreshCw className="w-4 h-4" />
                    {v2 ? t('delivery:v2.repeat') : t('delivery:reorder')}
                  </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Company Details Modal */}
        {showCompanyModal && (
          <div className="cashier-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <Card className="cashier-dialog w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 bg-surface-2 dark:bg-surface-1 border-line">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-xl font-bold text-t1">{t('delivery:companyDetailsTitle')}</h2>
                  <p className="text-sm text-t3">{t('delivery:companyDetailsSubtitle')}</p>
                </div>
                <button
                  onClick={() => { setShowCompanyModal(false); setDeliveryKb(null); }}
                  className="text-t3 hover:text-t2"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                {/* VAT Number with Lookup */}
                <div>
                  <Label className="text-xs text-t3">{t('delivery:vatNumberRequired')}</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      value={companyFormData.company_vat}
                      onChange={(e) => setCompanyFormData({
                        ...companyFormData,
                        company_vat: e.target.value.replace(/[^0-9A-Za-z]/g, '').toUpperCase()
                      })}
                      onFocus={() => setDeliveryKb({ field: 'company_vat', mode: 'text', label: 'ΑΦΜ' })}
                      placeholder={t('takeaway:enterVat')}
                      className="flex-1 bg-surface-2 border-line-strong text-t1 placeholder:text-t4"
                    />
                    <Button
                      onClick={lookupCompanyByVat}
                      disabled={isLookingUpCompany || companyFormData.company_vat.length < 9}
                      className="cashier-primary bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      {isLookingUpCompany ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Search className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-t4 mt-1">{t('delivery:vatLookupHint')}</p>
                </div>

                {/* Company Name */}
                <div>
                  <Label className="text-xs text-t3">{t('delivery:companyNameRequired')}</Label>
                  <Input
                    value={companyFormData.company_name}
                    onChange={(e) => setCompanyFormData({
                      ...companyFormData,
                      company_name: e.target.value
                    })}
                    onFocus={() => setDeliveryKb({ field: 'company_name', mode: 'text', label: t('delivery:companyNameRequired') })}
                    className="mt-1 bg-surface-2 border-line-strong text-t1 placeholder:text-t4"
                  />
                </div>

                {/* Tax Office + Commercial Title */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-t3">{t('delivery:taxOfficeFull')}</Label>
                    <Input
                      value={companyFormData.company_tax_office}
                      onChange={(e) => setCompanyFormData({
                        ...companyFormData,
                        company_tax_office: e.target.value
                      })}
                      onFocus={() => setDeliveryKb({ field: 'company_tax_office', mode: 'text', label: t('delivery:taxOfficeFull') })}
                      className="mt-1 bg-surface-2 border-line-strong text-t1 placeholder:text-t4"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-t3">{t('delivery:commercialTitleLabel')}</Label>
                    <Input
                      value={companyFormData.company_commercial_title}
                      onChange={(e) => setCompanyFormData({
                        ...companyFormData,
                        company_commercial_title: e.target.value
                      })}
                      onFocus={() => setDeliveryKb({ field: 'company_commercial_title', mode: 'text', label: t('delivery:commercialTitleLabel') })}
                      className="mt-1 bg-surface-2 border-line-strong text-t1 placeholder:text-t4"
                    />
                  </div>
                </div>

                {/* Address */}
                <div>
                  <Label className="text-xs text-t3">{t('delivery:companyAddressLabel')}</Label>
                  <Input
                    value={companyFormData.company_address}
                    onChange={(e) => setCompanyFormData({
                      ...companyFormData,
                      company_address: e.target.value
                    })}
                    onFocus={() => setDeliveryKb({ field: 'company_address', mode: 'text', label: t('delivery:companyAddressLabel') })}
                    className="mt-1 bg-surface-2 border-line-strong text-t1 placeholder:text-t4"
                  />
                </div>

                {/* City and Τ.Κ. */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-t3">{t('delivery:cityLabel')}</Label>
                    <Input
                      value={companyFormData.company_city}
                      onChange={(e) => setCompanyFormData({
                        ...companyFormData,
                        company_city: e.target.value
                      })}
                      onFocus={() => setDeliveryKb({ field: 'company_city', mode: 'text', label: t('delivery:cityLabel') })}
                      className="mt-1 bg-surface-2 border-line-strong text-t1 placeholder:text-t4"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-t3">{t('delivery:postalCodeShort')}</Label>
                    <Input
                      value={companyFormData.company_postal_code}
                      onChange={(e) => setCompanyFormData({
                        ...companyFormData,
                        company_postal_code: e.target.value
                      })}
                      onFocus={() => setDeliveryKb({ field: 'company_postal_code', mode: 'numeric', label: t('delivery:postalCodeShort'), maxLength: 5 })}
                      className="mt-1 bg-surface-2 border-line-strong text-t1 placeholder:text-t4"
                    />
                  </div>
                </div>

                {/* Country Code and Branch Code */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-t3">{t('delivery:countryCodeLabel')}</Label>
                    <CountryCodeInput
                      value={companyFormData.company_country_code || 'GR'}
                      query={companyCountryQuery}
                      onQueryChange={setCompanyCountryQuery}
                      onSelect={(code) => {
                        setCompanyFormData({ ...companyFormData, company_country_code: code });
                        setCompanyCountryQuery(countryLabel(code));
                        setDeliveryKb(null);
                      }}
                      onFocus={() => setDeliveryKb({ field: 'company_country', mode: 'text', label: t('delivery:countryCodeLabel') })}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-t3">{t('delivery:branchLabel')}</Label>
                    <Input
                      type="number"
                      value={companyFormData.company_branch_code}
                      onChange={(e) => setCompanyFormData({
                        ...companyFormData,
                        company_branch_code: parseInt(e.target.value) || 0
                      })}
                      onFocus={() => setDeliveryKb({ field: 'company_branch_code', mode: 'numeric', label: t('delivery:branchLabel'), maxLength: 3 })}
                      className="mt-1 bg-surface-2 border-line-strong text-t1 placeholder:text-t4"
                    />
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t border-line">
                  <Button
                    variant="outline"
                    onClick={() => { setShowCompanyModal(false); setDeliveryKb(null); }}
                    className="flex-1 border-line-strong text-t2 hover:bg-surface-2"
                  >
                    {t('common:cancel')}
                  </Button>
                  <Button
                    onClick={saveCompanyDetails}
                    disabled={!companyFormData.company_vat || !companyFormData.company_name}
                    className="cashier-primary flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {t('common:save')}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Add New Address Modal */}
        {showAddressForm && customer && (
          <div className="cashier-overlay fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => { addAddressAutocompleteRef.current = null; setShowAddressForm(false); }}>
            <div 
              className="cashier-dialog w-full max-w-4xl bg-surface-0 rounded-2xl border border-line/60 shadow-2xl shadow-black/15 dark:shadow-black/50 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="cashier-dialog-header px-5 pt-5 pb-4 border-b border-line/60">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-lg">
                      <MapPin className="w-5 h-5 light:text-white dark:text-t1" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-t1">{t('delivery:newAddressTitle')}</h2>
                      <p className="text-xs text-t3 dark:text-t4 mt-0.5">{t('delivery:forCustomer', { name: customer.name })}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => { addAddressAutocompleteRef.current = null; setShowAddressForm(false); }}
                    className="p-1.5 rounded-lg hover:bg-surface-1 text-t4 hover:text-t2 transition-colors touch-manipulation"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="max-h-[calc(90vh-140px)] overflow-y-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 lg:gap-0">
                  {/* Form Section */}
                  <div className="p-5 space-y-4 lg:border-r border-line/60">
                    {/* Address type + Default toggle row */}
                    <div className="flex items-end gap-3">
                      <div className="flex-1">
                        <label className="block text-[11px] uppercase tracking-wider text-t3 dark:text-t4 font-semibold mb-1.5">{t('delivery:addressType')}</label>
                        <div className="flex gap-1.5 flex-wrap">
                          {['Home', 'Work', 'Office', 'Parents'].map((type) => (
                            <button
                              key={type}
                              onClick={() => setNewAddressData({ ...newAddressData, address_type: type.toLowerCase() })}
                              className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all touch-manipulation active:scale-95 ${
                                newAddressData.address_type === type.toLowerCase()
                                  ? 'bg-blue-600 text-white shadow-md shadow-blue-600/20'
                                  : 'bg-surface-1 text-t3 hover:bg-surface-2 hover:text-t2 border border-line/60'
                              }`}
                            >
                              {type === 'Home' ? '🏠' : type === 'Work' ? '💼' : type === 'Office' ? '🏢' : '👨‍👩‍👧'} {addressTypeLabel(type)}
                            </button>
                          ))}
                        </div>
                      </div>
                      <button
                        onClick={() => setNewAddressData({ ...newAddressData, is_default: !newAddressData.is_default })}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-all touch-manipulation whitespace-nowrap ${
                          newAddressData.is_default
                            ? 'bg-emerald-600/15 text-emerald-600 dark:text-emerald-400 ring-1 ring-emerald-500/30'
                            : 'bg-surface-1 text-t4 hover:text-t3 border border-line/60'
                        }`}
                      >
                        <Star className={`w-3.5 h-3.5 ${newAddressData.is_default ? 'fill-emerald-400' : ''}`} />
                        {t('delivery:defaultAddress')}
                      </button>
                    </div>

                    {/* Street Address with search */}
                    <div>
                      <label className="block text-[11px] uppercase tracking-wider text-t3 dark:text-t4 font-semibold mb-1.5">
                        Διεύθυνση <span className="text-red-600 dark:text-red-400">*</span>
                      </label>
                      <div className="flex gap-2">
                        <input
                          ref={addAddressStreetRef}
                          value={newAddressData.street_address}
                          onChange={(e) => setNewAddressData({ ...newAddressData, street_address: e.target.value })}
                          placeholder="Πληκτρολογήστε διεύθυνση..."
                          autoFocus
                          autoComplete="one-time-code"
                          className="flex-1 px-3.5 py-2.5 bg-surface-2 dark:bg-surface-1 border border-line dark:border-line/60 rounded-xl text-sm text-t1 placeholder-t4 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/40 transition-all"
                        />
                        <button
                          onClick={geocodeAddAddress}
                          title="Αναζήτηση στον χάρτη"
                          className="px-3 py-2.5 rounded-xl bg-blue-600/15 hover:bg-blue-600/25 text-blue-600 dark:text-blue-400 border border-blue-500/20 transition-all touch-manipulation active:scale-95"
                        >
                          <Search className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Area + City */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[11px] uppercase tracking-wider text-t3 dark:text-t4 font-semibold mb-1.5">Περιοχή</label>
                        <input
                          value={newAddressData.area}
                          onChange={(e) => setNewAddressData({ ...newAddressData, area: e.target.value })}
                          placeholder="Γειτονιά"
                          autoComplete="one-time-code"
                          className="w-full px-3.5 py-2.5 bg-surface-2 dark:bg-surface-1 border border-line dark:border-line/60 rounded-xl text-sm text-t1 placeholder-t4 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/40 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] uppercase tracking-wider text-t3 dark:text-t4 font-semibold mb-1.5">Πόλη</label>
                        <input
                          value={newAddressData.city}
                          onChange={(e) => setNewAddressData({ ...newAddressData, city: e.target.value })}
                          placeholder="Πόλη"
                          autoComplete="one-time-code"
                          className="w-full px-3.5 py-2.5 bg-surface-2 dark:bg-surface-1 border border-line dark:border-line/60 rounded-xl text-sm text-t1 placeholder-t4 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/40 transition-all"
                        />
                      </div>
                    </div>

                    {/* Floor + Doorbell + Τ.Κ. */}
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-[11px] uppercase tracking-wider text-t3 dark:text-t4 font-semibold mb-1.5">Όροφος</label>
                        <input
                          value={newAddressData.floor}
                          onChange={(e) => setNewAddressData({ ...newAddressData, floor: e.target.value })}
                          placeholder="π.χ. 3ος"
                          autoComplete="one-time-code"
                          className="w-full px-3.5 py-2.5 bg-surface-2 dark:bg-surface-1 border border-line dark:border-line/60 rounded-xl text-sm text-t1 placeholder-t4 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/40 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] uppercase tracking-wider text-t3 dark:text-t4 font-semibold mb-1.5">
                          Κουδούνι <span className="text-red-600 dark:text-red-400">*</span>
                        </label>
                        <input
                          value={newAddressData.doorbell}
                          onChange={(e) => setNewAddressData({ ...newAddressData, doorbell: e.target.value })}
                          placeholder="Όνομα στο κουδούνι"
                          autoComplete="one-time-code"
                          className="w-full px-3.5 py-2.5 bg-surface-2 dark:bg-surface-1 border border-line dark:border-line/60 rounded-xl text-sm text-t1 placeholder-t4 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/40 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] uppercase tracking-wider text-t3 dark:text-t4 font-semibold mb-1.5">{t('delivery:postalCodeShort')}</label>
                        <input
                          value={newAddressData.postal_code}
                          onChange={(e) => setNewAddressData({ ...newAddressData, postal_code: e.target.value })}
                          placeholder="12345"
                          autoComplete="one-time-code"
                          className="w-full px-3.5 py-2.5 bg-surface-2 dark:bg-surface-1 border border-line dark:border-line/60 rounded-xl text-sm text-t1 placeholder-t4 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/40 transition-all"
                        />
                      </div>
                    </div>

                    {/* Delivery Notes */}
                    <div>
                      <label className="block text-[11px] uppercase tracking-wider text-t3 dark:text-t4 font-semibold mb-1.5">{t('delivery:deliveryNotes')}</label>
                      <textarea
                        value={newAddressData.delivery_notes}
                        onChange={(e) => setNewAddressData({ ...newAddressData, delivery_notes: e.target.value })}
                        placeholder={t('delivery:deliveryInstructionsPlaceholder')}
                        className="w-full px-3.5 py-2.5 bg-surface-2 dark:bg-surface-1 border border-line dark:border-line/60 rounded-xl text-sm text-t1 placeholder-t4 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/40 transition-all resize-none"
                        rows={2}
                      />
                    </div>

                    {/* Coordinates badge */}
                    {newAddressData.latitude && newAddressData.longitude && (
                      <div className="flex items-center gap-2 px-3 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                        <div className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center">
                          <Check className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                        </div>
                        <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">
                          {t('delivery:locationPinned', { lat: newAddressData.latitude.toFixed(4), lng: newAddressData.longitude.toFixed(4) })}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Map Section */}
                  <div className="p-5 space-y-3 bg-surface-0/50">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] uppercase tracking-wider text-t3 dark:text-t4 font-semibold">{t('delivery:pinLocation')}</span>
                      <span className="text-[10px] text-t3 dark:text-t4 font-medium">Πατήστε στον χάρτη ή σύρετε τον δείκτη</span>
                    </div>
                    <div 
                      ref={addAddressMapContainerRef}
                      className="w-full h-[320px] lg:h-[360px] rounded-xl border border-line/60 overflow-hidden shadow-inner"
                    />
                    {newAddressData.formatted_address && (
                      <div className="flex items-start gap-2 px-3 py-2 bg-surface-1/80 rounded-xl border border-line/40">
                        <Navigation2 className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
                        <p className="text-xs text-t3 leading-relaxed">{newAddressData.formatted_address}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer Actions */}
              <div className="px-5 py-4 border-t border-line/60 bg-surface-0/80 flex gap-2.5">
                <button
                  onClick={() => { addAddressAutocompleteRef.current = null; setShowAddressForm(false); }}
                  className="flex-1 px-4 py-3 rounded-xl bg-surface-2 dark:bg-surface-1 hover:bg-surface-3 dark:hover:bg-surface-2 border border-line dark:border-line/60 text-sm font-semibold text-t2 hover:text-t1 transition-all touch-manipulation active:scale-[0.97]"
                >
                  {t('common:cancel')}
                </button>
                <button
                  onClick={addNewAddress}
                  disabled={!newAddressData.street_address || !newAddressData.doorbell}
                  className="cashier-primary flex-1 px-4 py-3 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:bg-surface-2 disabled:text-t4 text-sm font-semibold text-white transition-all touch-manipulation active:scale-[0.97] flex items-center justify-center gap-2 shadow-lg shadow-blue-600/20 disabled:shadow-none"
                >
                  <Plus className="w-4 h-4" />
                  {t('delivery:addNewAddress')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Address Modal */}
        {showEditAddressForm && editingAddress && customer && (
          <div className="cashier-overlay fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => { setShowEditAddressForm(false); setEditingAddress(null); }}>
            <div 
              className="cashier-dialog w-full max-w-4xl bg-surface-0 rounded-2xl border border-line/60 shadow-2xl shadow-black/15 dark:shadow-black/50 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="cashier-dialog-header px-5 pt-5 pb-4 border-b border-line/60">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg">
                      <Edit className="w-5 h-5 light:text-white dark:text-t1" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-t1">{t('delivery:editAddressTitle')}</h2>
                      <p className="text-xs text-t3 dark:text-t4 mt-0.5">{t('delivery:forCustomer', { name: customer.name })}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => { setShowEditAddressForm(false); setEditingAddress(null); }}
                    className="p-1.5 rounded-lg hover:bg-surface-1 text-t4 hover:text-t2 transition-colors touch-manipulation"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="max-h-[calc(90vh-140px)] overflow-y-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 lg:gap-0">
                  {/* Form Section */}
                  <div className="p-5 space-y-4 lg:border-r border-line/60">
                    {/* Address type + Default toggle row */}
                    <div className="flex items-end gap-3">
                      <div className="flex-1">
                        <label className="block text-[11px] uppercase tracking-wider text-t3 dark:text-t4 font-semibold mb-1.5">{t('delivery:addressType')}</label>
                        <div className="flex gap-1.5 flex-wrap">
                          {['Home', 'Work', 'Office', 'Parents'].map((type) => (
                            <button
                              key={type}
                              onClick={() => setEditAddressData({ ...editAddressData, address_type: type.toLowerCase() })}
                              className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all touch-manipulation active:scale-95 ${
                                editAddressData.address_type === type.toLowerCase()
                                  ? 'bg-amber-600 text-white shadow-md shadow-amber-600/20'
                                  : 'bg-surface-1 text-t3 hover:bg-surface-2 hover:text-t2 border border-line/60'
                              }`}
                            >
                              {type === 'Home' ? '🏠' : type === 'Work' ? '💼' : type === 'Office' ? '🏢' : '👨‍👩‍👧'} {addressTypeLabel(type)}
                            </button>
                          ))}
                        </div>
                      </div>
                      <button
                        onClick={() => setEditAddressData({ ...editAddressData, is_default: !editAddressData.is_default })}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-all touch-manipulation whitespace-nowrap ${
                          editAddressData.is_default
                            ? 'bg-emerald-600/15 text-emerald-600 dark:text-emerald-400 ring-1 ring-emerald-500/30'
                            : 'bg-surface-1 text-t4 hover:text-t3 border border-line/60'
                        }`}
                      >
                        <Star className={`w-3.5 h-3.5 ${editAddressData.is_default ? 'fill-emerald-400' : ''}`} />
                        {t('delivery:defaultAddress')}
                      </button>
                    </div>

                    {/* Street Address with search */}
                    <div>
                      <label className="block text-[11px] uppercase tracking-wider text-t3 dark:text-t4 font-semibold mb-1.5">
                        Διεύθυνση <span className="text-red-600 dark:text-red-400">*</span>
                      </label>
                      <div className="flex gap-2">
                        <input
                          value={editAddressData.street_address}
                          onChange={(e) => setEditAddressData({ ...editAddressData, street_address: e.target.value })}
                          placeholder={t('delivery:streetAndNumber')}
                          autoComplete="one-time-code"
                          className="flex-1 px-3.5 py-2.5 bg-surface-2 dark:bg-surface-1 border border-line dark:border-line/60 rounded-xl text-sm text-t1 placeholder-t4 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500/40 transition-all"
                        />
                        <button
                          onClick={geocodeEditAddress}
                          title="Αναζήτηση στον χάρτη"
                          className="px-3 py-2.5 rounded-xl bg-amber-600/15 hover:bg-amber-600/25 text-amber-700 dark:text-amber-400 border border-amber-500/20 transition-all touch-manipulation active:scale-95"
                        >
                          <Search className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Area + City */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[11px] uppercase tracking-wider text-t3 dark:text-t4 font-semibold mb-1.5">Περιοχή</label>
                        <input
                          value={editAddressData.area}
                          onChange={(e) => setEditAddressData({ ...editAddressData, area: e.target.value })}
                          placeholder="Γειτονιά"
                          autoComplete="one-time-code"
                          className="w-full px-3.5 py-2.5 bg-surface-2 dark:bg-surface-1 border border-line dark:border-line/60 rounded-xl text-sm text-t1 placeholder-t4 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500/40 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] uppercase tracking-wider text-t3 dark:text-t4 font-semibold mb-1.5">Πόλη</label>
                        <input
                          value={editAddressData.city}
                          onChange={(e) => setEditAddressData({ ...editAddressData, city: e.target.value })}
                          placeholder="Πόλη"
                          autoComplete="one-time-code"
                          className="w-full px-3.5 py-2.5 bg-surface-2 dark:bg-surface-1 border border-line dark:border-line/60 rounded-xl text-sm text-t1 placeholder-t4 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500/40 transition-all"
                        />
                      </div>
                    </div>

                    {/* Floor + Doorbell + Τ.Κ. */}
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-[11px] uppercase tracking-wider text-t3 dark:text-t4 font-semibold mb-1.5">Όροφος</label>
                        <input
                          value={editAddressData.floor}
                          onChange={(e) => setEditAddressData({ ...editAddressData, floor: e.target.value })}
                          placeholder="π.χ. 3ος"
                          autoComplete="one-time-code"
                          className="w-full px-3.5 py-2.5 bg-surface-2 dark:bg-surface-1 border border-line dark:border-line/60 rounded-xl text-sm text-t1 placeholder-t4 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500/40 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] uppercase tracking-wider text-t3 dark:text-t4 font-semibold mb-1.5">
                          Κουδούνι <span className="text-red-600 dark:text-red-400">*</span>
                        </label>
                        <input
                          value={editAddressData.doorbell}
                          onChange={(e) => setEditAddressData({ ...editAddressData, doorbell: e.target.value })}
                          placeholder="Όνομα στο κουδούνι"
                          autoComplete="one-time-code"
                          className="w-full px-3.5 py-2.5 bg-surface-2 dark:bg-surface-1 border border-line dark:border-line/60 rounded-xl text-sm text-t1 placeholder-t4 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500/40 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] uppercase tracking-wider text-t3 dark:text-t4 font-semibold mb-1.5">{t('delivery:postalCodeShort')}</label>
                        <input
                          value={editAddressData.postal_code}
                          onChange={(e) => setEditAddressData({ ...editAddressData, postal_code: e.target.value })}
                          placeholder="12345"
                          autoComplete="one-time-code"
                          className="w-full px-3.5 py-2.5 bg-surface-2 dark:bg-surface-1 border border-line dark:border-line/60 rounded-xl text-sm text-t1 placeholder-t4 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500/40 transition-all"
                        />
                      </div>
                    </div>

                    {/* Delivery Notes */}
                    <div>
                      <label className="block text-[11px] uppercase tracking-wider text-t3 dark:text-t4 font-semibold mb-1.5">{t('delivery:deliveryNotes')}</label>
                      <textarea
                        value={editAddressData.delivery_notes}
                        onChange={(e) => setEditAddressData({ ...editAddressData, delivery_notes: e.target.value })}
                        placeholder={t('delivery:deliveryInstructionsPlaceholder')}
                        className="w-full px-3.5 py-2.5 bg-surface-2 dark:bg-surface-1 border border-line dark:border-line/60 rounded-xl text-sm text-t1 placeholder-t4 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500/40 transition-all resize-none"
                        rows={2}
                      />
                    </div>

                    {/* Coordinates badge */}
                    {editAddressData.latitude && editAddressData.longitude && (
                      <div className="flex items-center gap-2 px-3 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                        <div className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center">
                          <Check className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                        </div>
                        <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">
                          {t('delivery:locationPinned', { lat: editAddressData.latitude.toFixed(4), lng: editAddressData.longitude.toFixed(4) })}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Map Section */}
                  <div className="p-5 space-y-3 bg-surface-0/50">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] uppercase tracking-wider text-t3 dark:text-t4 font-semibold">{t('delivery:pinLocation')}</span>
                      <span className="text-[10px] text-t3 dark:text-t4 font-medium">Πατήστε στον χάρτη ή σύρετε τον δείκτη</span>
                    </div>
                    <div 
                      ref={editAddressMapContainerRef}
                      className="w-full h-[320px] lg:h-[360px] rounded-xl border border-line/60 overflow-hidden shadow-inner"
                    />
                    {editAddressData.formatted_address && (
                      <div className="flex items-start gap-2 px-3 py-2 bg-surface-1/80 rounded-xl border border-line/40">
                        <Navigation2 className="w-3.5 h-3.5 text-amber-700 dark:text-amber-400 mt-0.5 shrink-0" />
                        <p className="text-xs text-t3 leading-relaxed">{editAddressData.formatted_address}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer Actions */}
              <div className="px-5 py-4 border-t border-line/60 bg-surface-0/80 flex gap-2.5">
                <button
                  onClick={() => { setShowEditAddressForm(false); setEditingAddress(null); }}
                  className="flex-1 px-4 py-3 rounded-xl bg-surface-2 dark:bg-surface-1 hover:bg-surface-3 dark:hover:bg-surface-2 border border-line dark:border-line/60 text-sm font-semibold text-t2 hover:text-t1 transition-all touch-manipulation active:scale-[0.97]"
                >
                  {t('common:cancel')}
                </button>
                <button
                  onClick={updateAddress}
                  disabled={!editAddressData.street_address || !editAddressData.doorbell}
                  className="flex-1 px-4 py-3 rounded-xl bg-amber-600 hover:bg-amber-500 disabled:bg-surface-2 disabled:text-t4 text-sm font-semibold text-white transition-all touch-manipulation active:scale-[0.97] flex items-center justify-center gap-2 shadow-lg shadow-amber-600/20 disabled:shadow-none"
                >
                  <Check className="w-4 h-4" />
                  {t('delivery:saveChanges')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* «Αναίρεση» 4″ — στο πάνελ της νέας εμφάνισης όσο είναι ανοιχτή η παραγγελία, αλλιώς εδώ */}
      {!(v2 && orderMode && customer) && renderUndoBar('floating')}

      {/* Add to Cart Toast Notification */}
      {addedToCartAnimation && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 animate-in slide-in-from-bottom-4 fade-in duration-300">
          <div className="bg-green-600 text-white px-6 py-3 rounded-full shadow-lg flex items-center gap-3">
            <div className="bg-white/20 rounded-full p-1">
              <Check className="h-5 w-5" />
            </div>
            <span className="font-medium">{t('delivery:productAdded', { name: lastAddedProduct })}</span>
          </div>
        </div>
      )}

      {/* Numpad for order-level discount entry (percent or fixed €). */}
      <NumpadModal
        open={showNumpad}
        initialValue={numpadTarget === 'orderFixed' ? fixedDiscountAmount : orderDiscount}
        suffix={numpadTarget === 'orderFixed' ? '€' : '%'}
        allowDecimal={numpadTarget === 'orderFixed'}
        min={0}
        max={
          numpadTarget === 'orderFixed'
            ? (deliverySettings?.discount_max_amount || 9999)
            : (deliverySettings?.discount_max_percent || 100)
        }
        onClose={() => setShowNumpad(false)}
        onConfirm={(v) => {
          if (numpadTarget === 'orderFixed') setFixedDiscountAmount(v);
          else setOrderDiscount(v);
          setShowNumpad(false);
        }}
      />

      {/* On-screen keyboard for touch-only POS screens (customer search + new-customer form) */}
      {deliveryKb && (
        <TouchKeyboard
          mode={deliveryKb.mode}
          label={deliveryKb.label}
          value={deliveryKbValue()}
          onChange={deliveryKbChange}
          onDone={deliveryKbDone}
          maxLength={deliveryKb.maxLength}
        />
      )}
    </div>
  );
};

// Add Google Maps types
declare global {
  interface Window {
    google: any;
  }
}

export default DeliveryWithMap;
